import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { TextField } from "@/components/TextField";
import { SelectField } from "@/components/SelectField";
import { US_STATE_OPTIONS } from "@/lib/usStates";
import { messageFor } from "@/lib/errors";
import { anchorServerError } from "@/lib/serverFieldErrors";
import {
  rosterCardErrors,
  rosterFindings,
  rosterFindingsFromError,
  type RosterFinding,
} from "@/lib/bondRosterChecks";
import {
  clearedByBondDependants,
  clearedByBondSection,
  dependantRuleFor,
  resolveBondFieldSections,
  sectionRuleFor,
  visibleBondFields,
} from "@/lib/bondFieldSections";
import {
  allEligibilityConfirmed,
  bondEligibilityBlock,
  eligibilityRefusalIndex,
} from "@/lib/bondEligibility";
import {
  getAccount,
  listAccounts,
  type AccountSummary,
  type PersonnelPii,
} from "@/lib/brokerAccounts";
import {
  getBondCatalog,
  getResolvedBondConfig,
  BOND_CREATE_SENDS_ELIGIBILITY,
  cancelBondSubmission,
  createBondSubmission,
  findPreapprovalsInProgress,
  getBondSubmission,
  isOrderDecided,
  type BondSubmissionListItem,
  saveBondStep,
  submitBond,
  isSettled,
  isReferral,
  canonicalAccountingLinkState,
  isAccountingLinkConnected,
  checkState,
  waitingOnCheck,
  type BondRunCheckState,
  primaryBondOwner,
  ownersAnsweringStep,
  BOND_FINAL_STEP_KEY,
  type BondOwner,
  isAlreadySubmitted,
  type BondConfig,
  type BondConfigField,
  type BondFinalField,
  type BondProduct,
  type BondStep,
  type BondStepRun,
  type BondRunCheck,
  type BondSubmission,
  type BondAccountingLink,
  type BondAdvanceOptIn,
  type BondAccountingLinkState,
  type BondBankLink,
  type BondBankLinkState,
  type BondDuplicateCandidate,
  isBankLinkConnected,
  isPreapprovalSettled,
  hasStandingPreapproval,
  bondFlowOf,
  bondRatePercent,
  pollBondPreapproval,
  stepKind,
  rosterKind,
  isIndemnityComplete,
  isIndemnitySupported,
  isFieldsStep,
  isDataPullStep,
  type BondIndemnity,
  uploadBondDocument,
  deleteBondDocument,
  listBondDocuments,
  type BondDocument,
  type BondDocumentRef,
  type BondPreapproval,
} from "@/lib/brokerBonds";
import {
  backChipIndex,
  bondChips,
  chipTitles,
  preapprovalProgress,
  type BondChip,
} from "@/lib/bondWizard";
import {
  useAccountingLinkState,
  useBankLinkState,
  type BondLinkPollOptions,
} from "@/lib/useBondLink";
import { BondFieldRenderer } from "@/components/BondFieldRenderer";
import { BondPairedField } from "@/components/BondPairedField";
import { bondPairSatisfied, resolveBondPairs } from "@/lib/bondFieldPairs";
import type { BondFilesPort } from "@/components/BondFilesField";
import { CapturedValues, RecordField } from "@/components/BondRecord";
import { DocumentDropzone } from "@/components/DocumentDropzone";
import {
  ExtractedMarker,
  ExtractedReviewBanner,
} from "@/components/ExtractedReview";
import { useExtractedReview } from "@/lib/useExtractedReview";
import { useIndicationPdf } from "@/lib/useIndicationPdf";
import { useBondStepRun } from "@/lib/useBondStepRun";
import { selectUploader } from "@/lib/uploadSelection";
import { useDocumentTypes } from "@/lib/useDocumentTypes";
import { fileKindLabel } from "@/lib/brokerDocumentTypes";
import type { AuthedRequester } from "@/lib/brokerAuth";
import type { IntakeProposal } from "@/lib/brokerDocuments";
import {
  applyBondPrefill,
  bondProposalValues,
  proposalToBondPrefill,
  reviewKey,
  withoutBondPrefill,
  type BondPrefill,
  type BondPrefillDrop,
  type BondPrefillTarget,
} from "@/lib/bondDocumentPrefill";
import {
  capturesOwnerValues,
  isRepeatedPath,
  unansweredFields,
  bondWireValues,
  invalidFields,
  type BondFieldValue,
  fieldKind,
  unsupportedFields,
  type BondRenderField,
  type BondValueBag,
} from "@/lib/bondFields";
import {
  omitBlankPii,
  primaryOwner,
  rosterPiiByOwnerId,
  withPiiOnFile,
  withRosterPiiOnFile,
} from "@/lib/personnelPii";
import { BondIndemnityRoster } from "@/components/BondIndemnityRoster";
import { BondMissingChecklist } from "@/components/BondMissingChecklist";
import {
  AdvanceDeclinedNotice,
  SignalAdvanceOffer,
} from "@/components/SignalAdvanceOffer";
import { GiaSection } from "@/components/GiaSection";
import { PushToAccountToggle } from "@/components/PushToAccountToggle";
import { showsGiaSection } from "@/lib/brokerGia";
import {
  BondRepeatSummary,
  type RepeatSummarySection,
} from "@/components/BondRepeatSummary";
import {
  choiceFor,
  declarationFor,
  immutableFields,
  summarisedSteps,
  withoutImmutableFields,
  type ChangeChoice,
} from "@/lib/bondRepeatRequest";
import { BondOwnershipProgress } from "@/components/BondOwnershipProgress";
import {
  MARITAL_STATUS_PATH,
  addOwner,
  ROSTER_CONTINUE_LABEL,
  ROSTER_INCOMPLETE_NOTICE,
  cardOwnerIdOf,
  missingByCard,
  fieldsForCards,
  unplacedSignerNotes,
  hasRosterAnswer,
  isRosterComplete,
  rosterExplanation,
  drawnRoster,
  effectiveIndemnityRule,
  effectivePrimaryOwnerId,
  orderedRoster,
  namedRoster,
  ownerDeclarations,
  invalidWireKeyMessage,
  invalidWireKeys,
  ownerPeople,
  removeOwner,
  rosterBlocks,
  rosterFields,
  rosterFormValues,
  rosterHasRoom,
  rosterOrderFields,
  readFor,
  rosterRowsFromValues,
  rosterStepValues,
  rosterWireValues,
  undrawnOwners,
  spouseAddressDiffers,
  scopedRosterName,
  spouseOf,
  spouseSigns,
  spousesRequired,
  unscopeRosterName,
  withMaritalStatusOptions,
  withSpouse,
  withoutSpouse,
  type RosterPerson,
} from "@/lib/bondRoster";
import { formatBondDate, formatPreapprovalMoney } from "@/lib/bondValueDisplay";

// The surety bond intake flow (BRK-E005). Step 1 and the final step are
// CODE-OWNED; everything between them comes from the subproduct's RESOLVED
// product configuration, so onboarding a bond product is a config change rather
// than a release. Nothing here branches on a product or a subproduct.
//
// Flow: pick a subproduct → new-or-existing insured → attest to the config's
// eligibility items → the order is started (POST, which mints the SB number) →
// each config step is filled or run → the project & bond terms → submit.
//
// A data-pull step runs a Lendflow orchestration and polls it. Its outcome NEVER
// blocks the broker: a settled non-pass flags the order `referred`, which shows as
// a persistent notice through every later step and on the confirmation screen, and
// the broker completes the submission either way (locked decision #4).
//
// The page serves TWO routes (BRK-S088): `/surety/submissions/new` starts an
// order, and `/surety/submissions/:id` RESUMES one the server is already holding.
// There is no client-side persistence behind that — the order, its step values,
// its settled runs and its referral flag all come back from
// `GET /bond/submissions/{id}` (NN #9). A create moves the URL onto the `:id`
// form, so from the first step onwards a reload costs nothing.
// See docs/bonds.md.

// ─── Field adapters (config shapes → the renderer's shape) ────────────────────
// A fields step addresses values by `path`; the final step by `key`. The renderer
// takes one `name`, so the two are adapted here rather than teaching it both.

// FALLBACK ONLY (BRK-S091). A config now carries an explicit control `type`
// (SUR-S041), so this derivation exists for two cases and no others: a
// configuration SNAPSHOT taken before that shipped (in-flight drafts keep their
// old shape), and a field whose type the config genuinely omits. Unknown leaves
// stay plain text (degrade, never drop — NN #7).
function typeFromPath(path: string): BondRenderField["type"] {
  const leaf = (path.split(".").pop() ?? path).replace(/\[\]/g, "");
  if (leaf === "addresses" || leaf === "address") return "address";
  if (leaf === "phoneNumbers" || leaf === "telephone" || leaf === "phone") {
    return "phone";
  }
  if (/date|dob$/i.test(leaf)) return "date";
  return "text";
}

// DO NOT add a state rule here, and do not infer `format: "state"` from a field
// name. `personal[].driversLicenseState` is NOT a state code — it is an opaque
// Lendflow option id (1–78, swept live in SUR-S034), and the service 422s "TX"
// with "must be a valid number". It arrives as `type: "number"` with NO format,
// so the shared state picker correctly never fires on it.
//
// That only holds because discovery lists state fields EXPLICITLY (`state`,
// `stateOfIncorporation`) instead of matching /state$/i. A name-based rule here
// would render a 50-state picker on a licence-state field, and the broker's
// "Texas" would be rejected several layers away by a message about numbers.
// The `format` the server sends is the only signal for this.

// Also fallback only: the suggestion now emits human labels ("First name", not
// "firstName"), so this matters for pre-SUR-S041 snapshots. Only a camelCase
// IDENTIFIER (starts lowercase, no spaces) is rewritten — an authored label like
// "SSN" or "Date of birth" passes through untouched either way.
function humanizeLabel(label: string): string {
  if (/^[a-z]/.test(label) && /[A-Z]/.test(label) && !/\s/.test(label)) {
    const words = label.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();
    return words.charAt(0).toUpperCase() + words.slice(1);
  }
  return label;
}

function toRenderField(f: BondConfigField): BondRenderField {
  return {
    name: f.path,
    label: humanizeLabel(f.label),
    type: f.type ?? typeFromPath(f.path),
    required: f.required,
    options: f.options,
    // No helper text on a FIELDS step: the contract publishes none there (it
    // publishes `helpText` on final-step fields only), so reading one would be
    // rendering from a field PMS cannot send — BRK-S097.
    // Validation metadata, straight through — the rules are named on the wire
    // and enforced server-side; the renderer and `fieldError` just honour them.
    format: f.format,
    min: f.min,
    max: f.max,
    minLength: f.minLength,
    maxLength: f.maxLength,
    // `files` only, straight through (BRK-S145).
    maxFiles: f.maxFiles,
    accept: f.accept,
  };
}

function toRenderFinalField(f: BondFinalField): BondRenderField {
  return {
    name: f.key,
    label: f.label,
    type: f.type,
    required: f.required,
    options: f.options,
    help: f.helpText,
    maxFiles: f.maxFiles,
    accept: f.accept,
  };
}

// ─── The page ────────────────────────────────────────────────────────────────

type Mode = "new" | "existing";

/** The step key the code-owned final step saves under — the seam owns the
 *  literal (`BOND_FINAL_STEP_KEY`), because the server keys on it too and an
 *  order read relays the SHARED value bag under it (BRK-S107). */
const FINAL_STEP_KEY = BOND_FINAL_STEP_KEY;

/**
 * What the broker reads when a field on the step in front of them has no
 * control (BRK-S142).
 *
 * Names the ACTION rather than the cause: "reload" is the whole of what they can
 * do about a portal a version behind, and the `UnsupportedField` card beside the
 * gap says which field it is. Ends with a way through that is not the flow,
 * because a broker stuck on a bond request needs one.
 */
const BLOCKED_BY_UNSUPPORTED =
  "This step asks for something this version of the portal can’t show. Reload the page to pick up the latest version — if it still doesn’t appear, contact us and we’ll finish this request with you.";

/**
 * BRK-S120 — the two code-owned pieces of the document prefill.
 *
 * `business.legalName` is the one key in the pinned proposal (PMS-S269) that is
 * NOT a config field key on this page's own terms: it is the insured's legal
 * name, which step 1 asks for in a CODE-OWNED box (the config-driven steps ask
 * for it again, and get it from the same read). Naming it here is not the page
 * branching on a field — it is the code-owned step's own field, named once.
 */
const PROPOSAL_LEGAL_NAME_KEY = "business.legalName";

/** The review key for that box. Its own step namespace, because eligibility is
 *  not one of the config's steps. */
const ELIGIBILITY_NAME_REVIEW_KEY = reviewKey("eligibility", "legalName");

/** What the flow says when a document produced nothing it could use — a failed
 *  read, a timed-out read, and an empty read all land here. One sentence, one
 *  place, because all three mean the same thing to the broker: type it in. */
const UNREADABLE_COPY =
  "We couldn’t read the document — enter the details manually.";

/**
 * Where a resumed order picks up: the FIRST chip that isn't finished, or the
 * terms chip when they all are. Never chip 0 — the order exists, so eligibility
 * is behind the broker by definition.
 *
 * "Finished" is deliberately conservative, because landing too far forward hides
 * work the broker still owes:
 *   - a `fields` step counts only when the server has a value bag for it AND
 *     every required field in it is answered (a step with no required fields
 *     still needs its save, so an empty bag is not "done");
 *   - a `data_pull` step counts when its run came back SETTLED;
 *   - the preapproval CHECKS chip counts on the same rule — a settled run is a
 *     bureau call that already happened, and replaying it is not work owed;
 *   - the preapproval INDICATION chip counts once its run has settled AND the
 *     decision has been mirrored onto the order. It owes the broker nothing, so
 *     a resumed order lands past it (their letter is one Back away, and a
 *     not-granted order's notice renders on bond details anyway). While the
 *     decision is still `pending` it is NOT done, so a resume lands there and
 *     the chip waits for the mirror rather than showing bond details as if the
 *     review had never happened.
 *
 * Returned as a CHIP index — never a config-step index (BRK-S132). The two
 * differ the moment a configuration carries a `preapproval` step, and the whole
 * reason `bondChips` carries each step is that nothing has to convert between
 * them.
 */
function resumeChipIndex(
  chips: BondChip[],
  values: Record<string, BondValueBag>,
  runs: Record<string, BondStepRun>,
  /** The step's render fields, WITH the on-file waiver already applied — an
   *  SSN PMS holds is not work the broker still owes, so a resumed order must
   *  not be sent back to the indemnitor step to retype one (BRK-S105). */
  fieldsFor: (step: Extract<BondStep, { type: "fields" }>) => BondRenderField[],
  /** That step's bag as the FORM holds it — see the `isFieldsStep` branch. */
  bagFor: (step: Extract<BondStep, { type: "fields" }>) => BondValueBag,
  /**
   * The order's signer list (BRK-S152). A ROSTER step is finished only when the
   * SERVER says the list is complete — never when its required fields happen to
   * be answered, which is the optimistic success BRK-S122 forbids: the rule is
   * evaluated from the cap table upstream, so "every box has something in it"
   * and "everyone who must sign is listed" are different questions.
   *
   * It does not BLOCK; it only decides where a resume lands.
   */
  indemnity?: BondIndemnity | null,
): number {
  for (let i = 0; i < chips.length; i += 1) {
    const chip = chips[i];
    if (chip.kind === "eligibility") continue;
    // The terms chip is the landing when everything before it is done, so it is
    // never itself "unfinished" — reaching it ends the walk.
    if (chip.kind === "final") return i;
    if (chip.kind === "preapproval_checks") {
      if (!runs[chip.step.key]) return i;
      continue;
    }
    // The INDICATION chip is never "done", and the rule is deliberately the
    // simplest one available: a resumed order lands on it, and the chip itself
    // decides what that means.
    //
    //   • approved    — the broker sees their limits and their letter again,
    //     one Continue from where they left off. Their terms are restored
    //     behind it, so the click costs nothing and the alternative — a reload
    //     dumping them PAST their indication with no way back but the Back
    //     button — is worse for the one screen in this flow that gives them
    //     something;
    //   • not_granted — the chip advances on its own, so the broker lands on
    //     bond details with the neutral notice, which is where they were going;
    //   • pending     — it waits for the mirror, which is the whole reason the
    //     chip polls.
    //
    // BRK-S134's standing preapproval is unaffected: those chips are omitted
    // from the spine entirely, so there is nothing here to land on.
    if (chip.kind === "preapproval_indication") return i;
    // The SUMMARY is where a repeat request resumes to when nothing behind it
    // is unfinished (BRK-S162) — never a chip to skip past, because the
    // declaration on it is the thing the rest of the spine is built from and a
    // resumed order has not asked the question yet.
    if (chip.kind === "summary") return i;
    const step = chip.step;
    if (isFieldsStep(step)) {
      const bag = values[step.key];
      // A ROSTER step's fields are SCOPED per person, so the gate has to read
      // the FORM's bag rather than the order's — the two are keyed
      // differently, and reading the order's here made every required field
      // look unanswered on a roster the broker had completed.
      if (!bag || unansweredFields(fieldsFor(step), bagFor(step)).length > 0) {
        return i;
      }
      if (rosterKind(step) === "indemnity" && !isIndemnityComplete(indemnity)) {
        return i;
      }
      continue;
    }
    // A data pull, or a step type this build cannot render. Both count as done
    // once there is a settled run — and an unsupported step has none, so a
    // resume lands on its guarded card rather than skipping past it.
    if (!runs[step.key]) return i;
  }
  return chips.length - 1;
}

/**
 * Does this chip's own card own the forward move?
 *
 * The footer's Continue must be ABSENT for these, not merely inert: a data pull
 * and the preapproval Checks bar advance themselves, the Indication view has its
 * own Continue inside the card, and the unsupported-step card offers its own way
 * out. A live footer button in front of a card that has not finished — or in
 * front of nothing at all — is the exact shape of the blank-step bug
 * (BRK-S132).
 */
function chipDrivesItself(chip: BondChip | undefined): boolean {
  if (chip === undefined) return false;
  if (chip.kind === "preapproval_checks") return true;
  if (chip.kind === "preapproval_indication") return true;
  if (chip.kind !== "step") return false;
  const kind = stepKind(chip.step);
  return kind === "data_pull" || kind === "unsupported";
}

export function NewBondSubmissionPage() {
  const navigate = useNavigate();
  const { authedRequest, authedUploadWithProgress } = useAuth();
  // Present on `/surety/submissions/:id` — the order to resume.
  const { id: routeId } = useParams();
  /**
   * `?subproduct=…&account=…` — step 1, preselected (BRK-S160).
   *
   * The account's **Get pre-approved** button hands the wizard both halves of
   * its first screen. In the URL rather than in `location.state` on purpose:
   * the whole flow is reload-safe, a broker who refreshes (or bookmarks, or
   * reopens from history) lands back where they were, and `location.state` is
   * gone the moment they do any of those.
   *
   * It is not a pre-approval feature. Any subproduct + any of the brokerage's
   * own accounts works, and nothing here reads a product key — the wizard still
   * branches only on the resolved configuration.
   */
  const [searchParams] = useSearchParams();
  const wantedSubproductId = searchParams.get("subproduct") ?? "";
  const wantedAccountId = searchParams.get("account") ?? "";

  // Step 1 state.
  const [products, setProducts] = useState<BondProduct[] | null>(null);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [subproductId, setSubproductId] = useState("");
  const [config, setConfig] = useState<BondConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [mode, setMode] = useState<Mode>("new");
  const [newLegalName, setNewLegalName] = useState("");
  const [newState, setNewState] = useState("");
  const [accountId, setAccountId] = useState("");
  const [accountName, setAccountName] = useState("");
  /**
   * Whether a value the URL SUPPLIED is shown read-only (BRK-S181).
   *
   * Arriving from the account page's "Get pre-approved" decides both halves of
   * step 1, so re-offering the select, the New/Existing cards and the search
   * asked the broker a question already answered. A supplied value renders as
   * a string with a "Change" that reveals the real control; the two are
   * independent, so arriving with only a subproduct (the landing tile) leaves
   * the account picker live. Never on a resume, where both come off the order.
   */
  const [subproductLocked, setSubproductLocked] = useState(
    () => !routeId && wantedSubproductId !== "",
  );
  const [accountLocked, setAccountLocked] = useState(
    () => !routeId && wantedAccountId !== "",
  );
  const [accountNumber, setAccountNumber] = useState<string | null>(null);
  // BRK-S170 — WHICH statements the broker has confirmed, by their index in the
  // configuration's list. A set of indices rather than one boolean, because the
  // step now asks item by item and the create carries what was confirmed.
  const [eligibilityConfirmed, setEligibilityConfirmed] = useState<Set<number>>(
    () => new Set(),
  );
  /** The statement the server refused, and why — anchored to its own checkbox
   *  rather than reported at step level (BRK-S170). */
  const [eligibilityRefusal, setEligibilityRefusal] = useState<{
    index: number;
    reason: string;
  } | null>(null);

  // The started order + flow state.
  const [order, setOrder] = useState<BondSubmission | null>(null);
  const [duplicates, setDuplicates] = useState<BondDuplicateCandidate[] | null>(
    null,
  );
  /** The insured's pre-approval orders still in flight, when starting another
   *  would make a second one (BRK-S181). Non-null is the Continue-or-Discard
   *  prompt. */
  const [inFlight, setInFlight] = useState<BondSubmissionListItem[] | null>(
    null,
  );
  const [step, setStep] = useState(0);
  // Captured values + push-to-account choice, per step key.
  const [values, setValues] = useState<Record<string, BondValueBag>>({});
  const [pushToAccount, setPushToAccount] = useState<Record<string, boolean>>(
    {},
  );
  // ── The indemnitor roster's LAYOUT (BRK-S152) ───────────────────────────────
  // Which `personal[]` rows a roster step draws, per step key, and which spouse
  // rows the broker says have their own address.
  //
  // UI state, and only UI state (NN #9): both are DERIVED from the order's own
  // bag whenever the broker has not said otherwise — the layout from the
  // indexed paths it carries (`rosterRowsFromBag`, which reads `spouseOf`), and
  // the address question from whether the two addresses already differ. So a
  // reload restores the roster from the server rather than from anything this
  // page remembered, and an entry here only ever means "the broker changed the
  // shape of the roster since that read".
  const [rosterRows, setRosterRows] = useState<Record<string, RosterPerson[]>>(
    {},
  );
  const [spouseDiffers, setSpouseDiffers] = useState<Record<string, string[]>>(
    {},
  );
  // ── WHO the credit check is run against (BRK-S156) ────────────────────────
  // The broker's own pick SINCE THE LAST READ, per step key — UI state on the
  // same footing as the layout above it, and dropped by a save for the same
  // reason: the response's `owners[]` is the declaration that took, and holding
  // a pick over the top of it would show a primary the service has not
  // accepted. Absent here means "nobody has moved it", which
  // `effectivePrimaryOwnerId` answers from the order, then from the cap table.
  const [primaryPick, setPrimaryPick] = useState<Record<string, string>>({});
  // ── Which owner CARDS are open (BRK-S164) ─────────────────────────────────
  // Per step key, per owner — and only where the broker has SAID. The default
  // is read from the ORDER's own bag (`openOwnersFor`): a person the order
  // holds answers for came from a save and folds, a person it does not is
  // being typed in and stays open. Deriving it from the FORM bag instead would
  // fold a card shut on its first keystroke, which is why the two bags are
  // kept apart here.
  const [openCards, setOpenCards] = useState<
    Record<string, Record<string, boolean>>
  >({});
  // Why one card's own Save was refused, keyed by owner id (globally unique).
  // Separate from the step-level `error` so a refusal shows where the fields
  // that caused it are — and so a second card's save does not clear it.
  const [cardErrors, setCardErrors] = useState<Record<string, string>>({});
  /** Whose cards the LAST roster-wide finding was written onto (BRK-S171), so
   *  the next placement can clear exactly those and nothing else. */
  const rosterFindingOwners = useRef<Set<string>>(new Set());
  const [savingOwnerId, setSavingOwnerId] = useState<string | null>(null);
  // What a completed Save & Continue that did NOT settle the list says. Set
  // only by a save that landed, so it is never on screen as a complaint about
  // something the broker has not done yet.
  const [rosterNotice, setRosterNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState<BondRenderField[]>([]);
  // Metadata-driven validation errors, keyed by field name (BRK-S091). Separate
  // from `missing`: a blank required field and a filled-but-invalid one are
  // different complaints and must not overwrite each other.
  const [invalid, setInvalid] = useState<Record<string, string>>({});
  const [submittedNumber, setSubmittedNumber] = useState<string | null>(null);
  // The Signal Advance answer as FILED (BRK-S161) — beside the two links, and
  // driven by the submit's own response rather than by a re-read.
  const [submittedAdvanceOptIn, setSubmittedAdvanceOptIn] =
    useState<BondAdvanceOptIn | null>(null);
  // The order the submit answered with (BRK-S182) — the GIA section reads its
  // `gia` summary and indemnity block. Its own state rather than `order`, so
  // nothing keyed on the draft reacts to the filing.
  const [submittedOrder, setSubmittedOrder] = useState<BondSubmission | null>(
    null,
  );
  // The accounting link the just-filed order came back with (BRK-S115). Held
  // beside `submittedNumber` because the confirmation screen is driven by a
  // NUMBER rather than by the order doc, and the link has to reach it too. It
  // stays `undefined` when the order's config declared no accounting step —
  // absent is a fact here, so the panel renders nothing at all.
  const [submittedLink, setSubmittedLink] = useState<
    BondAccountingLink | undefined
  >(undefined);
  // The BANK link off the same response (BRK-S118). Its own piece of state, not
  // a second field on the one above, because the two links are independent
  // upstream: no configuration gates this one, so an order can come back with
  // either, both or neither. `undefined` is its only absence and renders as
  // nothing at all.
  const [submittedBankLink, setSubmittedBankLink] = useState<
    BondBankLink | undefined
  >(undefined);
  // The preapproval decision as the Indication chip's poll last saw it
  // (BRK-S132). Its own state rather than a patch onto `order`, because the
  // order doc is the server's snapshot of everything else and the decision
  // arrives on its own schedule (PMS mirrors it AFTER the run settles). Kept
  // here, not in the chip, because the TERMS step renders from it too — the
  // not-granted notice and BRK-S134's standing banner both live there.
  const [freshPreapproval, setFreshPreapproval] = useState<
    BondPreapproval | null | undefined
  >(undefined);
  /**
   * The repeat request's answer (BRK-S162). `null` until the broker picks one,
   * which is what keeps the spine behind the Summary chip at its shortest until
   * they do.
   *
   * Its own state rather than a read off the order, for the window PMS-S462 is
   * still in: the declaration is stored upstream and echoed on the order view,
   * and until that lands this is the only place it exists. `repeatChoice`
   * SEEDS from the order when the echo arrives, so a resumed order shows the
   * answer it was given rather than an empty radio group.
   */
  const [repeatChoice, setRepeatChoice] = useState<ChangeChoice | null>(null);
  // Sticky once any data pull settles non-pass. Held here (not per step) because
  // the notice follows the broker through the rest of the flow; `order.referred`
  // is the server's own copy, so a reload restores it without this state.
  const [referred, setReferred] = useState(false);
  const [referralReasons, setReferralReasons] = useState<string[]>([]);
  // Settled runs restored from the order, keyed by step key: a pull that already
  // happened is REPLAYED, never repeated (it is a real bureau call).
  const [runs, setRuns] = useState<Record<string, BondStepRun>>({});
  // The write-only-PII indicator for the insured's PRIMARY OWNER — the person
  // PMS injects stored SSN/licence for (BRK-S105). Server state, read off the
  // account, so it survives a reload without this page remembering anything.
  //
  // It answers for a step drawn as ONE block. A ROSTER step asks the question
  // once per person, so it reads `rosterPii` below instead.
  const [ownerPii, setOwnerPii] = useState<PersonnelPii | null>(null);
  // The same indicator for EVERY person on the insured's account, keyed by the
  // personnel id a roster addresses them with (BRK-S165). A `brk-…` identity
  // this portal minted is nobody PMS holds, so it simply misses the map.
  const [rosterPii, setRosterPii] = useState<Record<string, PersonnelPii>>({});
  // Bumped after a roster save, to re-read the account (BRK-S165). The read is
  // keyed on the ACCOUNT, which a save never changes — so without this an SSN
  // the broker has just filed would still read as not on file when they
  // re-opened the card, and the field would ask for it again.
  const [piiEpoch, setPiiEpoch] = useState(0);
  // Whether that read has SETTLED — success or failure. The resume landing
  // waits on it, because a step whose SSN is on file is finished, and landing
  // before the answer arrives would drop the broker back onto it.
  const [piiSettled, setPiiSettled] = useState(false);
  // Has the STANDALONE request been filed from the terminal Indication card
  // (BRK-S160)? Held here because the FOOTER reads it: once the order is
  // submitted there is nothing behind the broker to go back to, and a Back that
  // lands on a step PMS will refuse to save is a trap.
  const [standaloneFiled, setStandaloneFiled] = useState(false);

  // ── Document prefill (BRK-S120) ─────────────────────────────────────────────
  // The proposal a dropped bond request produced, and what this configuration
  // could make of it. Both are UI state: the prefill is a set of proposed values
  // the broker confirms step by step, and nothing is written until they do
  // (NN #9 — a reload loses an unconfirmed prefill, which is correct).
  const [proposal, setProposal] = useState<IntakeProposal | null>(null);
  const [prefill, setPrefill] = useState<BondPrefill | null>(null);
  const review = useExtractedReview(undefined);
  const { seed: seedReview, touch: touchReview } = review;
  // Held for `seedFromOrder`: an order read arrives LATER and replaces the whole
  // value map, so the prefill has to be re-overlaid rather than lost. A ref
  // because that callback must stay stable (it is an effect dependency).
  const prefillRef = useRef<BondPrefill | null>(null);
  // Which read has been applied, and against which FIELD SET — so the effect runs
  // once per read, and re-runs when the broker changes the bond type (the field
  // set changed, so the same values map differently).
  //
  // Keyed on the derived targets, NOT on `subproductId`: the id changes the
  // instant the broker picks a type, while the resolved config arrives an HTTP
  // round trip later. An id-keyed guard therefore fired against the OUTGOING
  // configuration and then skipped the incoming one — the read stayed mapped
  // onto the bond type the broker had just left.
  const appliedRef = useRef<{
    batchId: string;
    targets: BondPrefillTarget[];
  } | null>(null);

  // Whether a step to land on has already been chosen. A ref, not state:
  // re-rendering on the change would re-run the very effect it guards. The create
  // path sets it, so the new id landing in the URL doesn't re-position the flow.
  const landedRef = useRef(false);

  /** Prefill from an order the server hands back — the create's account-derived
   *  values, or everything a resumed order has captured so far. The final step's
   *  bag comes back under the same `final` key it was filed under. */
  const seedFromOrder = useCallback(
    (from: BondSubmission) => {
      if (!from.values) return;
      const fromOrder = from.values as Record<string, BondValueBag>;
      const held = prefillRef.current;
      if (!held) {
        setValues(fromOrder);
        return;
      }
      // A document read BEFORE the order existed must survive the order's own
      // values landing — but never outrank them: the order's bag is the server's
      // account-derived truth, and a marker over a value the document did not
      // propose would be a lie (BRK-S120).
      const applied = applyBondPrefill(fromOrder, held);
      setValues(applied.values);
      // The eligibility box's own mark is deliberately not carried: this runs on
      // create and on resume, and step 1 is behind the broker for good by then
      // (stepping back onto it would offer to start a SECOND order).
      seedReview(applied.reviewKeys);
    },
    [seedReview],
  );

  // Resume: the whole flow's state comes back off the order (BRK-S088).
  //
  // The guard is "do we already HAVE this order?", not "have we asked for it?" —
  // an attempt-flag would be set by a first run that a StrictMode cleanup then
  // cancels, and the retry would skip itself and leave the page loading forever.
  // It also covers the create path, where the order is in state before the id
  // reaches the URL, and keeps the read to ONCE per order: a resumed order is a
  // snapshot to continue from, and re-reading it would clobber unsaved answers.
  useEffect(() => {
    if (!routeId || order?.id === routeId) return;
    let cancelled = false;
    setError(null);
    (async () => {
      try {
        const resumed = await getBondSubmission(authedRequest, routeId);
        if (cancelled) return;
        setOrder(resumed);
        // Setting the subproduct is what re-resolves the config, so the step
        // spine comes back the same way it does on the create path.
        setSubproductId(resumed.subproductId);
        setAccountId(resumed.accountId);
        setAccountName(resumed.accountLegalName ?? "");
        // A resumed order always HAS an account — whether it was created for
        // this order or picked from the book — so push-to-account is offered.
        // (The order view carries no `mode`; see docs/bonds.md, Known gaps.)
        setMode("existing");
        seedFromOrder(resumed);

        const settled = Object.fromEntries(
          Object.entries(resumed.stepRuns ?? {}).filter(([, run]) =>
            isSettled(run),
          ),
        );
        setRuns(settled);
        // The referral flag rides on the order itself, so it survives a reload
        // even when the run that caused it isn't in this response.
        if (resumed.referred) setReferred(true);
        const reasons: string[] = [];
        for (const run of Object.values(settled)) {
          if (!isReferral(run)) continue;
          setReferred(true);
          for (const reason of run.reasons ?? []) {
            if (!reasons.includes(reason)) reasons.push(reason);
          }
        }
        if (reasons.length) setReferralReasons(reasons);
      } catch (err) {
        if (!cancelled) setError(messageFor(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest, routeId, order?.id, seedFromOrder]);

  // The on-file indicator comes from the insured's ACCOUNT (PMS-S237 puts it on
  // every personnel entry there, and deliberately NOT on the bond order view —
  // the order's bags have the sensitive paths redacted out of them entirely).
  //
  // A failure here is never a reason to stop: the worst case is that a field
  // PMS could have filled asks the broker for it, which is exactly the flow
  // that shipped before this story (NN #7).
  useEffect(() => {
    const accountId = order?.accountId;
    if (!accountId) return;
    let cancelled = false;
    setPiiSettled(false);
    (async () => {
      try {
        const detail = await getAccount(authedRequest, accountId);
        if (cancelled) return;
        setOwnerPii(primaryOwner(detail.personnel)?.pii ?? null);
        setRosterPii(rosterPiiByOwnerId(detail.personnel));
      } catch {
        if (!cancelled) {
          setOwnerPii(null);
          setRosterPii({});
        }
      } finally {
        if (!cancelled) setPiiSettled(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest, order?.accountId, piiEpoch]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const page = await getBondCatalog(authedRequest);
        if (!cancelled) setProducts(page.items);
      } catch (err) {
        if (!cancelled) setCatalogError(messageFor(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest]);

  // Step 1, preselected from the URL (BRK-S160) — ONCE, and never on a resume,
  // where the subproduct and the account come off the order itself.
  //
  // The insured's NAME is read separately because the link carries only the id:
  // the same own-scoped `GET /api/broker/accounts/{id}` the flow already makes,
  // so a broker who was handed somebody else's id gets a 404 rather than a
  // label. A failed read costs a label and nothing else — the id is what the
  // create posts, and the account search is still there.
  const presetRef = useRef(false);
  useEffect(() => {
    if (routeId || presetRef.current) return;
    if (!wantedSubproductId && !wantedAccountId) return;
    presetRef.current = true;
    if (wantedSubproductId) setSubproductId(wantedSubproductId);
    if (!wantedAccountId) return;
    setMode("existing");
    setAccountId(wantedAccountId);
  }, [routeId, wantedSubproductId, wantedAccountId]);

  // The supplied insured's NAME and number, read in an effect of its OWN
  // (BRK-S181). It used to ride the one-shot preset above, whose ref guard
  // meant a cancelled first run (React StrictMode's mount → cleanup → mount)
  // never read the name at all — invisible while the picker rendered the row,
  // but a read-only insured with no name is a blank the broker cannot check.
  useEffect(() => {
    if (routeId || !wantedAccountId) return;
    let cancelled = false;
    (async () => {
      try {
        const detail = await getAccount(authedRequest, wantedAccountId);
        if (!cancelled) {
          setAccountName(detail.legalName);
          setAccountNumber(detail.accountNumber ?? null);
        }
      } catch {
        // Silent: an error banner here would be about a label rather than
        // about the flow. But a read-only insured with no name is a blank the
        // broker cannot check, so the picker comes back (BRK-S181) — with the
        // supplied id still chosen, as before.
        if (!cancelled) setAccountLocked(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest, routeId, wantedAccountId]);

  // The resolved config arrives when a subproduct is chosen — it drives the step
  // spine, so until it lands there is nothing to walk.
  useEffect(() => {
    if (!subproductId) {
      setConfig(null);
      return;
    }
    let cancelled = false;
    setConfigLoading(true);
    setConfigError(null);
    (async () => {
      try {
        const resolved = await getResolvedBondConfig(
          authedRequest,
          subproductId,
        );
        if (cancelled) return;
        setConfig(resolved);
        // A fresh subproduct means fresh attestations: the items themselves come
        // from this config, so a tick carried over would attest to another
        // product's statements — and an INDEX carried over would attest to a
        // different sentence at the same position, which is worse (BRK-S170).
        setEligibilityConfirmed(new Set());
        setEligibilityRefusal(null);
      } catch (err) {
        if (!cancelled) setConfigError(messageFor(err));
      } finally {
        if (!cancelled) setConfigLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest, subproductId]);

  /**
   * One fields step's render fields, with the BRK-S105 on-file waiver applied.
   *
   * Derived in ONE place and read by the control, the required checklist, and
   * the resume-position rule alike — three copies of "is this field still
   * owed?" is exactly how a control renders optional while the gate behind it
   * still blocks.
   */
  /** Everybody the ORDER describes — the declared roster plus anybody its
   *  entries name. What the form DRAWS is a subset of this (below). */
  const declaredRowsForStep = useCallback(
    (stepKey: string): RosterPerson[] =>
      rosterRowsFromValues(
        values[stepKey] ?? {},
        order?.owners,
        // STABLE across renders, derived from the order — see the parameter's
        // own note for the defect a freshly minted one caused.
        order ? `brk-${order.id}-${stepKey}-1` : undefined,
      ),
    [values, order],
  );

  /**
   * One roster step's rows: the broker's own layout when they have changed it,
   * otherwise the people the order's bag describes — MINUS anybody it declares
   * who has answered nothing (BRK-S156, `drawnRoster`).
   *
   * The filter is on the DERIVED branch only. An entry in `rosterRows` means
   * the broker has said what this roster's shape is — including adopting
   * somebody back through "+ Add owner" — and re-filtering their own layout
   * would close the block they had just asked for.
   */
  const rowsForStep = useCallback(
    (stepKey: string): RosterPerson[] =>
      rosterRows[stepKey] ??
      drawnRoster(
        declaredRowsForStep(stepKey),
        values[stepKey] ?? {},
        order?.owners,
      ),
    [rosterRows, declaredRowsForStep, values, order],
  );

  /**
   * Which owner cards this step draws OPEN (BRK-S164).
   *
   * The broker's own word where they have given one (Edit, or a card's Save),
   * and otherwise the ORDER's own bag: a person it holds answers for came back
   * from a save and folds, a person it does not is being typed in. Read off
   * `order` rather than off `values`, because `values` gains a scoped key on
   * every keystroke — deriving from it would fold a card shut under the cursor
   * the moment the first character landed.
   */
  const openOwnersFor = useCallback(
    (stepKey: string, roster: readonly RosterPerson[]): Set<string> => {
      const said = openCards[stepKey] ?? {};
      const saved = rosterStepValues(order ?? {}, stepKey) ?? {};
      const open = new Set<string>();
      for (const person of ownerPeople(roster)) {
        const byDefault = !hasRosterAnswer(
          saved,
          person.ownerId,
          order?.owners,
        );
        if (said[person.ownerId] ?? byDefault) open.add(person.ownerId);
      }
      return open;
    },
    [openCards, order],
  );

  /** Take the broker to the card a blocked save is about — and FOCUS it, so a
   *  keyboard user is moved rather than merely scrolled past. */
  /**
   * Put roster-wide findings on the cards they name, and OPEN those cards
   * (BRK-S171).
   *
   * Opening is not a nicety: a card is collapsed once it has been saved, and a
   * refusal written onto a collapsed card is a refusal the broker never sees —
   * the save would simply appear to do nothing. Returns whether anything was
   * placed, so the caller can stop.
   */
  function placeRosterFindings(
    stepKey: string,
    findings: readonly RosterFinding[],
  ): boolean {
    const errors = rosterCardErrors(findings);
    // Clear what the LAST placement wrote before writing this one, so a
    // corrected cap table stops saying it is wrong. Only those cards: a
    // per-card save's own refusal belongs to that card and is not this
    // function's to erase.
    const stale = rosterFindingOwners.current;
    rosterFindingOwners.current = new Set(Object.keys(errors));
    if (findings.length === 0 && stale.size === 0) return false;
    setCardErrors((all) => {
      const next = { ...all };
      for (const ownerId of stale) delete next[ownerId];
      return { ...next, ...errors };
    });
    if (findings.length === 0) return false;
    setOpenCards((all) => {
      const step = { ...(all[stepKey] ?? {}) };
      for (const ownerId of Object.keys(errors)) step[ownerId] = true;
      return { ...all, [stepKey]: step };
    });
    revealOwnerCard(Object.keys(errors)[0]);
    return true;
  }

  function revealOwnerCard(ownerId: string) {
    const block = document.querySelector<HTMLElement>(
      `[data-owner-id="${CSS.escape(ownerId)}"]`,
    );
    block?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    block?.focus?.({ preventScroll: true });
  }

  /** Which spouse rows have an address of their own — the broker's answer when
   *  they have given one, otherwise what the two stored addresses already say. */
  const differsForStep = useCallback(
    (
      stepKey: string,
      roster: readonly RosterPerson[],
      fields: BondRenderField[],
    ) => {
      const said = spouseDiffers[stepKey];
      if (said) return new Set(said);
      const form = rosterFormValues(
        values[stepKey] ?? {},
        roster,
        fields,
        order?.owners,
      );
      return new Set(
        roster
          .filter((person) => spouseAddressDiffers(form, fields, person))
          .map((person) => person.ownerId),
      );
    },
    [spouseDiffers, values, order],
  );

  /**
   * One step's bag as the FORM holds it.
   *
   * A roster step's is SCOPED per person: the order's own values (entry lists,
   * or a collapsed scalar for the primary owner) are distributed by identity
   * first, and the broker's own scoped edits are laid over the top so a
   * keystroke always wins over what was read. Idempotent, so it can be derived
   * at every render rather than stored — which is what keeps the layout and the
   * values from disagreeing after a reload (NN #9).
   */
  const rosterBagFor = useCallback(
    (
      stepDef: BondStep,
      roster: readonly RosterPerson[],
      fields: readonly BondRenderField[],
    ): BondValueBag => {
      const stored = values[stepDef.key] ?? {};
      if (rosterKind(stepDef) !== "indemnity") return stored;
      const seeded = rosterFormValues(stored, roster, fields, order?.owners);
      const edits: BondValueBag = {};
      for (const [key, value] of Object.entries(stored)) {
        if (unscopeRosterName(key) !== null) edits[key] = value;
      }
      return { ...seeded, ...edits };
    },
    [values, order],
  );

  /**
   * Which owner this step marks PRIMARY — the broker's pick, then the order's
   * own declaration, then the largest share on the cap table (BRK-S156).
   *
   * Derived at every render rather than stored, so a save's answer takes effect
   * by being read: the page drops the pick and the declaration wins.
   */
  const primaryForStep = useCallback(
    (stepKey: string, roster: readonly RosterPerson[], form: BondValueBag) =>
      effectivePrimaryOwnerId(
        roster,
        form,
        order?.owners,
        primaryPick[stepKey],
      ),
    [order, primaryPick],
  );

  /**
   * The order's preapproval block — the freshest thing anyone has said about it.
   *
   * Held beside the order rather than read off it, because the Indication chip
   * polls for the mirror and its result must reach the terms step (which is
   * where a not-granted notice and the standing banner render). `undefined`
   * keeps the order's own answer, which distinguishes "nothing said" from PMS's
   * explicit `null`.
   */
  const preapproval = freshPreapproval ?? order?.preapproval ?? null;

  /**
   * Is this a REPEAT request (BRK-S162) — an order for an insured who already
   * holds a standing program?
   *
   * The SAME condition `skipPreapproval` reads, deliberately: the two travel
   * together and do different things. That one drops the two preapproval chips
   * because nothing ran; this one inserts the Summary and closes the fields
   * steps the broker has not said changed, because they have answered all of it
   * before.
   *
   * The standalone pre-approval flow is excluded: its whole purpose is to GET a
   * program, so summarising what a broker already holds and offering to skip
   * the steps would be the wizard talking itself out of its own job.
   */
  const repeatRequest =
    hasStandingPreapproval(preapproval) && bondFlowOf(config) !== "preapproval";

  /** What the server says is on file for ONE person on the roster (BRK-S165).
   *  A `brk-…` identity this portal minted is nobody PMS holds, so it misses
   *  the map and the field keeps its gate. */
  const piiForOwner = useCallback(
    (ownerId: string) => rosterPii[ownerId],
    [rosterPii],
  );

  const fieldsForStep = useCallback(
    (stepDef: Extract<BondStep, { type: "fields" }>): BondRenderField[] => {
      // The EIN is not a control on a REPEAT request (BRK-S162): PMS makes a
      // set `account.ein` immutable on every write path, so an input for it
      // could only ever produce a rejected save. It is SHOWN on the Summary
      // card with the reason instead, and the stored value still rides out on
      // the save untouched — which is what keeps that save from looking like a
      // change. On a FIRST request the EIN is being set, which is the one write
      // PMS still accepts, so nothing here fires.
      const declared = withMaritalStatusOptions(
        stepDef.fields.map(toRenderField),
      );
      const base = repeatRequest ? withoutImmutableFields(declared) : declared;
      if (rosterKind(stepDef) !== "indemnity") {
        return withPiiOnFile(base, ownerPii);
      }
      // A ROSTER step's fields are its repeated block drawn once per row, in
      // the same INDEXED spelling the value bag uses — so the required
      // checklist, the format rules, the resume-position rule and the save all
      // read exactly what the controls wrote (BRK-S152). Nothing here is a
      // second copy of the expansion.
      const roster = rowsForStep(stepDef.key);
      const blocks = rosterBlocks(base, roster, {
        spouseAddressDiffers: differsForStep(stepDef.key, roster, base),
      });
      return [
        ...rosterOrderFields(base),
        ...withRosterPiiOnFile(rosterFields(blocks), piiForOwner),
      ];
    },
    [ownerPii, piiForOwner, rowsForStep, differsForStep, repeatRequest],
  );

  /**
   * Where a document's values are allowed to land: every `fields` step plus the
   * terms step, in the flow's OWN addressing (a fields step by `path`, the final
   * step by `key`).
   *
   * Derived from the configuration for the same reason the controls are: the
   * prefill must not know which fields a bond type asks for. A value with no
   * field here is reported, never forced somewhere it might fit.
   */
  const prefillTargets = useMemo<BondPrefillTarget[]>(() => {
    if (!config) return [];
    return [
      ...config.steps.flatMap((s) =>
        s.type === "fields"
          ? [{ stepKey: s.key, fields: s.fields.map(toRenderField) }]
          : [],
      ),
      {
        stepKey: FINAL_STEP_KEY,
        fields: config.finalStep.fields.map(toRenderFinalField),
      },
    ];
  }, [config]);

  // Apply a read once the configuration that gives it meaning is in hand. The
  // dropzone sits on step 1, so a broker CAN drop a document before choosing a
  // bond type — the read is then held until the config resolves rather than
  // thrown away, and changing the bond type re-maps it onto the new field set.
  useEffect(() => {
    if (!proposal || !config) return;
    const last = appliedRef.current;
    if (last?.batchId === proposal.batchId && last.targets === prefillTargets) {
      return;
    }
    appliedRef.current = { batchId: proposal.batchId, targets: prefillTargets };

    const mapped = proposalToBondPrefill(proposal, prefillTargets);
    // A re-map (the broker changed the bond type after dropping the document)
    // starts from the form WITHOUT the last read's still-unreviewed values: they
    // answered a field set this configuration may not even have, and left behind
    // they would ride along on a step save nothing rendered them on.
    const previous = prefillRef.current;
    const base =
      previous === null
        ? values
        : withoutBondPrefill(values, previous, review.isPending);
    prefillRef.current = mapped;
    setPrefill(mapped);

    const applied = applyBondPrefill(base, mapped);
    setValues(applied.values);

    // Step 1's own legal-name box, for a NEW insured: the same fact the config
    // steps get, on the code-owned control that asks for it first. Only when the
    // broker has typed nothing — their own words always win.
    const marks = [...applied.reviewKeys];
    const proposedName = bondProposalValues(proposal)[PROPOSAL_LEGAL_NAME_KEY];
    if (
      mode === "new" &&
      !newLegalName.trim() &&
      typeof proposedName === "string" &&
      proposedName.trim() !== ""
    ) {
      setNewLegalName(proposedName.trim());
      marks.push(ELIGIBILITY_NAME_REVIEW_KEY);
    }
    seedReview(marks);
    // `values`, `mode` and `newLegalName` are read at the moment a read lands;
    // they are not triggers, or every keystroke would re-apply the prefill. The
    // token guard above is what makes that safe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal, config, prefillTargets, seedReview]);

  /**
   * The wizard's chips (BRK-S132). ONE configuration step can be TWO chips, so
   * the page indexes chips and never config steps — `bondChips` carries each
   * step, which is what retires the old `steps[step - 1]` arithmetic.
   */
  /**
   * What the Summary card reads back (BRK-S162) — every fields step the
   * question covers, with the fields and the values the form itself uses.
   *
   * The ROSTER's per-owner expansion included, via the same `fieldsForStep` /
   * `rosterBagFor` pair the controls are drawn from: a summary built off the
   * raw configuration would show one indemnitor block for an order with three
   * people on it, which is the shape of claim this card exists to avoid.
   */
  /**
   * Seed the answer from the ORDER, once, when PMS echoes one back (BRK-S162).
   *
   * The server is the state (NN #9): a resumed repeat request must show the
   * declaration it was given rather than an empty radio group. It only ever
   * ADOPTS a server answer over an absent local one — a broker who has just
   * changed their mind on this card is not overruled by a poll.
   */
  useEffect(() => {
    const declared = order?.changeDeclaration;
    if (!declared || repeatChoice !== null) return;
    setRepeatChoice(choiceFor(declared));
  }, [order?.changeDeclaration, repeatChoice]);

  const repeatSections = useMemo<RepeatSummarySection[]>(() => {
    if (!repeatRequest || !config) return [];
    return summarisedSteps(config.steps).flatMap((step) => {
      if (!isFieldsStep(step)) return [];
      return [
        {
          step,
          fields: fieldsForStep(step),
          values: rosterBagFor(
            step,
            rowsForStep(step.key),
            withMaritalStatusOptions(step.fields.map(toRenderField)),
          ),
        },
      ];
    });
    // The bags are read at render; they are not triggers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repeatRequest, config, fieldsForStep, values, rowsForStep]);

  /**
   * The insured's EIN as the ORDER holds it — shown on the Summary, and a
   * control nowhere.
   *
   * Read out of the step bags rather than fetched: it is a value the broker
   * already filed, PMS prefilled it onto this order, and a second read of the
   * account to render one line would be a second source for it.
   */
  const repeatEin = useMemo<string | null>(() => {
    if (!repeatRequest || !config) return null;
    for (const step of config.steps) {
      if (!isFieldsStep(step)) continue;
      const bag = values[step.key] ?? {};
      for (const field of immutableFields(
        withMaritalStatusOptions(step.fields.map(toRenderField)),
      )) {
        const value = bag[field.name];
        if (typeof value === "string" && value.trim() !== "") return value;
      }
    }
    return null;
  }, [repeatRequest, config, values]);

  const chips = useMemo(
    () =>
      bondChips(config?.steps ?? [], {
        skipPreapproval: hasStandingPreapproval(preapproval),
        // The ONE thing the standalone flow changes about the spine, and it
        // comes from the CONFIGURATION rather than from the product: this page
        // knows nothing about which subproduct it is running (BRK-S160).
        flow: bondFlowOf(config),
        ...(repeatRequest
          ? {
              repeat: {
                declaration:
                  repeatChoice === null ? null : declarationFor(repeatChoice),
              },
            }
          : {}),
      }),
    [config, preapproval, repeatRequest, repeatChoice],
  );
  const stepTitles = chipTitles(chips);
  const currentChip: BondChip | undefined = chips[step];
  /**
   * Is the broker standing on the code-owned project-&-bond terms?
   *
   * DERIVED FROM THE CHIP'S KIND, never from `step === chips.length - 1`
   * (BRK-S160). The two were the same thing until the standalone flow, whose
   * LAST chip is the Indication — so an index comparison would have rendered
   * the terms form, the standing banner and the not-granted notice on top of
   * the indication, and put a "Submit bond request" button under them.
   */
  const onFinalChip = currentChip?.kind === "final";

  /**
   * The fields on the surface in front of the broker that this build cannot
   * draw a control for (BRK-S142).
   *
   * A FIELD-level counterpart to `UnsupportedStep`, and deliberately harsher:
   * that card offers "continue without it", because a whole step may be
   * optional to the broker's progress. A field is part of a step they are
   * otherwise completing, so letting them past it would file an order missing
   * something the configuration declared — with a green tick on the chip and
   * nothing on screen saying so. So it BLOCKS, and the footer says why.
   */
  const blockedFields = useMemo<BondRenderField[]>(() => {
    if (onFinalChip) {
      // BRK-S168 — only what the broker can SEE. A field suppressed by a
      // "not applicable" unit would otherwise block Continue with an
      // explanation card nobody can reach.
      return unsupportedFields(
        visibleBondFields(
          (config?.finalStep.fields ?? []).map(toRenderFinalField),
          values[FINAL_STEP_KEY] ?? {},
        ),
      );
    }
    if (currentChip?.kind !== "step" || !isFieldsStep(currentChip.step)) {
      return [];
    }
    // BRK-S172 — only what the broker can actually SEE. An unsupported field
    // inside a section they never opened would block Continue with an
    // explanation card nobody can reach.
    return unsupportedFields(
      visibleBondFields(
        fieldsForStep(currentChip.step),
        values[currentChip.step.key] ?? {},
      ),
    );
  }, [onFinalChip, config, currentChip, fieldsForStep, values]);

  // A resumed order can only be positioned once BOTH the order and its config
  // are in — the spine doesn't exist until the config resolves. Once, per resume:
  // after that the broker's own Back/Continue owns the step.
  useEffect(() => {
    if (!routeId || landedRef.current || !order || !config || !piiSettled)
      return;
    landedRef.current = true;
    // Read straight off the wire: every step's bag arrives COMPLETE, pushed and
    // bond-order-private alike, resolved server-side (PMS-S238 / BRK-S110). The
    // landing rule counts a pushed step as finished and the controls re-open
    // prefilled from the step's own bag — no client-side redistribution, which
    // was BRK's second source of truth for where a value belonged (BRK-S107).
    setStep(
      resumeChipIndex(
        chips,
        values,
        runs,
        fieldsForStep,
        (step) =>
          rosterBagFor(
            step,
            rowsForStep(step.key),
            withMaritalStatusOptions(step.fields.map(toRenderField)),
          ),
        order.indemnity,
      ),
    );
    // `values`/`runs`/`fieldsForStep`/`chips` are read at the moment the halves
    // land; they are not triggers, or every keystroke would re-position the
    // flow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId, order, config, piiSettled]);

  /**
   * Whether the step under the broker's cursor is the roster — which is what
   * the footer's ONE button is named for (BRK-S164).
   *
   * The GATE itself is no longer read here. `isRosterComplete` is asked AFTER
   * a save, in `saveAndAdvance`, because the evaluation is upstream: a reading
   * taken before the click is always an answer about the PREVIOUS roster, and
   * BRK-S156 spent it disabling a Continue that stood beside a "Save and
   * check" the broker had to press first anyway.
   */
  const rosterStepKey =
    currentChip?.kind === "step" && rosterKind(currentChip.step) === "indemnity"
      ? currentChip.step.key
      : null;

  const subproductName =
    products?.flatMap((p) => p.subproducts).find((s) => s.id === subproductId)
      ?.name ?? "";

  /**
   * The PROGRAM the indication is for — the PRODUCT's display name from the
   * catalog ("Construction Bond"), not the subproduct's ("Bid Bond").
   *
   * A preapproval qualifies the insured for a surety PROGRAM, and every
   * subproduct under a product shares it. Naming the subproduct there would say
   * the insured qualifies for bid bonds specifically, which is both wrong and
   * narrower than what Signal granted.
   */
  const programName =
    products?.find((p) => p.subproducts.some((sp) => sp.id === subproductId))
      ?.name ?? "";

  /** How the insured is named on screen. The order's own legal name once it
   *  exists, falling back to what step 1 was told — never a bare "your
   *  business", which reads as a form that lost track of who it is about. */
  const insuredName =
    order?.accountLegalName ?? accountName ?? newLegalName.trim();

  function setValue(
    stepKey: string,
    name: string,
    value: BondFieldValue | undefined,
    /** The step's rendered fields, so unticking a section's controller can
     *  clear its dependants (BRK-S172). Omitted where no step owns one. */
    stepFields: readonly BondRenderField[] = [],
  ) {
    // Editing a prefilled field IS its review (BRK-S120) — the marker clears on
    // the first keystroke, exactly as it does on the New Business path.
    touchReview(reviewKey(stepKey, name));
    setMissing([]);
    // The step's own verdict on the LAST save stops being true the moment the
    // broker changes an answer — leaving it up would be a complaint about a
    // roster that no longer exists (BRK-S164).
    setRosterNotice(null);
    setInvalid((all) => {
      if (!(name in all)) return all;
      const next = { ...all };
      delete next[name];
      return next;
    });
    // BRK-S172 — unticking a conditional section's controller CLEARS what it
    // was hiding, at the moment the broker unticks it rather than at save
    // time. Same rule as the bond-penalty toggle: what the broker sees is what
    // is stored, and re-ticking then offers a clean section instead of a
    // half-remembered one about a parent they have just said does not exist.
    const probe: BondRenderField = { name, label: name, type: "text" };
    const section = sectionRuleFor(probe);
    // BRK-S168 — the same rule for the other shape: answering a unit "not
    // applicable" clears the length and the figure beside it, at the moment the
    // broker answers rather than at save time.
    const dependant = dependantRuleFor(probe);
    const cleared = [
      ...(section && value !== true
        ? clearedByBondSection(section, stepFields)
        : []),
      ...(dependant
        ? clearedByBondDependants(dependant, stepFields, value)
        : []),
    ];
    setValues((all) => {
      const bag = { ...(all[stepKey] ?? {}), [name]: value };
      for (const key of cleared) delete bag[key];
      return { ...all, [stepKey]: bag };
    });
  }

  /**
   * A roster field's change, plus the one structural consequence a value can
   * have: answering an owner's MARITAL STATUS decides whether their spouse has
   * a seat, and therefore whether a spouse block is drawn (BRK-S152).
   *
   * Three answers, not two (`spouseSigns`): married or in a domestic
   * partnership adds the row, the four that do not add a spouse remove it, and
   * an UNKNOWN status leaves whatever is there alone — the seat is
   * `needs_information` upstream, and clearing a dropdown is not a statement
   * that somebody is unmarried.
   *
   * Removing the row takes its values with it. Leaving them behind would file a
   * spouse for an owner who just said they have none — the answers would ride
   * the save invisibly, with no block on screen to show them.
   */
  function setRosterValue(
    stepKey: string,
    name: string,
    value: BondFieldValue | undefined,
    spousesOn: boolean,
  ) {
    setValue(stepKey, name, value);
    const scoped = unscopeRosterName(name);
    if (!spousesOn || scoped?.path !== MARITAL_STATUS_PATH) return;
    const ownerId = scoped.ownerId;
    const signs = spouseSigns(value);
    if (signs === null) return;
    const roster = rowsForStep(stepKey);
    const spouse = spouseOf(roster, ownerId);
    if (signs && spouse) return;
    setRosterRows((all) => ({
      ...all,
      [stepKey]: signs
        ? withSpouse(roster, ownerId)
        : withoutSpouse(roster, ownerId),
    }));
    if (!signs && spouse) {
      setValues((all) => ({
        ...all,
        [stepKey]: withoutPersonValues(all[stepKey] ?? {}, spouse.ownerId),
      }));
    }
  }

  /** Every form value belonging to one person, dropped — so removing somebody
   *  takes their answers with them rather than filing them against nobody. */
  function withoutPersonValues(
    bag: BondValueBag,
    ownerId: string,
  ): BondValueBag {
    const out: BondValueBag = {};
    for (const [key, value] of Object.entries(bag)) {
      if (unscopeRosterName(key)?.ownerId === ownerId) continue;
      out[key] = value;
    }
    return out;
  }

  /**
   * "+ Add owner" — one more row on the CAP TABLE, never a choice of signer.
   *
   * It ADOPTS before it mints (BRK-S156). A person the order already declares
   * but whose block is not drawn — the roster PMS re-derived from the insured's
   * account — comes back under the identity PMS holds for them; minting a fresh
   * one would file a second personnel row for one person, which is the defect
   * not drawing them was supposed to avoid rather than cause.
   */
  function addRosterOwner(stepKey: string) {
    const drawn = rowsForStep(stepKey);
    const adopt = undrawnOwners(declaredRowsForStep(stepKey), drawn)[0];
    const next = addOwner(drawn, adopt);
    setRosterRows((all) => ({ ...all, [stepKey]: next }));
    // A fresh card exists to be TYPED INTO, so it opens — said explicitly
    // rather than left to the default, which is a fact about the order and not
    // about the broker having just asked for this card (BRK-S164).
    const added = next[next.length - 1];
    if (added) {
      setOpenCards((all) => ({
        ...all,
        [stepKey]: { ...(all[stepKey] ?? {}), [added.ownerId]: true },
      }));
    }
  }

  /**
   * Removing an owner takes their spouse row and both sets of answers — and,
   * when the ORDER already holds them, files the removal (BRK-S164).
   *
   * The save is conditional on purpose. A person the order has never heard of
   * needs no round trip to be forgotten, and a PUT for one would put a
   * server-side validation of the whole roster in front of a broker whose only
   * intention was to close a card they opened by mistake. A person the order
   * DOES hold is a different matter: leaving them there until some later save
   * is what would make Remove a lie.
   */
  function removeRosterOwner(stepDef: BondStep, ownerId: string) {
    const stepKey = stepDef.key;
    const roster = rowsForStep(stepKey);
    const spouse = spouseOf(roster, ownerId);
    const next = removeOwner(roster, ownerId);
    // `removeOwner` refuses to drop the last owner, so a no-op must not go on
    // to file anything.
    if (next.length === roster.length) return;
    setRosterRows((all) => ({ ...all, [stepKey]: next }));
    setValues((all) => {
      let bag = withoutPersonValues(all[stepKey] ?? {}, ownerId);
      if (spouse) bag = withoutPersonValues(bag, spouse.ownerId);
      return { ...all, [stepKey]: bag };
    });
    setMissing([]);
    setCardErrors((all) => {
      if (!(ownerId in all)) return all;
      const rest = { ...all };
      delete rest[ownerId];
      return rest;
    });
    const saved = rosterStepValues(order ?? {}, stepKey) ?? {};
    if (!hasRosterAnswer(saved, ownerId, order?.owners)) return;
    void saveAndAdvance(stepDef, {
      roster: next,
      // Nothing on any card is CHECKED by a removal: blocking "forget this
      // person" on a half-typed card elsewhere would trap the broker on the
      // very screen they were tidying.
      cardOwnerIds: new Set<string>(),
      advance: false,
    });
  }

  const step1Ready =
    Boolean(subproductId) &&
    Boolean(config) &&
    // EVERY statement, not one blanket box. A configuration that asks for none
    // is trivially satisfied, exactly as it was.
    allEligibilityConfirmed(config?.eligibility ?? [], eligibilityConfirmed) &&
    (mode === "existing"
      ? Boolean(accountId)
      : // The server's new-account create requires BOTH (the draft floor is
        // legal name + state), so the gate matches it.
        Boolean(newLegalName.trim()) && Boolean(newState));

  /**
   * DO NOT START A SECOND PRE-APPROVAL WHILE ONE IS IN FLIGHT (BRK-S181).
   *
   * Before a pre-approval is created for an insured already on the books, ask
   * the existing list — narrowed server-side to this insured — for its
   * pre-approval orders, and each one's ORDER VIEW whether it is still
   * NON-TERMINAL (`findPreapprovalsInProgress` owns the rule; never the list
   * row's `decision`, which is the insured's standing program). If one is, the
   * broker chooses: Continue it, or Discard it and start over. Nothing is
   * created until they do.
   *
   * PRE-APPROVALS ONLY, and only an existing insured: a bid bond keeps today's
   * behaviour because a contractor has several in progress at once, and a
   * brand-new insured has no orders to collide with. A check that cannot be
   * READ creates nothing — silently proceeding is exactly how the second live
   * order this guard exists to prevent would be made.
   *
   * Returns whether the create may go ahead.
   */
  async function clearedToStart(): Promise<boolean> {
    if (mode !== "existing" || !accountId) return true;
    if (bondFlowOf(config) !== "preapproval") return true;
    try {
      const found = await findPreapprovalsInProgress(authedRequest, accountId);
      if (found.length === 0) return true;
      setInFlight(found);
      return false;
    } catch (err) {
      setError(
        `We couldn’t check whether this insured already has a pre-approval in progress, so nothing was started. ${messageFor(err)}`,
      );
      return false;
    }
  }

  /**
   * DISCARD AND START OVER: cancel every in-flight order FIRST, and only then
   * create the new one — so neither branch ever leaves two live orders. A
   * cancel refused with ORDER_HAS_DECISION means discovery settled a decision
   * PMS had not mirrored yet when the guard read the order view: that order is
   * DECIDED, not in flight, so it is treated exactly as the guard would have
   * treated it with the mirror caught up — nothing to discard, nothing live to
   * collide with, and no error. Any other failure stops here, with nothing
   * created and the choice still offered.
   */
  async function discardAndStartOver() {
    if (!inFlight) return;
    setBusy(true);
    setError(null);
    try {
      for (const existing of inFlight) {
        try {
          await cancelBondSubmission(authedRequest, existing.id);
        } catch (err) {
          if (!isOrderDecided(err)) throw err;
        }
      }
    } catch (err) {
      setError(messageFor(err));
      setBusy(false);
      return;
    }
    setInFlight(null);
    await startOrder(false, { guarded: true });
  }

  async function startOrder(
    confirmDuplicates = false,
    opts: { guarded?: boolean } = {},
  ) {
    setBusy(true);
    setError(null);
    setEligibilityRefusal(null);
    if (!opts.guarded && !(await clearedToStart())) {
      setBusy(false);
      return;
    }
    const statements = config?.eligibility ?? [];
    // BRK-S170 — what the broker attested to, minted at the moment the order is
    // created: the attestation takes effect when it is FILED, not when a box was
    // ticked. Sent only when the pinned contract describes the field; until then
    // the create body is byte-identical to what it has always been, which is the
    // claim `test/lib/bondEligibility.test.ts` makes rather than assumes.
    const eligibility = BOND_CREATE_SENDS_ELIGIBILITY
      ? bondEligibilityBlock(
          statements,
          eligibilityConfirmed,
          new Date().toISOString(),
        )
      : null;
    try {
      const result = await createBondSubmission(authedRequest, {
        subproductId,
        mode,
        ...(mode === "existing"
          ? { accountId }
          : {
              accountFields: {
                legalName: newLegalName.trim(),
                ...(newState ? { state: newState } : {}),
              },
            }),
        ...(confirmDuplicates ? { confirmDuplicates: true } : {}),
        ...(eligibility ? { eligibility } : {}),
      });
      if (result.kind === "duplicates") {
        // Advisory, never a block: the broker picks a candidate or creates anyway.
        setDuplicates(result.candidates);
        return;
      }
      setDuplicates(null);
      setOrder(result.submission);
      seedFromOrder(result.submission);
      // An order can arrive already referred (a pull that ran before a reload).
      if (result.submission.referred) setReferred(true);
      setStep(1);
      // The order now exists server-side with its SB number, so put it in the
      // URL: from here a reload resumes instead of restarting (BRK-S088). The
      // order is already in state and the landing is already chosen, so the id
      // arriving in the URL neither re-fetches it nor moves the broker.
      landedRef.current = true;
      // `/continue`, not `/:id` — that one is the read-first profile now
      // (BRK-S140), and pushing it here would drop the broker out of the flow
      // they are halfway through.
      navigate(`/surety/submissions/${result.submission.id}/continue`, {
        replace: true,
      });
    } catch (err) {
      // A refusal the server ATTRIBUTED to one statement goes under that
      // statement (BRK-S170). The step-level line is still set: a broker who has
      // scrolled past the list would otherwise watch Continue do nothing.
      const index = eligibilityRefusalIndex(err, statements.length);
      const reason = messageFor(err);
      if (index !== null) setEligibilityRefusal({ index, reason });
      setError(reason);
    } finally {
      setBusy(false);
    }
  }

  /** Record a settled run's verdict. A non-pass outcome refers the order — sticky
   *  for the rest of the flow, and never a reason to stop. */
  const noteRun = useCallback((run: BondStepRun) => {
    if (!isReferral(run)) return;
    setReferred(true);
    setReferralReasons((prev) => {
      const next = run.reasons ?? [];
      const merged = [...prev];
      for (const r of next) if (!merged.includes(r)) merged.push(r);
      return merged;
    });
  }, []);

  // ── Documents attached during intake (BRK-S145) ─────────────────────────────
  //
  // The configuration may declare a `files` field on any step; the control it
  // renders needs three things this component owns — the served upload rules,
  // the order to attach to, and whether the order is still a draft.

  /** Whether ANY field in this configuration collects documents. Gates the
   *  vocabulary read: a configuration with no `files` field must not spend a
   *  request on rules nothing will consult. */
  const collectsFiles = useMemo(() => {
    if (!config) return false;
    const stepFields = config.steps.flatMap((step) =>
      isFieldsStep(step) ? step.fields.map(toRenderField) : [],
    );
    const finalFields = config.finalStep.fields.map(toRenderFinalField);
    return [...stepFields, ...finalFields].some(
      (field) => fieldKind(field) === "files",
    );
  }, [config]);

  const documentTypes = useDocumentTypes(authedRequest, collectsFiles);

  // PMS's document TYPE per attached document, which the four-field reference
  // in the value bag does not carry. Read once the order is in hand, so a
  // RESUMED order's rows say "Bid documents" rather than "PDF" — and a failed
  // read is silence, not an error: the row still renders, from its content
  // type. Not the value bag's source of truth and never written back into it.
  const [documents, setDocuments] = useState<BondDocument[]>([]);
  const orderId = order?.id;
  useEffect(() => {
    if (!orderId || !collectsFiles) return;
    let cancelled = false;
    void (async () => {
      try {
        const body = await listBondDocuments(authedRequest, orderId);
        if (!cancelled) setDocuments(body.documents);
      } catch {
        // A label is a nicety; the reference is the fact. Nothing to say.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest, orderId, collectsFiles]);

  const filesPort = useMemo<BondFilesPort | undefined>(() => {
    if (!orderId) return undefined;
    return {
      rules: documentTypes.rules,
      types: documentTypes.types,
      rulesError: documentTypes.error,
      readOnly: order?.status === "submitted",
      typeLabel: (doc: BondDocumentRef) => {
        const known = documents.find((d) => d.documentId === doc.documentId);
        return known
          ? documentTypes.labelFor(known.type)
          : fileKindLabel(doc.contentType);
      },
      upload: async (fieldKey, file, type, onProgress) => {
        const stored = await uploadBondDocument(
          authedUploadWithProgress,
          orderId,
          {
            fieldKey,
            file,
            type,
            onProgress: ({ loaded, total }) =>
              onProgress(total ? Math.round((loaded / total) * 100) : 0),
          },
        );
        // Remembered for its LABEL only — the page's own read model, never a
        // second copy of the answer.
        setDocuments((current) => [
          ...current.filter((d) => d.documentId !== stored.documentId),
          stored,
        ]);
        // NARROWED to the four fields the contract publishes as a step value.
        // The upload answers with the fuller `BondDocument` — `type`,
        // `fieldKey` and `uploadedAt` are the SERVER's to know — and echoing
        // those into the bag would put a second, staler copy of them on the
        // submit body. Caught by a test that read the real submit body.
        return {
          documentId: stored.documentId,
          filename: stored.filename,
          contentType: stored.contentType,
          byteSize: stored.byteSize,
        };
      },
      remove: async (documentId) => {
        await deleteBondDocument(authedRequest, orderId, documentId);
        setDocuments((current) =>
          current.filter((d) => d.documentId !== documentId),
        );
      },
    };
    // `documentTypes` is rebuilt every render (it closes over `types`), so the
    // fields it contributes are listed rather than the object itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orderId,
    order?.status,
    documents,
    documentTypes.rules,
    documentTypes.error,
    documentTypes.types,
    authedRequest,
    authedUploadWithProgress,
  ]);

  async function submitOrder() {
    const declared = (config?.finalStep.fields ?? []).map(toRenderFinalField);
    const finalBag = values[FINAL_STEP_KEY] ?? {};
    // BRK-S168 — a unit answered "not applicable" has no length and no figure
    // beside it, so the terms step must not go on asking for them. ONE
    // narrowing, exactly as the fields-step save does, so the unsupported
    // check, the required checklist, the format rules and the wire values all
    // work from the same list.
    const fields = visibleBondFields(declared, finalBag);
    if (unsupportedFields(fields).length) {
      setError(BLOCKED_BY_UNSUPPORTED);
      return;
    }
    // BRK-S169 — a composite deliberately leaves ONE of its two members empty,
    // so a configuration marking either one required would block a step the
    // broker has in fact answered. Whichever member their sibling covers is
    // exempt; nothing else is.
    const covered = bondPairSatisfied(fields, finalBag);
    const unmet = unansweredFields(fields, finalBag).filter(
      (field) => !covered.has(field.name),
    );
    if (unmet.length) {
      setMissing(unmet);
      return;
    }
    const bad = invalidFields(fields, finalBag);
    if (bad.length) {
      setInvalid(Object.fromEntries(bad.map((b) => [b.field.name, b.reason])));
      return;
    }
    if (!order || busy) return;
    setBusy(true);
    setError(null);
    try {
      // The terms ride on the submit itself — the server validates them against
      // the order's config snapshot and stores them with the filing. (They are
      // bond-specific, not account fields, so there is no push-to-account here.)
      const filed = await submitBond(
        authedRequest,
        order.id,
        // Canonicalised the same way a step save is: the terms ride on the
        // submit body, so a final-step phone reaches the filing through here
        // and nowhere else (BRK-S111).
        bondWireValues(fields, values[FINAL_STEP_KEY] ?? {}) as Record<
          string,
          unknown
        >,
      );
      if (filed.referred) setReferred(true);
      // The link rides on the submit's OWN response — PMS publishes
      // `accountingLink` on every bond-order body — so the confirmation shows
      // it with no second read (BRK-S115).
      setSubmittedLink(filed.accountingLink);
      setSubmittedBankLink(filed.bankLink);
      // The Signal Advance answer rides the same response (BRK-S161). Carried
      // in its own state beside the two links, and for the same reason: the
      // confirmation screen is driven by the SUBMIT's body, not by a re-read.
      setSubmittedAdvanceOptIn(filed.advanceOptIn ?? null);
      // The filed order itself, for the GIA section (BRK-S182): its `gia`
      // summary rides this response, and `showsGiaSection` decides from THIS
      // body — the approved outcome only — rather than from a local flag.
      setSubmittedOrder(filed);
      setSubmittedNumber(filed.number);
    } catch (err) {
      // A 409 means this order was already filed — the broker double-submitted,
      // or a retry landed twice. That is success that already happened, not a
      // failure to show them.
      if (isAlreadySubmitted(err)) {
        // No filed doc on this path — fall back to the order already in hand,
        // which came from the GET and carries `accountingLink` too. When it
        // carries none, the panel renders NOTHING: a link nobody handed us is
        // silence, never an error, and never a fabricated one (BRK-S115).
        // (The wider staleness of this path is BRK-S113's, not this story's.)
        setSubmittedLink(order.accountingLink);
        setSubmittedBankLink(order.bankLink);
        setSubmittedAdvanceOptIn(order.advanceOptIn ?? null);
        setSubmittedOrder(order);
        setSubmittedNumber(order.number);
        return;
      }
      // The terms ride on the submit body, so a rejection names final-step
      // fields — anchor them the same way a step save's are (BRK-S092).
      const anchored = anchorServerError(
        err,
        fields.map((f) => f.name),
      );
      setInvalid(anchored.byField);
      setError(anchored.summary);
    } finally {
      setBusy(false);
    }
  }

  /** Adopt a duplicate candidate instead of creating a second insured. */
  function useCandidate(candidate: BondDuplicateCandidate) {
    setMode("existing");
    setAccountId(candidate.accountId);
    setAccountName(candidate.legalName);
    setDuplicates(null);
  }

  /**
   * How ONE call to `saveAndAdvance` differs from the plain footer one
   * (BRK-S164).
   *
   * The wire is identical in every case — the whole roster, every time — and
   * that is deliberate: a per-card save that sent only its own card would be a
   * second wire shape for one endpoint, and the entry lists the contract asks
   * for are built from the roster as a whole. What varies is what the save
   * CHECKS before it goes, and what it does with the answer.
   */
  interface RosterSaveOptions {
    /** The roster to file — an override for a removal, whose state the render
     *  has not caught up with at the moment the save is issued. */
    roster?: readonly RosterPerson[];
    /** Whose CARDS this save validates. Undefined checks the whole step (the
     *  non-roster path, unchanged). An empty set checks the order-level
     *  fields and nobody's card. */
    cardOwnerIds?: ReadonlySet<string>;
    /** Whether a settled roster moves the wizard on. */
    advance?: boolean;
    /** The CARD this save belongs to, when it is one card's own Save. It is
     *  where the busy state and any refusal are shown — at step level a card's
     *  refusal reads as the whole roster's. */
    cardOwnerId?: string;
  }

  /**
   * File the repeat request's DECLARATION and move on (BRK-S162).
   *
   * ⚠️ WHY IT RIDES ON A STEP SAVE, AND WHICH ONE. The declaration is a fact
   * about the ORDER, and the broker BFF offers exactly one write between an
   * order's creation and its submit — the step save (PMS-S462 puts
   * `changeDeclaration` there and stores it on the pointer). So it goes on a
   * save against the FIRST step the summary covers, with an EMPTY values bag: a
   * step save MERGES, so an empty bag changes nothing the broker filed, and
   * `pushToAccount: false` keeps it from touching the insured's record on a
   * request that has not edited anything.
   *
   * It is sent for EVERY answer, "nothing changed" included. That is the answer
   * an underwriter most needs recorded — it is the broker asserting the file is
   * current — and it is the one path where no other save would ever carry it.
   *
   * BEST-EFFORT IS NOT AN OPTION HERE. The declaration decides which steps the
   * broker fills in next, so a silent failure would move them past work they
   * said they needed to do. A refusal keeps them on the Summary with the
   * server's own sentence.
   */
  async function confirmRepeatDeclaration() {
    if (!order || !config || repeatChoice === null) return;
    const first = summarisedSteps(config.steps)[0];
    setBusy(true);
    setError(null);
    try {
      if (first) {
        const fresh = await saveBondStep(authedRequest, order.id, first.key, {
          values: {},
          changeDeclaration: declarationFor(repeatChoice),
          // Never on this save: it edits nothing, so there is nothing to push,
          // and a push report claiming otherwise would be a claim about the
          // insured's record that this click did not make.
          ...(mode === "existing" ? { pushToAccount: false } : {}),
        });
        setOrder(fresh.submission);
      }
      setStep((current) => current + 1);
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  async function saveAndAdvance(
    stepDef: BondStep,
    opts: RosterSaveOptions = {},
  ) {
    const advance = opts.advance ?? true;
    const declared = stepDef.type === "fields" ? fieldsForStep(stepDef) : [];
    const roster =
      opts.roster ??
      (rosterKind(stepDef) === "indemnity" ? rowsForStep(stepDef.key) : []);
    // The CONFIGURATION's fields, in contract paths — the same list the render
    // and the resume landing hand to these two, and NOT the scoped names
    // `fieldsForStep` draws (BRK-S154: passing those made the seeding scope a
    // name that was already scoped, and the save then spelled a contract path
    // nobody declared).
    const template =
      stepDef.type === "fields"
        ? withMaritalStatusOptions(stepDef.fields.map(toRenderField))
        : [];
    // A ROSTER step's form bag is SCOPED per person (see `bondRoster.ts`), so
    // the gate reads the same bag the controls wrote — and the order's own
    // values are folded in first, which is what lets a resumed order be
    // validated against what the server already holds.
    const bag = rosterBagFor(stepDef, roster, template);
    // BRK-S172 — a conditional section the broker has not opened is a question
    // this step did not ask, so ONE narrowing covers all three things that read
    // the field list: the required checklist, the format rules, and the wire
    // values. Three separate exemptions is how a field ends up invisible to the
    // broker and still required by the gate — a step nobody can get past and
    // nobody can see why.
    const fields = visibleBondFields(declared, bag);
    // A field with no control cannot be answered, so saving this step would
    // file it incomplete with a tick beside it (BRK-S142). The footer already
    // disables Continue; this is the guard for every other way in.
    if (unsupportedFields(fields).length) {
      setError(BLOCKED_BY_UNSUPPORTED);
      return;
    }
    // ⚠️ WHAT IS CHECKED IS NARROWER THAN WHAT IS SENT (BRK-S164). A per-card
    // Save answers for its own card, and Save & Continue for the cards that
    // are OPEN — the collapsed ones were saved and accepted already, so
    // re-checking them would block the step on a person the broker cannot see
    // and did not touch. Order-level fields are in every set, because they
    // belong to no card and are always owed.
    const checked =
      opts.cardOwnerIds === undefined
        ? fields
        : fieldsForCards(fields, roster, opts.cardOwnerIds);
    /** Send the broker to the card a refusal is about, when it is about one. */
    const revealOffender = (field: BondRenderField | undefined) => {
      const scoped = field && unscopeRosterName(field.name);
      if (scoped) revealOwnerCard(cardOwnerIdOf(roster, scoped.ownerId));
    };
    const unmet = unansweredFields(checked, bag);
    if (unmet.length) {
      setMissing(unmet);
      revealOffender(unmet[0]);
      return;
    }
    const bad = invalidFields(checked, bag);
    if (bad.length) {
      setInvalid(Object.fromEntries(bad.map((b) => [b.field.name, b.reason])));
      revealOffender(bad[0]?.field);
      return;
    }
    // BRK-S171 — the two things a roster can be wrong about as a WHOLE: the
    // shares add up past 100%, or two people share an email. Checked over the
    // ENTIRE roster rather than over `checked`, deliberately: both are facts
    // about the cap table, and a per-card save that only looked at its own card
    // could not see either. The server refuses both anyway; this is the round
    // trip saved, not the gate.
    if (rosterKind(stepDef) === "indemnity") {
      const findings = rosterFindings(roster, fields, bag);
      if (placeRosterFindings(stepDef.key, findings)) return;
    }
    if (!order) return;
    setBusy(true);
    if (opts.cardOwnerId) setSavingOwnerId(opts.cardOwnerId);
    setError(null);
    setRosterNotice(null);
    try {
      // A roster step files two facts the broker never typed: which owner each
      // spouse belongs to, and — unless they said it differs — the owner's
      // address on the spouse's entry. Added at the SAVE rather than pre-filled
      // into the form: a copy taken at save cannot go stale against an owner's
      // address the broker corrected afterwards. `rosterWireValues` is also
      // what turns the scoped bag back into the contract's `{ownerId, value}`
      // entry lists — no scoped name survives it.
      const named = namedRoster(roster, bag);
      const wire =
        rosterKind(stepDef) === "indemnity"
          ? rosterWireValues(bondWireValues(fields, bag), named, fields, {
              spouseAddressDiffers: differsForStep(
                stepDef.key,
                named,
                template,
              ),
            })
          : bondWireValues(fields, bag);
      // A blank write-only PII field is OMITTED, never sent as "" (BRK-S105).
      // PMS reads an absent key as "use the stored value" and injects the
      // plaintext into the vendor payload server-side; sending "" would say the
      // same thing while also claiming the broker cleared the field.
      //
      // `bondWireValues` has already put the bag in the CONTRACT's spelling —
      // today that means phones as bare E.164, because the control humanises
      // for display and D-10's split runs both ways (BRK-S111). The two are
      // independent (one rewrites phones, the other drops blank PII keys), so
      // the order is only about types.
      const body = omitBlankPii(wire as BondValueBag);
      // ⚠️ THE LAST LINE (BRK-S154). `rosterWireValues` is where a scoped form
      // name is supposed to stop; this is the assertion that it did, made in
      // the one place a step-save body is assembled. Defence in depth, not the
      // fix for any particular leak — but a key like `<ownerId>::personal[].x`
      // is a contract path this portal invented, and PMS relays it verbatim to
      // discovery, which refuses the WHOLE save over a block nobody declared.
      // Failing here names the keys and says nothing was saved.
      const bad = invalidWireKeys(body);
      if (bad.length) {
        // Logged with the step, so a support conversation starts from the
        // evidence rather than from the broker's description of a 4xx.
        console.error("bond step save blocked — invalid field names", {
          step: stepDef.key,
          keys: bad,
        });
        setError(invalidWireKeyMessage(bad));
        return;
      }
      const fresh = await saveBondStep(authedRequest, order.id, stepDef.key, {
        values: body,
        // WHO the people on this step are (contract 3.0.0 / SUR-S065). Declared
        // by the caller, which is what makes an identity exist before the first
        // keystroke — every entry on this save is keyed by one of these ids.
        // WHOSE credit is pulled rides out here rather than in `values`
        // (BRK-S156): it addresses no contract path, it is a fact about the
        // roster, and the roster is what `owners` declares.
        ...(rosterKind(stepDef) === "indemnity"
          ? {
              owners: ownerDeclarations(
                named,
                primaryForStep(stepDef.key, roster, bag),
              ),
            }
          : {}),
        // Offered in existing mode only, default ON — so the account record
        // keeps up with what the broker just corrected unless they opt out.
        ...(mode === "existing"
          ? { pushToAccount: pushToAccount[stepDef.key] !== false }
          : {}),
      });
      setOrder(fresh.submission);
      // ⚠️ RE-SEED THE ROSTER FROM THE RESPONSE (PMS-S393). A person this save
      // CREATED now has their real `personnel.id`, and the response's
      // `owners[]` is re-derived from the updated account — so the id this
      // page minted for them is spent. Dropping the local layout is what
      // adopts the real one: `rowsForStep` falls back to the order's own
      // roster and entry lists, which the save has just refreshed.
      //
      // The address question is dropped with it, and re-derives from the two
      // stored addresses (`spouseAddressDiffers`) — it is a question about
      // values the order carries, so re-deriving it is reading rather than
      // forgetting.
      //
      // ⚠️ AND THE BAG GOES WITH IT (BRK-S155). Dropping the layout while
      // KEEPING the pre-save bag was half a re-seed: every scoped key in it was
      // addressed to an id that had just been spent, so the block for the real
      // one rendered EMPTY — the broker retyped a name they had typed a second
      // earlier — and `personal[].spouseOf`, which lives in the bag, was not
      // there to read, so a spouse row came back promoted to a second owner.
      // The bag is replaced by the RESPONSE's own, in the contract's entry-list
      // spelling, which is exactly what a resume holds; `rosterBagFor` derives
      // the scoped form bag from it at every render, so the layout and the
      // values cannot disagree (NN #9).
      const rosterBag = rosterStepValues(fresh.submission, stepDef.key);
      if (rosterKind(stepDef) === "indemnity" && rosterBag) {
        setValues((all) => ({ ...all, [stepDef.key]: rosterBag }));
        setRosterRows((all) => {
          if (!(stepDef.key in all)) return all;
          const next = { ...all };
          delete next[stepDef.key];
          return next;
        });
        setSpouseDiffers((all) => {
          if (!(stepDef.key in all)) return all;
          const next = { ...all };
          delete next[stepDef.key];
          return next;
        });
        // ⚠️ AND THE PRIMARY PICK GOES WITH THEM (BRK-S156). The response's
        // `owners[]` is the declaration that took: until PMS-S403 lands PMS may
        // honour a different primary from the one just declared, and keeping
        // the pick on screen would show the broker a choice the service has not
        // accepted — a promise about whose credit is pulled, made by a client
        // that does not decide it.
        setPrimaryPick((all) => {
          if (!(stepDef.key in all)) return all;
          const next = { ...all };
          delete next[stepDef.key];
          return next;
        });
      }
      // The push report (`fresh.account`) is deliberately NOT rendered
      // (BRK-S181): the notice that used to print it put raw contract paths on
      // a broker surface, so it and its amber skipped warning were removed.
      // The push itself is unchanged.
      // ⚠️ THE CARD FOLDS ON THE SERVER'S ANSWER AND ON NOTHING ELSE
      // (BRK-S164, the BRK-S113/S122 rule). The save that just landed is what
      // makes this person "saved", so the explicit open state is dropped and
      // the default takes over — and the default reads the ORDER, which now
      // holds their answers. A refused save never reaches here, so a card
      // cannot fold over a failure.
      if (rosterKind(stepDef) === "indemnity") {
        // ⚠️ RE-READ WHAT IS ON FILE (BRK-S165). This save may have filed an
        // SSN or a licence for somebody, and PMS redacts both paths out of
        // everything it echoes back — so the only way to learn that the value
        // is now held is to ask the account again. Without it a broker who
        // re-opened the card they had just saved would be asked for the SSN
        // they had just typed, with no notice saying it had landed. The read
        // is best-effort and already handled (NN #7); a failure costs a
        // notice, never the save that just succeeded.
        setPiiEpoch((n) => n + 1);
        setCardErrors({});
        setOpenCards((all) => {
          if (!(stepDef.key in all)) return all;
          const next = { ...all };
          delete next[stepDef.key];
          return next;
        });
      }
      // ⚠️ THE GATE (BRK-S156, asked HERE and only here since BRK-S164). The
      // save always happens — that is what refreshes the evaluation — and the
      // advance is what the server's answer decides. A roster the rule is not
      // finished with keeps the broker on the step with the fresh bar and the
      // fresh card notes in front of them, rather than walking them past a
      // request an underwriter is going to send back.
      //
      // The primary is read from the response's own roster, which the re-seed
      // above has just adopted: `owners` is on `fresh.submission`, so this is
      // the declaration that took and not the one that was sent.
      if (rosterKind(stepDef) === "indemnity") {
        const saved = rosterStepValues(fresh.submission, stepDef.key) ?? bag;
        const after = drawnRoster(
          rosterRowsFromValues(saved, fresh.submission.owners),
          saved,
          fresh.submission.owners,
        );
        if (
          !isRosterComplete(
            fresh.submission.indemnity,
            effectivePrimaryOwnerId(
              after,
              saved,
              fresh.submission.owners,
              undefined,
            ),
          )
        ) {
          // Say that the save LANDED and the step is not finished — the
          // evidence is on the cards. `rosterExplanation` wins wherever the
          // shortfall belongs to the roster rather than to a person, because
          // no card can carry that one.
          if (advance) {
            setRosterNotice(
              rosterExplanation(fresh.submission.indemnity) ??
                ROSTER_INCOMPLETE_NOTICE,
            );
          }
          return;
        }
      }
      if (!advance) return;
      setStep((s) => s + 1);
    } catch (err) {
      // A rejected save carries per-field detail; put each reason under its own
      // input and keep only what couldn't be placed at step level (BRK-S092).
      // Same `invalid` channel the client-side rules use, so editing a field
      // clears a server error exactly as it clears a local one.
      // BRK-S171 — a refusal about the roster as a WHOLE goes on the cards it
      // names, whichever save issued it. First, because it is the more specific
      // placement: `anchorServerError` has no field to anchor either code to
      // and would drop both to step level.
      const rosterWide =
        rosterKind(stepDef) === "indemnity"
          ? rosterFindingsFromError(err, roster, bag)
          : [];
      if (placeRosterFindings(stepDef.key, rosterWide)) return;
      const anchored = anchorServerError(
        err,
        fields.map((f) => f.name),
      );
      setInvalid(anchored.byField);
      // A card's own save reports on the card, where the fields that caused it
      // are — never at step level, which would read as the whole roster having
      // been refused.
      if (opts.cardOwnerId) {
        setCardErrors((all) => ({
          ...all,
          [opts.cardOwnerId as string]:
            anchored.summary ?? "This owner could not be saved.",
        }));
      } else {
        setError(anchored.summary);
      }
    } finally {
      setSavingOwnerId(null);
      setBusy(false);
    }
  }

  if (submittedNumber) {
    return (
      <SubmittedScreen
        number={submittedNumber}
        subproductName={subproductName}
        referred={referred}
        referralReasons={referralReasons}
        orderId={order?.id}
        accountingLink={submittedLink}
        bankLink={submittedBankLink}
        // The Signal Advance answer, and the enrolment that records one
        // (BRK-S161). The response the route answers with is the ORDER — links
        // included, because the enrolment is what makes discovery mint them —
        // so the two link panels are refreshed from the server's own body
        // rather than from a local flag.
        advanceOptIn={submittedAdvanceOptIn}
        filedOrder={submittedOrder}
        onAdvanceAnswered={(fresh) => {
          setOrder(fresh);
          setSubmittedLink(fresh.accountingLink);
          setSubmittedBankLink(fresh.bankLink);
          setSubmittedAdvanceOptIn(fresh.advanceOptIn ?? null);
        }}
      />
    );
  }

  // Resuming: nothing of the flow can render until the order is in hand, and a
  // failed read is a handled state with a way out — never a white screen (NN #7).
  if (routeId && !order) {
    return (
      <section className="mx-auto flex w-full max-w-3xl flex-col gap-4 py-8">
        {error ? (
          <p role="alert" className="text-sm text-brk-danger">
            {error}
          </p>
        ) : (
          <p role="status" className="text-sm text-brk-dim">
            Loading this bond submission…
          </p>
        )}
        <Link
          to="/surety/submissions"
          className="self-start text-sm text-brk-accent hover:underline"
        >
          ← Back to bond submissions
        </Link>
      </section>
    );
  }

  // A CANCELLED order (BRK-S181) is not a form either: PMS refuses every write
  // on it (409 ORDER_CANCELLED), so the flow would be controls that cannot be
  // saved. It says what happened and points at the record, which still opens.
  if (order?.status === "cancelled") {
    return (
      <section className="mx-auto flex w-full max-w-3xl flex-col gap-4 py-8">
        <h1 className="text-2xl font-bold tracking-tight">{order.number}</h1>
        <p role="status" className="text-sm text-brk-text">
          This request was cancelled, so it can’t be continued. It’s kept for
          your records.
        </p>
        <Link
          to={`/surety/submissions/${order.id}`}
          className="self-start text-sm font-medium text-brk-accent hover:underline"
        >
          View the request
        </Link>
      </section>
    );
  }

  // An order that has been filed is a record, not a form: it has a number and a
  // referral state, and there is nothing left to edit or re-file.
  if (order?.status === "submitted") {
    return (
      <FiledOrderView
        order={order}
        subproductName={subproductName}
        config={config}
        configLoading={configLoading}
        configError={configError}
        referred={referred}
        referralReasons={referralReasons}
      />
    );
  }

  return (
    <section className="mx-auto flex w-full max-w-3xl flex-col gap-6 py-8">
      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-brk-accent">
          Construction Bond Exchange
        </p>
        <h1 className="text-2xl font-bold tracking-tight">
          New bond submission
        </h1>
        {order && (
          <p className="text-sm text-brk-dim">
            Draft{" "}
            <span className="font-mono font-medium text-brk-text">
              {order.number}
            </span>
          </p>
        )}
      </div>

      <StepIndicator titles={stepTitles} current={step} />

      {/* Persistent from the moment a pull refers the order — through every later
          step, and again on the confirmation screen. */}
      {referred && <ReferralNotice reasons={referralReasons} />}

      <div className="flex min-w-0 flex-col gap-6">
        {step === 0 && (
          <StepEligibility
            products={products}
            catalogError={catalogError}
            subproductId={subproductId}
            onSubproductChange={setSubproductId}
            config={config}
            configLoading={configLoading}
            configError={configError}
            mode={mode}
            onModeChange={setMode}
            newLegalName={newLegalName}
            onNewLegalName={setNewLegalName}
            newState={newState}
            onNewState={setNewState}
            accountId={accountId}
            accountName={accountName}
            lockedSubproduct={subproductLocked}
            onUnlockSubproduct={() => setSubproductLocked(false)}
            lockedAccount={
              accountLocked && mode === "existing" && accountId !== ""
                ? { name: accountName, number: accountNumber }
                : null
            }
            onUnlockAccount={() => setAccountLocked(false)}
            onPickAccount={(a) => {
              setAccountId(a.id);
              setAccountName(a.legalName);
            }}
            eligibilityConfirmed={eligibilityConfirmed}
            onEligibilityChange={(index, on) =>
              setEligibilityConfirmed((prev) => {
                const next = new Set(prev);
                if (on) next.add(index);
                else next.delete(index);
                return next;
              })
            }
            eligibilityRefusal={eligibilityRefusal}
            legalNamePending={review.isPending(ELIGIBILITY_NAME_REVIEW_KEY)}
            onLegalNameTouched={() => touchReview(ELIGIBILITY_NAME_REVIEW_KEY)}
            documentPanel={
              <BondDocumentPanel
                authedRequest={authedRequest}
                prefill={prefill}
                onProposal={setProposal}
              />
            }
          />
        )}

        {/* The REPEAT request's read-only summary and its one question
            (BRK-S162). What Signal already holds, and "has any of this
            changed?" — the answer builds the spine behind this chip. */}
        {currentChip?.kind === "summary" && config && (
          <BondRepeatSummary
            steps={config.steps}
            sections={repeatSections}
            ein={repeatEin}
            choice={repeatChoice}
            onChoice={setRepeatChoice}
          />
        )}

        {/* A roster this build cannot draw — the configuration asked for one
            kind of list and got an ordinary single block, with a live Continue
            in front of it. Named instead (BRK-S114 / BRK-S152). */}
        {currentChip?.kind === "step" &&
          rosterKind(currentChip.step) === "unsupported" && (
            <UnsupportedStep
              step={currentChip.step}
              onSkip={() => setStep((s) => s + 1)}
            />
          )}

        {currentChip?.kind === "step" &&
          isFieldsStep(currentChip.step) &&
          rosterKind(currentChip.step) === "indemnity" &&
          (() => {
            const rosterStep = currentChip.step;
            const rule = effectiveIndemnityRule(
              order?.indemnity,
              config?.indemnity,
            );
            const roster = rowsForStep(rosterStep.key);
            const base = withMaritalStatusOptions(
              rosterStep.fields.map(toRenderField),
            );
            const differs = differsForStep(rosterStep.key, roster, base);
            const blocks = rosterBlocks(base, roster, {
              spouseAddressDiffers: differs,
            });
            const rosterBag = rosterBagFor(rosterStep, roster, base);
            // ⚠️ THE WAIVER IS PER PERSON (BRK-S165), and it follows neither
            // the first block nor the primary radio: it follows what the
            // SERVER says is on file for THAT person. The account read carries
            // `pii` on every one of them, so a card whose SSN PMS already
            // holds waives it and a card whose SSN it does not keeps its gate
            // — including a person this portal has only just minted an id for.
            const withWaiver = blocks.map((block) => ({
              ...block,
              fields: withRosterPiiOnFile(block.fields, piiForOwner),
            }));
            const primary = primaryForStep(rosterStep.key, roster, rosterBag);
            const spousesOn = spousesRequired(rule);
            const checklisted =
              order?.indemnity != null && isIndemnitySupported(order.indemnity);
            const missingSplit = missingByCard(missing, roster);
            const unplaced = checklisted
              ? unplacedSignerNotes(
                  order?.indemnity,
                  new Set(ownerPeople(roster).map((p) => p.ownerId)),
                )
              : [];
            return (
              <div className="flex flex-col gap-6">
                <h2 className="text-lg font-semibold text-brk-text">
                  {rosterStep.title}
                </h2>
                <ExtractedReviewBanner
                  count={
                    fieldsForStep(rosterStep).filter((f) =>
                      review.isPending(reviewKey(rosterStep.key, f.name)),
                    ).length
                  }
                />
                {/* The bar reads the ORDER's evaluation and nothing this page
                    worked out. `null` is "this product does not ask for
                    personal indemnity", which renders nothing; an absent key is
                    a PMS that predates the block, and renders nothing too
                    (BRK-S164: no gate, no bar, no notes). */}
                {order?.indemnity && checklisted && (
                  <BondOwnershipProgress indemnity={order.indemnity} />
                )}
                {/* Seats with no card to sit on — somebody `owners[]` declares
                    that the form is not drawing (BRK-S156's checklist seat).
                    Said here because there is nowhere else honest to say it,
                    and NAMED because there is no card around them to say who
                    they are (BRK-S164). */}
                {unplaced.length > 0 && (
                  <ul
                    role="status"
                    className="flex flex-col gap-1 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
                  >
                    {unplaced.map((note) => (
                      <li key={note}>{note}</li>
                    ))}
                  </ul>
                )}
                {order?.indemnity && !isIndemnitySupported(order.indemnity) && (
                  <UnsupportedIndemnityNotice />
                )}
                {/* Only the fields that belong to NO card keep a step-level
                    home; each person's go on their own card (BRK-S181). */}
                <BondMissingChecklist missing={missingSplit.order} />
                <BondIndemnityRoster
                  blocks={withWaiver}
                  roster={roster}
                  values={rosterBag}
                  onChange={(name, v) =>
                    setRosterValue(rosterStep.key, name, v, spousesOn)
                  }
                  invalid={invalid}
                  pendingReview={(name) =>
                    review.isPending(reviewKey(rosterStep.key, name))
                  }
                  filesPort={filesPort}
                  // The progress bar already quotes the minimum share under
                  // it, so the roster's footer says it only when there is no
                  // bar — the same sentence twice is one sentence nobody reads
                  // (BRK-S156/S164).
                  rule={checklisted ? null : rule}
                  indemnity={checklisted ? (order?.indemnity ?? null) : null}
                  primaryOwnerId={primary}
                  onPrimaryChange={(ownerId) =>
                    setPrimaryPick((all) => ({
                      ...all,
                      [rosterStep.key]: ownerId,
                    }))
                  }
                  openOwners={openOwnersFor(rosterStep.key, roster)}
                  onEditOwner={(id) =>
                    setOpenCards((all) => ({
                      ...all,
                      [rosterStep.key]: {
                        ...(all[rosterStep.key] ?? {}),
                        [id]: true,
                      },
                    }))
                  }
                  onSaveOwner={(id) =>
                    void saveAndAdvance(rosterStep, {
                      cardOwnerIds: new Set([id]),
                      cardOwnerId: id,
                      advance: false,
                    })
                  }
                  savingOwnerId={savingOwnerId}
                  cardErrors={cardErrors}
                  cardMissing={missingSplit.byCard}
                  canAddOwner={rosterHasRoom(roster)}
                  onAddOwner={() => addRosterOwner(rosterStep.key)}
                  onRemoveOwner={(id) => removeRosterOwner(rosterStep, id)}
                  removableOwners={ownerPeople(roster).length > 1}
                  spouseAddressDiffers={differs}
                  onSpouseAddressDiffersChange={(spouseId, on) =>
                    setSpouseDiffers((all) => {
                      const current = new Set(
                        all[rosterStep.key] ?? [...differs],
                      );
                      if (on) current.add(spouseId);
                      else current.delete(spouseId);
                      return { ...all, [rosterStep.key]: [...current] };
                    })
                  }
                />
                {/* What a Save & Continue that LANDED but did not settle the
                    list says. Announced rather than left to be noticed: the
                    broker pressed a button that saved, so silence would read
                    as nothing having happened (BRK-S164). */}
                {rosterNotice && (
                  <p
                    role="status"
                    className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
                  >
                    {rosterNotice}
                  </p>
                )}
                {mode === "existing" && (
                  <PushToAccountToggle
                    on={pushToAccount[rosterStep.key] !== false}
                    onChange={(on) =>
                      setPushToAccount((p) => ({ ...p, [rosterStep.key]: on }))
                    }
                  />
                )}
              </div>
            );
          })()}

        {currentChip?.kind === "step" &&
          isFieldsStep(currentChip.step) &&
          rosterKind(currentChip.step) === "none" &&
          (() => {
            const fieldsStep = currentChip.step;
            const stepFields = fieldsForStep(fieldsStep);
            return (
              <FieldsStep
                step={fieldsStep}
                fields={stepFields}
                values={values[fieldsStep.key] ?? {}}
                onChange={(name, v) =>
                  setValue(fieldsStep.key, name, v, stepFields)
                }
                showPushToAccount={mode === "existing"}
                pushToAccount={pushToAccount[fieldsStep.key] !== false}
                onPushToAccountChange={(on) =>
                  setPushToAccount((p) => ({ ...p, [fieldsStep.key]: on }))
                }
                missing={missing}
                invalid={invalid}
                pendingReview={(name) =>
                  review.isPending(reviewKey(fieldsStep.key, name))
                }
                reviewCount={
                  stepFields.filter((f) =>
                    review.isPending(reviewKey(fieldsStep.key, f.name)),
                  ).length
                }
                filesPort={filesPort}
              />
            );
          })()}

        {currentChip?.kind === "step" &&
          isDataPullStep(currentChip.step) &&
          order && (
            <DataPullStep
              // Remounts per step, so each pull owns its own run state.
              key={`${order.id}:${currentChip.step.key}`}
              orderId={order.id}
              step={currentChip.step}
              initialRun={runs[currentChip.step.key]}
              onSettled={noteRun}
              onDone={() => setStep((s) => s + 1)}
            />
          )}

        {/* A step type this build cannot render — named, and never a blank card
            with a live Continue behind it (BRK-S132). */}
        {currentChip?.kind === "step" &&
          stepKind(currentChip.step) === "unsupported" && (
            <UnsupportedStep
              step={currentChip.step}
              onSkip={() => setStep((s) => s + 1)}
            />
          )}

        {/* One `preapproval` configuration step, two chips (BRK-S132). */}
        {currentChip?.kind === "preapproval_checks" && order && (
          <PreapprovalChecksStep
            key={`${order.id}:${currentChip.step.key}:checks`}
            orderId={order.id}
            step={currentChip.step}
            initialRun={runs[currentChip.step.key]}
            onSettled={(run) =>
              setRuns((all) => ({ ...all, [currentChip.step.key]: run }))
            }
            onDone={() => setStep((s) => s + 1)}
          />
        )}

        {currentChip?.kind === "preapproval_indication" && order && (
          <PreapprovalIndicationStep
            key={`${order.id}:${currentChip.step.key}:indication`}
            orderId={order.id}
            accountId={order.accountId}
            seed={preapproval}
            insuredName={insuredName}
            programName={programName}
            // The LAST chip in a standalone flow, which is what turns this card
            // from a screen the broker passes through into the end of the
            // request (BRK-S160). Derived from the spine, not from the
            // configuration: whatever made this the terminal chip, the card's
            // job is the same.
            terminal={currentChip === chips[chips.length - 1]}
            onFiled={() => setStandaloneFiled(true)}
            onPreapproval={setFreshPreapproval}
            onDone={() => setStep((s) => s + 1)}
          />
        )}

        {/* Not granted lands the broker HERE, and the notice explains itself
            beside the step they are now filling in (BRK-S133) — never on a
            screen of its own, which would make a decline out of something that
            is not one. */}
        {/* An order that inherited a granted program from the insured never ran
            the two chips, so the standing figures live HERE — above the step
            they are context for (BRK-S134). */}
        {onFinalChip &&
          order &&
          hasStandingPreapproval(preapproval) &&
          preapproval && (
            <StandingPreapprovalBanner
              orderId={order.id}
              preapproval={preapproval}
              programName={programName}
            />
          )}

        {onFinalChip && preapproval?.status === "not_granted" && (
          <NotGrantedNotice
            insuredName={insuredName}
            programName={programName}
          />
        )}

        {onFinalChip && config && (
          <FinalStep
            fields={config.finalStep.fields}
            values={values[FINAL_STEP_KEY] ?? {}}
            onChange={(name, v) =>
              setValue(
                FINAL_STEP_KEY,
                name,
                v,
                // BRK-S168 — the terms step declares the units, so its own
                // fields have to reach the clearing rule.
                config.finalStep.fields.map(toRenderFinalField),
              )
            }
            missing={missing}
            invalid={invalid}
            pendingReview={(name) =>
              review.isPending(reviewKey(FINAL_STEP_KEY, name))
            }
            reviewCount={
              config.finalStep.fields.filter((f) =>
                review.isPending(reviewKey(FINAL_STEP_KEY, f.key)),
              ).length
            }
            filesPort={filesPort}
          />
        )}

        {inFlight && inFlight.length > 0 && (
          <PreapprovalInFlightPrompt
            orders={inFlight}
            insuredName={accountName}
            busy={busy}
            onContinue={(id) => {
              setInFlight(null);
              navigate(`/surety/submissions/${id}/continue`);
            }}
            onDiscard={() => void discardAndStartOver()}
          />
        )}

        {duplicates && (
          <DuplicateAdvisory
            candidates={duplicates}
            busy={busy}
            onUse={useCandidate}
            onCreateAnyway={() => startOrder(true)}
            onDismiss={() => setDuplicates(null)}
          />
        )}

        {/* Why Continue is disabled, in the same place a rejected save's
            step-level line appears (BRK-S142). The per-field cards above say
            WHICH fields; this says what it means for the broker. */}
        {blockedFields.length > 0 && (
          <p
            role="alert"
            className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
          >
            {BLOCKED_BY_UNSUPPORTED}
          </p>
        )}

        {error && (
          <p role="alert" className="text-sm text-brk-danger">
            {error}
          </p>
        )}

        <div className="mt-2 flex flex-wrap items-center justify-between gap-3 border-t border-brk-border pt-5">
          {/* Once the order exists, eligibility is settled and step 0 is behind
              the broker for good — stepping back onto it would offer to start a
              SECOND order for the same insured. The way out is the list, where
              this draft is now waiting for them. */}
          {standaloneFiled ? (
            // ⚠️ THE REQUEST IS FILED, so there is nothing behind the broker to
            // go back to (BRK-S160). Back would land them on a step whose save
            // PMS refuses on a submitted order — an inline failure with no way
            // forward, which is the trap this flow's own card exists to avoid.
            // A spacer rather than nothing, so the ending's actions stay where
            // the eye left them.
            <span aria-hidden="true" />
          ) : step === 0 || step === 1 ? (
            <Link
              to={order ? "/surety/submissions" : "/surety/construction"}
              className="rounded-md border border-brk-border px-4 py-2 text-sm font-medium text-brk-text transition-colors hover:bg-brk-bg"
            >
              {order ? "Finish later" : "Cancel"}
            </Link>
          ) : (
            <button
              type="button"
              onClick={() => setStep((s) => backChipIndex(chips, s))}
              className="rounded-md border border-brk-border px-4 py-2 text-sm font-medium text-brk-text transition-colors hover:bg-brk-bg"
            >
              ← Back
            </button>
          )}

          {step === 0 ? (
            <button
              type="button"
              onClick={() => startOrder()}
              disabled={!step1Ready || busy}
              className="rounded-md bg-brk-accent px-5 py-2 font-medium text-white transition-colors hover:bg-brk-accent-strong disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? "Starting…" : "Continue"}
            </button>
          ) : onFinalChip ? (
            <button
              type="button"
              onClick={submitOrder}
              // Disabling while in flight is the first guard against a double
              // submit; the 409 handler is the second, for a click that landed.
              // A field with no control blocks it outright (BRK-S142).
              disabled={busy || blockedFields.length > 0}
              className="rounded-full bg-brk-accent px-6 py-3 font-medium text-white transition-colors hover:bg-brk-accent-strong disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? "Submitting…" : "Submit bond request"}
            </button>
          ) : chipDrivesItself(currentChip) ? (
            // These cards drive themselves forward from the inside — a data
            // pull, the preapproval Checks bar (which auto-advances on settle),
            // the Indication view, and the guarded unsupported-step card. The
            // footer must NOT offer a Continue beside them: the blank-card bug
            // was exactly a live footer button in front of nothing (BRK-S132).
            <span aria-hidden="true" />
          ) : rosterStepKey !== null ? (
            // ⚠️ ONE BUTTON (BRK-S164), where BRK-S156 had two.
            //
            // The pair was "Save and check" beside a Continue that was dead
            // until the server said otherwise — two controls for what the
            // broker experiences as one intention, and the live one was the
            // one that said it would not move them. It is one action now, and
            // it is honest about being both halves: the save refreshes the
            // evaluation, and the evaluation decides whether the step ends.
            //
            // It is ENABLED whenever there is a roster at all. A pre-click
            // reading of the gate is always an answer about the PREVIOUS
            // roster, so disabling on it withholds the very save that would
            // change the answer.
            <button
              type="button"
              onClick={() => {
                if (currentChip?.kind !== "step") return;
                const step = currentChip.step;
                void saveAndAdvance(step, {
                  // Only the OPEN cards are checked: a collapsed one was saved
                  // and accepted, so re-checking it would block the step on a
                  // person the broker cannot see and did not touch.
                  cardOwnerIds: openOwnersFor(step.key, rowsForStep(step.key)),
                });
              }}
              disabled={
                busy ||
                blockedFields.length > 0 ||
                ownerPeople(rowsForStep(rosterStepKey)).length === 0
              }
              className="rounded-md bg-brk-accent px-5 py-2 font-medium text-white transition-colors hover:bg-brk-accent-strong disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? "Saving…" : ROSTER_CONTINUE_LABEL}
            </button>
          ) : currentChip?.kind === "summary" ? (
            // The SUMMARY chip's own Continue (BRK-S162). Disabled until the
            // question is answered, because the answer is what decides which
            // steps come next: a Continue that moved the broker on with no
            // declaration would file an order whose spine was chosen for them.
            <button
              type="button"
              onClick={() => void confirmRepeatDeclaration()}
              disabled={busy || repeatChoice === null}
              className="rounded-md bg-brk-accent px-5 py-2 font-medium text-white transition-colors hover:bg-brk-accent-strong disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? "Saving…" : "Continue"}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                if (currentChip?.kind === "step") {
                  saveAndAdvance(currentChip.step);
                }
              }}
              disabled={busy || blockedFields.length > 0}
              className="rounded-md bg-brk-accent px-5 py-2 font-medium text-white transition-colors hover:bg-brk-accent-strong disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? "Saving…" : "Continue"}
            </button>
          )}
        </div>

        {step === 0 && (
          <button
            type="button"
            onClick={() => navigate("/surety/construction")}
            className="self-start text-sm text-brk-accent hover:underline"
          >
            ← Back to Construction Bond
          </button>
        )}
      </div>
    </section>
  );
}

// ─── Step indicator ──────────────────────────────────────────────────────────

function StepIndicator({
  titles,
  current,
}: {
  titles: string[];
  current: number;
}) {
  return (
    <ol aria-label="Submission steps" className="flex items-center gap-2">
      {titles.map((label, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <li key={label} className="flex flex-1 items-center gap-2">
            <span
              aria-current={active ? "step" : undefined}
              className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                active
                  ? "bg-brk-accent text-white"
                  : done
                    ? "bg-brk-accent/15 text-brk-accent"
                    : "bg-brk-bg text-brk-dim ring-1 ring-brk-border"
              }`}
            >
              {done ? "✓" : i + 1}
            </span>
            <span
              className={`hidden truncate text-xs sm:block ${
                active ? "font-semibold text-brk-text" : "text-brk-dim"
              }`}
            >
              {label}
            </span>
            {i < titles.length - 1 && (
              <span className="h-px flex-1 bg-brk-border" aria-hidden="true" />
            )}
          </li>
        );
      })}
    </ol>
  );
}

// ─── Step 1 — eligibility (code-owned) ───────────────────────────────────────

function StepEligibility({
  products,
  catalogError,
  subproductId,
  onSubproductChange,
  config,
  configLoading,
  configError,
  mode,
  onModeChange,
  newLegalName,
  onNewLegalName,
  newState,
  onNewState,
  accountId,
  accountName,
  lockedSubproduct,
  onUnlockSubproduct,
  lockedAccount,
  onUnlockAccount,
  onPickAccount,
  eligibilityConfirmed,
  onEligibilityChange,
  eligibilityRefusal,
  legalNamePending,
  onLegalNameTouched,
  documentPanel,
}: {
  products: BondProduct[] | null;
  catalogError: string | null;
  subproductId: string;
  onSubproductChange: (id: string) => void;
  config: BondConfig | null;
  configLoading: boolean;
  configError: string | null;
  mode: Mode;
  onModeChange: (m: Mode) => void;
  newLegalName: string;
  onNewLegalName: (v: string) => void;
  newState: string;
  onNewState: (v: string) => void;
  accountId: string;
  accountName: string;
  /** The URL supplied the subproduct and the broker has not asked to change
   *  it (BRK-S181). Read-only only once the catalog NAMES it — an id the
   *  catalog does not carry falls back to the select rather than printing a
   *  blank. */
  lockedSubproduct: boolean;
  onUnlockSubproduct: () => void;
  /** The URL-supplied insured, read-only, or null for the live picker. */
  lockedAccount: { name: string; number: string | null } | null;
  onUnlockAccount: () => void;
  onPickAccount: (a: AccountSummary) => void;
  /** WHICH statements are ticked, by index in the configuration's list. */
  eligibilityConfirmed: ReadonlySet<number>;
  onEligibilityChange: (index: number, confirmed: boolean) => void;
  /** The one statement the server refused, anchored to it (BRK-S170). */
  eligibilityRefusal: { index: number; reason: string } | null;
  /** Whether the legal name came from an uploaded document and still awaits the
   *  broker's confirmation (BRK-S120). */
  legalNamePending: boolean;
  onLegalNameTouched: () => void;
  /** The document path, composed by the page (which owns the seam + the prefill)
   *  so this step stays presentational. */
  documentPanel: ReactNode;
}) {
  const subproductOptions = (products ?? []).flatMap((p) =>
    p.subproducts.map((s) => ({
      value: s.id,
      label: products && products.length > 1 ? `${p.name} — ${s.name}` : s.name,
    })),
  );
  const lockedSubproductName = lockedSubproduct
    ? subproductOptions.find((o) => o.value === subproductId)?.label
    : undefined;
  return (
    <div className="flex flex-col gap-6">
      {catalogError ? (
        <p role="alert" className="text-sm text-brk-danger">
          {catalogError}
        </p>
      ) : !products ? (
        <p role="status" className="text-sm text-brk-dim">
          Loading bond products…
        </p>
      ) : products.length === 0 ? (
        <p className="text-sm text-brk-dim">
          No bond products are available to your brokerage yet.
        </p>
      ) : lockedSubproductName !== undefined ? (
        <SuppliedChoice
          label="Bond type"
          value={lockedSubproductName}
          onChange={onUnlockSubproduct}
        />
      ) : (
        <SelectField
          label="Bond type"
          placeholder="Select a bond type"
          options={subproductOptions}
          value={subproductId}
          onChange={(e) => onSubproductChange(e.target.value)}
          required
        />
      )}

      {lockedAccount ? (
        <SuppliedChoice
          label="Insured"
          value={lockedAccount.name || "Loading the insured…"}
          detail={lockedAccount.number}
          onChange={onUnlockAccount}
        />
      ) : (
        <>
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-sm font-medium text-brk-text">
              New account or existing?
            </legend>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <ModeCard
                selected={mode === "new"}
                onSelect={() => onModeChange("new")}
                title="New account"
                subtitle="A new principal / insured."
              />
              <ModeCard
                selected={mode === "existing"}
                onSelect={() => onModeChange("existing")}
                title="Existing account"
                subtitle="An account your brokerage already owns."
              />
            </div>
          </fieldset>

          {mode === "existing" ? (
            <AccountSearch
              selectedId={accountId}
              selectedName={accountName}
              onPick={onPickAccount}
            />
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1">
                <TextField
                  label="Insured legal name"
                  placeholder="Acme Construction LLC"
                  value={newLegalName}
                  onChange={(e) => {
                    onLegalNameTouched();
                    onNewLegalName(e.target.value);
                  }}
                  required
                />
                {legalNamePending && <ExtractedMarker />}
              </div>
              <SelectField
                label="State"
                placeholder="Select"
                options={US_STATE_OPTIONS}
                value={newState}
                onChange={(e) => onNewState(e.target.value)}
                required
              />
            </div>
          )}
        </>
      )}

      {documentPanel}

      {configError ? (
        <p role="alert" className="text-sm text-brk-danger">
          {configError}
        </p>
      ) : configLoading ? (
        <p role="status" className="text-sm text-brk-dim">
          Loading the eligibility requirements…
        </p>
      ) : config ? (
        <EligibilityNotice
          items={config.eligibility}
          confirmed={eligibilityConfirmed}
          onChange={onEligibilityChange}
          refusal={eligibilityRefusal}
        />
      ) : (
        <p className="text-sm text-brk-dim">
          Choose a bond type to see its eligibility requirements.
        </p>
      )}
    </div>
  );
}

/**
 * A step-1 value the URL already decided, shown as a STRING (BRK-S181).
 *
 * Named as a group by its label, so it reads as "Bond type: Program
 * Pre-approval" rather than as a stray line of text. The "Change" is the escape
 * hatch — it reveals the real control, still preselected — and is named for
 * what it changes, since two identical "Change" buttons on one screen read as
 * one.
 */
function SuppliedChoice({
  label,
  value,
  detail,
  onChange,
}: {
  label: string;
  value: string;
  detail?: string | null;
  onChange: () => void;
}) {
  const labelId = useId();
  return (
    <div
      role="group"
      aria-labelledby={labelId}
      className="flex flex-col gap-1.5"
    >
      <span id={labelId} className="text-sm font-medium text-brk-text">
        {label}
      </span>
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-md border border-brk-border bg-brk-bg px-3 py-2">
        <span className="flex min-w-0 flex-col">
          <span className="break-words text-brk-text">{value}</span>
          {detail && (
            <span className="font-mono text-xs text-brk-dim">{detail}</span>
          )}
        </span>
        <button
          type="button"
          onClick={onChange}
          aria-label={`Change ${label.toLowerCase()}`}
          className="shrink-0 text-sm font-medium text-brk-accent hover:underline"
        >
          Change
        </button>
      </div>
    </div>
  );
}

/**
 * The attestations the broker confirms — ONE REQUIRED CHECKBOX PER STATEMENT
 * (BRK-S170).
 *
 * The statements come from the resolved config and are echoed VERBATIM: they
 * are no longer a list hardcoded in this page, so a program change is a config
 * change, and re-wording a statement somebody attested to is the one thing this
 * must never do. An empty list means this subproduct asks for none.
 *
 * It used to be a numbered list under one "I confirm all of the above" box.
 * That box confirmed nothing in particular and was never sent anywhere, so what
 * a broker had agreed to existed only in this browser. Per-item boxes are the
 * half that needs no server, so they ship regardless of whether the create
 * carries the block yet.
 *
 * Addressed by INDEX, because a bare `string[]` gives an item no other identity
 * — and because that is how the server names the one it refuses. See
 * `lib/bondEligibility.ts`.
 */
function EligibilityNotice({
  items,
  confirmed,
  onChange,
  refusal,
}: {
  items: string[];
  confirmed: ReadonlySet<number>;
  onChange: (index: number, confirmed: boolean) => void;
  refusal: { index: number; reason: string } | null;
}) {
  if (items.length === 0) return null;
  return (
    // A FIELDSET now (BRK-S170): the statements are one group of related
    // required checkboxes rather than a heading over prose, and the legend is
    // what names the group to a screen reader — and to a test that wants "every
    // eligibility box" without knowing the configuration's sentences.
    //
    // The CARD's border is on this wrapper, not on the fieldset (BRK-S181). A
    // legend is drawn ON its fieldset's top border, so while the fieldset was
    // the card, the legend's lines painted over the border — the instruction
    // visibly broke it. A borderless fieldset has no line for a legend to sit
    // on, at any width, however the legend wraps.
    <div className="rounded-xl border border-brk-border bg-brk-panel p-5">
      <fieldset
        className="flex flex-col gap-4"
        aria-describedby="eligibility-instruction"
      >
        {/* A DIRECT child of the fieldset, and it has to stay one: a <legend>
            nested inside a wrapper does not name its group, so the whole card
            stops being findable as "Eligibility confirmation" — to a screen
            reader as much as to a test. The instruction is the group's
            DESCRIPTION, tied by aria-describedby, rather than part of its
            name. */}
        <legend className="mb-1 text-base font-semibold text-brk-text">
          Eligibility confirmation
        </legend>
        <p id="eligibility-instruction" className="text-sm text-brk-dim">
          Confirm each of these on behalf of the principal. All are required.
        </p>
        <ul className="flex flex-col gap-3">
          {items.map((item, index) => (
            <EligibilityItem
              // The INDEX is the identity here, not the text: two configurations
              // could legitimately repeat a statement, and a duplicated key would
              // tie two boxes together.
              key={index}
              index={index}
              text={item}
              confirmed={confirmed.has(index)}
              onChange={(on) => onChange(index, on)}
              error={refusal?.index === index ? refusal.reason : null}
            />
          ))}
        </ul>
      </fieldset>
    </div>
  );
}

function EligibilityItem({
  index,
  text,
  confirmed,
  onChange,
  error,
}: {
  index: number;
  text: string;
  confirmed: boolean;
  onChange: (confirmed: boolean) => void;
  error: string | null;
}) {
  const errorId = `eligibility-${index}-error`;
  return (
    <li className="flex flex-col gap-1">
      <label
        className={`flex items-start gap-3 rounded-lg border bg-brk-bg p-4 ${
          error ? "border-brk-danger" : "border-brk-border"
        }`}
      >
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 shrink-0"
          checked={confirmed}
          onChange={(e) => onChange(e.target.checked)}
          required
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
        />
        {/* The configuration's own words. Never reformatted, never truncated —
            a broker is agreeing to this sentence. */}
        <span className="min-w-0 text-sm text-brk-text">{text}</span>
      </label>
      {error && (
        <p id={errorId} role="alert" className="text-sm text-brk-danger">
          {error}
        </p>
      )}
    </li>
  );
}

function ModeCard({
  selected,
  onSelect,
  title,
  subtitle,
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  subtitle: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={`flex items-center gap-3 rounded-xl border p-4 text-left transition-colors ${
        selected
          ? "border-brk-accent bg-brk-accent/5 ring-1 ring-brk-accent"
          : "border-brk-border bg-brk-panel hover:border-brk-accent/60"
      }`}
    >
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="font-semibold text-brk-text">{title}</span>
        <span className="text-sm text-brk-dim">{subtitle}</span>
      </span>
      <span aria-hidden="true" className="shrink-0 text-lg text-brk-dim">
        ›
      </span>
    </button>
  );
}

/** The real own-scoped account search (BRK-S079 replaced the sample list). */
function AccountSearch({
  selectedId,
  selectedName,
  onPick,
}: {
  selectedId: string;
  selectedName: string;
  onPick: (a: AccountSummary) => void;
}) {
  const { authedRequest } = useAuth();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<AccountSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const page = await listAccounts(authedRequest, {
          ...(query.trim() ? { search: query.trim() } : {}),
          limit: 10,
        });
        if (!cancelled) {
          setResults(page.items);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError(messageFor(err));
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [authedRequest, query]);

  return (
    <div className="flex flex-col gap-3">
      <label className="flex w-full flex-col gap-1.5">
        <span className="text-sm font-medium text-brk-text">
          Find the account
        </span>
        <input
          type="search"
          placeholder="Search accounts by name"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full rounded-md border border-brk-border bg-brk-panel px-3 py-2 text-brk-text outline-none focus:border-brk-accent focus:ring-2 focus:ring-brk-accent/30"
        />
      </label>

      {error && (
        <p role="alert" className="text-sm text-brk-danger">
          {error}
        </p>
      )}

      {!results && !error && (
        <p role="status" className="text-sm text-brk-dim">
          Loading accounts…
        </p>
      )}

      <ul className="flex flex-col gap-2">
        {results?.map((a) => {
          const selected = selectedId === a.id;
          return (
            <li key={a.id}>
              <button
                type="button"
                aria-pressed={selected}
                onClick={() => onPick(a)}
                className={`flex w-full items-center justify-between gap-3 rounded-lg border p-4 text-left transition-colors ${
                  selected
                    ? "border-brk-accent bg-brk-accent/5 ring-1 ring-brk-accent"
                    : "border-brk-border bg-brk-panel hover:border-brk-accent"
                }`}
              >
                <span className="flex min-w-0 flex-col">
                  <span className="truncate font-medium text-brk-text">
                    {a.legalName}
                  </span>
                  <span className="truncate text-sm text-brk-dim">
                    {a.accountNumber}
                  </span>
                </span>
                {selected && (
                  <span aria-hidden="true" className="shrink-0 text-brk-accent">
                    ✓
                  </span>
                )}
              </button>
            </li>
          );
        })}
        {results?.length === 0 && (
          <li className="text-sm text-brk-dim">
            {query.trim()
              ? `No accounts match “${query.trim()}”.`
              : "Your brokerage has no accounts yet."}
          </li>
        )}
      </ul>

      {selectedId && selectedName && (
        <p className="text-sm text-brk-dim">
          Filing against{" "}
          <span className="font-medium text-brk-text">{selectedName}</span>.
        </p>
      )}
    </div>
  );
}

/**
 * "This insured already has a pre-approval in progress" — Continue it, or
 * Discard it and start over (BRK-S181).
 *
 * A region rather than a modal, like the duplicate advisory beside it: the
 * broker's step 1 stays on screen and nothing is lost by reading this. It
 * names the order by its SB number and start date, because "a pre-approval"
 * alone gives the broker nothing to recognise. Discarding CANCELS the order
 * (it is kept, marked cancelled) — the sentence says so, because "discard" on
 * its own reads as a delete. No decision, score or reason is shown: the row
 * carries none, and this is a broker surface.
 */
function PreapprovalInFlightPrompt({
  orders,
  insuredName,
  busy,
  onContinue,
  onDiscard,
}: {
  orders: readonly BondSubmissionListItem[];
  insuredName: string;
  busy: boolean;
  onContinue: (id: string) => void;
  onDiscard: () => void;
}) {
  // The newest is the one to continue — the list is newest-first, and a second
  // in-flight order can only predate this guard.
  const latest = orders[0];
  const headingId = useId();
  if (!latest) return null;
  return (
    <section
      aria-labelledby={headingId}
      className="flex flex-col gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-900 sm:p-5"
    >
      <h2 id={headingId} className="font-semibold">
        A pre-approval already in progress
      </h2>
      <p className="text-sm">
        {insuredName || "This insured"} already has a pre-approval in progress —{" "}
        <span className="font-mono">{latest.number}</span>, started{" "}
        {formatBondDate(latest.createdAt)}
        {orders.length > 1 ? ` (and ${orders.length - 1} more)` : ""}. Continue
        it, or discard it and start over. Discarding cancels
        {orders.length > 1 ? " those requests" : " that request"}; it’s kept,
        marked cancelled.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => onContinue(latest.id)}
          disabled={busy}
          className="rounded-md bg-brk-accent px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brk-accent-strong disabled:opacity-60"
        >
          Continue {latest.number}
        </button>
        <button
          type="button"
          onClick={onDiscard}
          disabled={busy}
          className="rounded-md border border-amber-400 bg-white px-4 py-2 text-sm font-medium text-amber-900 transition-colors hover:bg-amber-100 disabled:opacity-60"
        >
          {busy ? "Working…" : "Discard and start over"}
        </button>
      </div>
    </section>
  );
}

/** The advisory duplicate-match panel. Brokerage-scoped and never a block: the
 *  broker adopts a candidate or creates the insured anyway. */
function DuplicateAdvisory({
  candidates,
  busy,
  onUse,
  onCreateAnyway,
  onDismiss,
}: {
  candidates: BondDuplicateCandidate[];
  busy: boolean;
  onUse: (c: BondDuplicateCandidate) => void;
  onCreateAnyway: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className="flex flex-col gap-4 rounded-xl border border-amber-300 bg-amber-50 p-5">
      <div className="flex flex-col gap-1">
        <h2 className="text-base font-semibold text-amber-900">
          Your brokerage may already have this insured
        </h2>
        <p className="text-sm text-amber-800">
          These look like the same business. Use one of them, or carry on
          creating a new account — it&apos;s your call.
        </p>
      </div>
      <ul className="flex flex-col gap-2">
        {candidates.map((c) => (
          <li
            key={c.accountId}
            className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-white p-4"
          >
            <span className="flex min-w-0 flex-col">
              <span className="truncate font-medium text-brk-text">
                {c.legalName}
              </span>
              <span className="truncate text-sm text-brk-dim">
                {/* No "why we matched" line: the duplicate advisory is a CLOSED
                    response carrying exactly these five fields, so a `reason`
                    could never arrive (BRK-S097). */}
                {[c.accountNumber, c.city, c.state].filter(Boolean).join(" · ")}
              </span>
            </span>
            <button
              type="button"
              onClick={() => onUse(c)}
              className="shrink-0 rounded-md border border-brk-accent px-3 py-1.5 text-sm font-medium text-brk-accent transition-colors hover:bg-brk-accent/5"
            >
              Use this account
            </button>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onCreateAnyway}
          disabled={busy}
          className="rounded-md bg-brk-accent px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brk-accent-strong disabled:opacity-60"
        >
          {busy ? "Creating…" : "Create new anyway"}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="text-sm text-brk-dim hover:underline"
        >
          Back to the form
        </button>
      </div>
    </div>
  );
}

// ─── Config-driven fields step ───────────────────────────────────────────────

/**
 * Whether a field takes the whole row rather than one grid column — asked once
 * for both the fields steps and the final step, because a rule stated twice is
 * a rule that eventually differs.
 *
 * Reads `fieldKind`, not `type`: the guarded card an UNSUPPORTED field renders is
 * a paragraph, and squeezing an alert into half a row is how it stops being read.
 */
function fullRow(field: BondRenderField): boolean {
  const kind = fieldKind(field);
  return (
    kind === "address" ||
    kind === "textarea" ||
    kind === "unsupported" ||
    // A drop zone squeezed into half a row is a drop zone nobody hits on a
    // phone, and its attached list wants the width (BRK-S145).
    kind === "files"
  );
}

function FieldsStep({
  step,
  fields,
  values,
  onChange,
  showPushToAccount,
  pushToAccount,
  onPushToAccountChange,
  missing,
  invalid,
  pendingReview,
  reviewCount,
  filesPort,
}: {
  step: Extract<BondStep, { type: "fields" }>;
  /** Already adapted AND on-file-waived by the page (BRK-S105) — this component
   *  must not re-derive them, or the waiver would apply to the controls and not
   *  to the gate that guards them. */
  fields: BondRenderField[];
  values: BondValueBag;
  onChange: (name: string, value: BondFieldValue | undefined) => void;
  showPushToAccount: boolean;
  pushToAccount: boolean;
  onPushToAccountChange: (on: boolean) => void;
  missing: BondRenderField[];
  /** Field name → why its value isn't an answer (BRK-S091). */
  invalid: Record<string, string>;
  /** Whether this field was filled from an uploaded document and still awaits
   *  the broker's confirmation (BRK-S120). */
  pendingReview: (name: string) => boolean;
  /** How many of this step's fields are still awaiting it — the banner's count. */
  reviewCount: number;
  /** What a `files` field needs (BRK-S145); undefined before an order exists. */
  filesPort?: BondFilesPort;
}) {
  // BRK-S172 — the fields that always render, and the conditional sections.
  // Note this reads the DECLARED list, not the visible one: a section has to
  // know its own fields in order to draw them when it opens, and the page's
  // gate does its own narrowing (`visibleBondFields`).
  const { fields: plain, sections } = resolveBondFieldSections(fields, values);
  return (
    <div className="flex flex-col gap-6">
      <h2 className="text-lg font-semibold text-brk-text">{step.title}</h2>

      <ExtractedReviewBanner count={reviewCount} />
      <BondMissingChecklist missing={missing} />

      <FieldGrid
        fields={plain}
        values={values}
        onChange={onChange}
        invalid={invalid}
        pendingReview={pendingReview}
        filesPort={filesPort}
      />

      {/* BRK-S172 — a conditional block, drawn only when its controller is
          ticked. LAST, whatever order the configuration listed its fields in: a
          block that appeared in the middle would push everything below it down
          each time it opened. */}
      {sections.map(
        (section) =>
          section.open && (
            <section
              key={section.rule.id}
              className="flex flex-col gap-4 rounded-xl border border-brk-border bg-brk-panel p-5"
            >
              <div className="flex flex-col gap-1">
                <h3 className="text-base font-semibold text-brk-text">
                  {section.rule.title}
                </h3>
                {section.rule.help && (
                  <p className="text-sm text-brk-dim">{section.rule.help}</p>
                )}
              </div>
              <FieldGrid
                fields={section.fields}
                values={values}
                onChange={onChange}
                invalid={invalid}
                pendingReview={pendingReview}
                filesPort={filesPort}
              />
            </section>
          ),
      )}

      {showPushToAccount && (
        <PushToAccountToggle
          on={pushToAccount}
          onChange={onPushToAccountChange}
        />
      )}
    </div>
  );
}

/**
 * The two-column field grid, shared by a step's own fields and by any
 * conditional section it draws (BRK-S172) — extracted rather than copied, so a
 * field inside the Parent entity block is laid out by exactly the rules a field
 * outside it is.
 */
function FieldGrid({
  fields,
  values,
  onChange,
  invalid,
  pendingReview,
  filesPort,
}: {
  fields: BondRenderField[];
  values: BondValueBag;
  onChange: (name: string, value: BondFieldValue | undefined) => void;
  invalid: Record<string, string>;
  pendingReview: (name: string) => boolean;
  filesPort?: BondFilesPort;
}) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {fields.map((f) => {
        // A checkbox that governs a section gets the rule's helper sentence
        // when the configuration supplies none of its own — the configuration
        // owns its wording, and this stands in for its silence.
        const rule = sectionRuleFor(f);
        const field =
          rule?.controllerHelp && !f.help
            ? { ...f, help: rule.controllerHelp }
            : f;
        return (
          <div
            key={f.name}
            // An address or a long answer wants the full row, and so does the
            // guarded card an unsupported field renders — it is a paragraph, not
            // a control. A scalar sits in the two-column grid from `sm:` up.
            className={fullRow(field) ? "sm:col-span-2" : undefined}
          >
            <BondFieldRenderer
              field={field}
              value={values[f.name]}
              onChange={(v) => onChange(f.name, v)}
              error={invalid[f.name]}
              pendingReview={pendingReview(f.name)}
              files={filesPort}
            />
          </div>
        );
      })}
    </div>
  );
}

/**
 * The signer list carries a status or a basis this build has never heard of
 * (BRK-S152).
 *
 * The guarded state, and the reason it is a NAMED card rather than a dropped
 * row: the checklist could render the seats it understands and silently omit
 * the one it does not, which reads as a shorter list rather than as a portal a
 * version behind. The roster itself stays usable — the broker can still type
 * and still save — because the missing half is the CHECKLIST, not the form.
 */
export const UNSUPPORTED_INDEMNITY_COPY =
  "We can’t show the full list of who has to sign in this version of the portal. Reload the page to pick up the latest version — you can carry on entering owners either way, and we’ll confirm the list with you.";

function UnsupportedIndemnityNotice() {
  return (
    <p
      role="alert"
      className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
    >
      {UNSUPPORTED_INDEMNITY_COPY}
    </p>
  );
}

// ─── Final step — project & bond terms ───────────────────────────────────────

// BRK-S167 — THE THREE COMPUTED READ-ONLY DISPLAYS ARE GONE (Aaron 2026-09-21,
// from the surety scoping document's "no longer collected" list). A code-owned
// `ComputedField` printed an estimated bid bond penalty, a contract duration in
// months and an estimated maintenance amount, each derived from two configured
// fields. Do not reinstate one: the arithmetic was BRK's, keyed to field names
// the configuration happens to declare, and two of its inputs
// (`estimatedBondAmount`, `contractStartDate`) are being struck from the
// configuration itself. A figure the broker is shown is a figure Signal is
// telling them — it belongs upstream, in the configuration or in the quote, not
// in a static SPA multiplying two boxes together.

function FinalStep({
  fields,
  values,
  onChange,
  missing,
  invalid,
  pendingReview,
  reviewCount,
  filesPort,
}: {
  fields: BondFinalField[];
  values: BondValueBag;
  onChange: (name: string, value: BondFieldValue | undefined) => void;
  missing: BondRenderField[];
  /** Field name → why its value isn't an answer (BRK-S091). */
  invalid: Record<string, string>;
  /** Whether this field came from an uploaded document and still awaits the
   *  broker's confirmation (BRK-S120). The terms are where most of a bond
   *  request's values land, so this is the step the marker matters most on. */
  pendingReview: (name: string) => boolean;
  reviewCount: number;
  /** What a `files` field needs (BRK-S145). The terms step is where the
   *  configuration puts them today. */
  filesPort?: BondFilesPort;
}) {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold text-brk-text">
          Project &amp; bond information
        </h2>
        <p className="text-sm text-brk-dim">
          The obligee, project, and bond terms for this submission.
        </p>
      </div>

      <ExtractedReviewBanner count={reviewCount} />
      <BondMissingChecklist missing={missing} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* BRK-S169 — a DECLARED pair collapses into one control with a unit
            toggle; everything else renders exactly as it did. The table is
            consulted here and in `BondPairedField`, never inside the generic
            renderer, which may not know a field's name. */}
        {resolveBondPairs(
          // BRK-S168 — a unit answered "not applicable" takes its length and
          // its figure off the step. Narrowed BEFORE the pairing, so a
          // suppressed member can never form half a composite.
          visibleBondFields(fields.map(toRenderFinalField), values),
        ).map((unit) => {
          if (unit.kind === "pair") {
            return (
              <div key={unit.id}>
                <BondPairedField
                  unit={unit}
                  values={values}
                  onChange={onChange}
                  invalid={invalid}
                  pendingReview={pendingReview}
                />
              </div>
            );
          }
          const rf = unit.field;
          return (
            <div
              key={unit.id}
              className={fullRow(rf) ? "sm:col-span-2" : undefined}
            >
              <BondFieldRenderer
                field={rf}
                value={values[rf.name]}
                onChange={(v) => onChange(rf.name, v)}
                error={invalid[rf.name]}
                pendingReview={pendingReview(rf.name)}
                files={filesPort}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Data-pull steps (a live Lendflow orchestration run) ─────────────────────

function StatusIcon({
  state,
  status,
}: {
  state: BondRunCheckState;
  status: BondRunCheck["status"];
}) {
  // THREE icons, not two (PMS-S187). A spinner says work is happening, so a row
  // nothing has been asked about must not have one: an unsent row gets a
  // stationary clock, and the difference between a spinning row and a still one
  // is what the 2026-08-20 stall had no way to show.
  const settled = state === "verdict";
  const passed = status === "pass";
  return (
    <span
      aria-hidden="true"
      className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
        !settled
          ? "bg-brk-bg text-brk-dim"
          : passed
            ? "bg-green-100 text-green-700"
            : "bg-amber-100 text-amber-700"
      }`}
    >
      {state === "not_sent" ? (
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="9" opacity="0.45" />
          <path d="M12 8v4l3 2" />
        </svg>
      ) : !settled ? (
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          className="animate-spin"
        >
          <circle
            cx="12"
            cy="12"
            r="9"
            stroke="currentColor"
            strokeWidth="2.5"
            opacity="0.2"
          />
          <path
            d="M21 12a9 9 0 0 0-9-9"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        </svg>
      ) : passed ? (
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m5 12 5 5 9-11" />
        </svg>
      ) : (
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 8v5m0 3h.01" />
          <circle cx="12" cy="12" r="9" />
        </svg>
      )}
    </span>
  );
}

/** A check's verdict word. Anything non-pass reads as REVIEW rather than FAILED:
 *  it refers the order to an underwriter, which is not the broker failing. */
/**
 * The row's label — THREE answers, not two (PMS-S187).
 *
 * BRK-S112 made `queued` and `pending` read the SAME, on the reasoning that the
 * difference between "acknowledged" and "in flight at the bureau" is ours to
 * manage rather than something a broker watching a spinner can act on. Observed
 * live on 2026-08-20, that reasoning was wrong in the case that mattered: a
 * stalled step left three pulls unsent and this function said PENDING about all
 * six rows, including the two that had already passed and the three nobody had
 * asked anything.
 *
 * Nothing here reads as a verdict that is not one. "WAITING TO START" and "IN
 * PROGRESS" are both plainly non-answers, which is the property the queued row
 * must keep.
 */
function verdictLabel(
  state: BondRunCheckState,
  status: BondRunCheck["status"],
): string {
  if (state === "not_sent") return "WAITING TO START";
  if (state === "awaiting_vendor") return "IN PROGRESS";
  return status === "pass" ? "PASSED" : "REVIEW";
}

/**
 * The row's supporting line for a row that has NOT answered — what is actually
 * happening, in a broker's terms.
 *
 * Shown beside `detail` rather than instead of it: one says what the check is,
 * this says what is becoming of it. Null for a settled row, whose supporting
 * line is its verdict and, where there is one, its `error`.
 */
function progressNote(state: BondRunCheckState): string | null {
  // Neither line repeats the row's TITLE, which is on the line above it — the
  // same rule PMS-S226 fixed on `title`/`detail`. The step's own line names the
  // check, because there the name is the information; here it is noise.
  if (state === "not_sent") return "Queued behind the other checks.";
  if (state === "awaiting_vendor") return "Sent — waiting for a response.";
  return null;
}

/**
 * One data-pull check as a row: the service, what it looked at, its VERDICT, and
 * the amber underwriting-logic chip where the server supplies a rule.
 *
 * Shared by the live runner and the filed order's record (BRK-S106) — a broker
 * re-opening a filing sees the same rows they watched settle, because they are
 * literally the same component rather than a second rendering that drifts.
 */
function CheckRow({ check: c }: { check: BondRunCheck }) {
  // The row's state, read from the row (PMS-S187) — never from the step's fold,
  // which is a summary of every row and cannot say anything about this one.
  const state = checkState(c);
  const note = progressNote(state);
  return (
    <div
      // An unsent row is DIMMER than a sent one: it is the row where least is
      // happening, and a panel where every non-verdict row looked identical is
      // the defect this story exists for. No amber and no green — absence is not
      // a verdict, so it must not borrow a verdict's colour.
      className={`flex flex-wrap items-center gap-4 rounded-xl border p-4 transition-colors ${
        state === "not_sent"
          ? "border-dashed border-brk-border bg-brk-bg"
          : state === "awaiting_vendor"
            ? "border-brk-border bg-brk-panel"
            : c.status === "pass"
              ? "border-green-200 bg-green-50"
              : "border-amber-300 bg-amber-50"
      }`}
    >
      <StatusIcon state={state} status={c.status} />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="font-semibold text-brk-text">{c.title}</span>
        {c.detail && <span className="text-sm text-brk-dim">{c.detail}</span>}
        {note && <span className="text-sm text-brk-dim">{note}</span>}
        {/* WHY it did not come back, in the server's own words (BRK-S112). A
            row that went amber with nothing but a verdict on it is a support
            call, and this is the only place the reason is published. Shown
            beside `detail` rather than instead of it: one says what the check
            was, the other says what became of it. */}
        {c.error && <span className="text-sm text-amber-800">{c.error}</span>}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1.5">
        <span
          className={`text-sm font-semibold ${
            state !== "verdict"
              ? "text-brk-dim"
              : c.status === "pass"
                ? "text-green-700"
                : "text-amber-800"
          }`}
        >
          {verdictLabel(state, c.status)}
        </span>
        {/* Retained from the mockup: the underwriting rule behind the check. */}
        {c.logic && (
          <span className="max-w-[15rem] rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-right text-xs leading-snug text-amber-800">
            <span className="mb-0.5 block text-[10px] font-bold uppercase tracking-wide text-amber-600">
              Underwriting logic
            </span>
            {c.logic}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * One `data_pull` step: start the orchestration run, poll it, and render each
 * service as a row (the ChecksRunner visual the mockup established, now fed by
 * real data — including the amber "Underwriting logic" chip wherever the server
 * supplies a rule).
 *
 * The outcome never gates the flow. A non-pass settles into a referral the parent
 * makes sticky; a failed start or a failed/timed-out poll offers a retry AND a way
 * to carry on, because a pull the broker can't complete must not trap them
 * (NN #7).
 */
export function DataPullStep({
  orderId,
  step,
  initialRun,
  onSettled,
  onDone,
}: {
  orderId: string;
  step: Extract<BondStep, { type: "data_pull" }>;
  /** A run this order already settled, restored from the order (BRK-S088). */
  initialRun?: BondStepRun;
  onSettled: (run: BondStepRun) => void;
  onDone: () => void;
}) {
  const { authedRequest } = useAuth();
  // One run per attempt, shared with the other run surface (BRK-S196).
  const { run, error, retry } = useBondStepRun(
    authedRequest,
    orderId,
    step.key,
    { initialRun, onSettled },
  );

  const checks = run?.checks ?? [];
  const settled = run?.status === "settled";
  const passed = settled && run?.outcome === "pass";
  // WHAT IS ACTUALLY HAPPENING, read off the rows (PMS-S187). "Running the
  // checks — this only takes a moment" was the line on screen during the
  // 2026-08-20 stall, while nothing had been sent to anyone.
  const waitingOn = waitingOnCheck(run);
  const anythingSent = checks.some((c) => checkState(c) === "awaiting_vendor");
  const stepNote = settled
    ? "These checks are complete."
    : checks.length === 0
      ? "Starting the checks for this step."
      : anythingSent && waitingOn !== null
        ? `Waiting on ${waitingOn}. The rest run after it.`
        : waitingOn !== null
          ? "These checks are queued — none has been sent yet."
          : "Running the checks for this step.";

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold text-brk-text">{step.title}</h2>
        <p className="text-sm text-brk-dim">{stepNote}</p>
      </div>

      {checks.map((c) => (
        <CheckRow key={c.key} check={c} />
      ))}

      {error && (
        <div className="flex flex-col gap-3 rounded-xl border border-brk-border bg-brk-panel p-5">
          <p role="alert" className="text-sm text-brk-text">
            {error}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={retry}
              className="rounded-md border border-brk-border px-4 py-2 text-sm font-medium text-brk-accent transition-colors hover:bg-brk-bg"
            >
              Try again
            </button>
            {/* A check the broker can't complete must never trap them. */}
            <button
              type="button"
              onClick={onDone}
              className="text-sm text-brk-accent hover:underline"
            >
              Continue without it
            </button>
          </div>
        </div>
      )}

      {settled && (
        <div
          className={`flex flex-col items-center gap-4 rounded-xl border p-6 text-center ${
            passed
              ? "border-green-200 bg-green-50"
              : "border-amber-300 bg-amber-50"
          }`}
        >
          <p
            className={`font-semibold ${
              passed ? "text-green-800" : "text-amber-900"
            }`}
          >
            {passed
              ? "All checks passed — you can carry on."
              : "We'll pass this to an underwriter. You can carry on and finish your submission."}
          </p>
          <button
            type="button"
            onClick={onDone}
            className="rounded-full bg-brk-accent px-6 py-3 font-medium text-white transition-colors hover:bg-brk-accent-strong"
          >
            Continue
          </button>
        </div>
      )}
    </div>
  );
}

// ─── An unrenderable step, named rather than blank (BRK-S132) ────────────────

/**
 * The guarded state for a step `type` this build has never heard of.
 *
 * This is what the wizard did BEFORE it existed: both `type === "fields"` and
 * `type === "data_pull"` were false, every render branch fell through, and the
 * step rendered NOTHING — with the footer's Continue still live in front of it.
 * A broker got a blank card they could not act on and could not describe, and
 * the flow carried on as if the step had been answered.
 *
 * The configuration's vocabulary is CLOSED and owned upstream, so this can only
 * happen in the deploy window between discovery adding a member and this bundle
 * shipping (or on a broker holding a cached bundle). One release closes it —
 * which is exactly why the state has to be honest rather than absent, and why
 * it says what to do about it.
 *
 * It never traps: a step this build cannot render is not a step the broker can
 * complete, so Continue is offered. Whatever the step was asking for is then
 * genuinely missing, which an underwriter will see — better than a silent skip
 * nobody can tell from a completed step.
 */
function UnsupportedStep({
  step,
  onSkip,
}: {
  step: BondStep;
  onSkip: () => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold text-brk-text">{step.title}</h2>
        <p className="text-sm text-brk-dim">
          This step needs a newer version of the portal.
        </p>
      </div>
      <div className="flex flex-col gap-3 rounded-xl border border-amber-300 bg-amber-50 p-5">
        <p role="alert" className="text-sm text-amber-900">
          We can’t show <span className="font-medium">{step.title}</span> yet —
          reload the page to pick up the latest version. If it still doesn’t
          appear, carry on and our team will follow up for anything missing.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-md border border-amber-400 px-4 py-2 text-sm font-medium text-amber-900 transition-colors hover:bg-amber-100"
          >
            Reload
          </button>
          <button
            type="button"
            onClick={onSkip}
            className="text-sm text-brk-accent hover:underline"
          >
            Continue without it
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Program preapproval: chip 1 of 2, the Checks bar (BRK-S132) ─────────────

/**
 * The Checks chip: start the run, poll it, and show a progress bar.
 *
 * Deliberately NOT `DataPullStep` with a different skin, and the difference is
 * not cosmetic. A data pull renders one row per orchestration with a verdict on
 * each, because a broker filling in a bond request needs to know which bureau
 * is slow and which referred them. A program preapproval renders a BAR and
 * nothing else: which bureaus Signal consults, and what each of them said, is
 * Signal's underwriting business (Aaron 2026-09-02). The rows are on the wire —
 * this is the one surface that must not read them, and `preapprovalProgress`
 * takes the fold precisely so it cannot.
 *
 * It AUTO-ADVANCES on settle: there is no verdict for the broker to acknowledge
 * here, so a Continue button would be a click between them and their
 * indication. The Indication chip is what has something to say.
 *
 * The run/poll mechanics are the SAME endpoints a data pull uses (a step key is
 * all either needs) and the same recovery: a failed start or a timed-out poll
 * offers Try again AND Continue without it, because a preapproval never blocks
 * an order.
 */
export function PreapprovalChecksStep({
  orderId,
  step,
  initialRun,
  onSettled,
  onDone,
}: {
  orderId: string;
  step: BondStep;
  /** A run this order already settled, restored from the order (BRK-S088). */
  initialRun?: BondStepRun;
  onSettled: (run: BondStepRun) => void;
  onDone: () => void;
}) {
  const { authedRequest } = useAuth();
  // One run per attempt, shared with the other run surface (BRK-S196).
  const { run, error, retry } = useBondStepRun(
    authedRequest,
    orderId,
    step.key,
    { initialRun, onSettled },
  );
  // Advance ONCE. `onDone` moves the wizard, which re-renders this card until
  // the swap lands; a bare effect on `settled` would fire again on the way out.
  const advanced = useRef(false);

  const progress = preapprovalProgress(run);

  useEffect(() => {
    if (!progress.settled || advanced.current) return;
    advanced.current = true;
    onDone();
  }, [progress.settled, onDone]);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold text-brk-text">Checks</h2>
        <p className="text-sm text-brk-dim">
          We’re reviewing your business to see which surety program you qualify
          for. Nothing to do — this only takes a moment.
        </p>
      </div>

      <div className="flex flex-col gap-3 rounded-xl border border-brk-border bg-brk-panel p-5">
        <div
          role="progressbar"
          aria-label="Reviewing your business"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress.percent}
          aria-valuetext={progress.label}
          className="h-2 w-full overflow-hidden rounded-full bg-brk-bg"
        >
          <div
            className="h-full rounded-full bg-brk-accent transition-[width] duration-700 ease-out"
            style={{ width: `${progress.percent}%` }}
          />
        </div>
        <p role="status" className="text-sm text-brk-text">
          {progress.label}
        </p>
      </div>

      {error && (
        <div className="flex flex-col gap-3 rounded-xl border border-brk-border bg-brk-panel p-5">
          <p role="alert" className="text-sm text-brk-text">
            {error}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={retry}
              className="rounded-md border border-brk-border px-4 py-2 text-sm font-medium text-brk-accent transition-colors hover:bg-brk-bg"
            >
              Try again
            </button>
            {/* A review the broker can't complete must never trap them. */}
            <button
              type="button"
              onClick={onDone}
              className="text-sm text-brk-accent hover:underline"
            >
              Continue without it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Program preapproval: chip 2 of 2, the Indication (BRK-S132) ─────────────

/**
 * The Indication chip. It renders the DECISION, and its job is mostly to know
 * which of four things is true:
 *
 *   • `approved`    — the indication view (BRK-S133);
 *   • `not_granted` — nothing to show HERE. The wizard advances to bond details,
 *     where a neutral notice sits atop the step the broker is now filling in
 *     (BRK-S133). A dedicated "not granted" screen would make a decline out of
 *     something that is not one: the order goes to an underwriter and carries on;
 *   • `pending`     — the mirror lag. PMS writes the decision onto the order
 *     AFTER the run settles, so the read that follows a settled run can
 *     legitimately still say pending. Bounded polling + an honest waiting state,
 *     never a blank panel;
 *   • nothing published — a PMS that predates PMS-S306, or a read that failed.
 *     One handled state with a way forward, because a preapproval never blocks
 *     an order (NN #7).
 */
function PreapprovalIndicationStep({
  orderId,
  accountId,
  seed,
  insuredName,
  programName,
  terminal = false,
  onFiled,
  onPreapproval,
  onDone,
}: {
  orderId: string;
  /** Where **Done** goes — the insured's Surety program, which is where a
   *  standalone pre-approval's decision actually lives. */
  accountId: string;
  /** What the order already said, so a settled decision renders with no read. */
  seed: BondPreapproval | null;
  insuredName: string;
  programName: string;
  /**
   * Is this the LAST chip (BRK-S160)?
   *
   * In a bond flow it never is: the decision is a screen on the way to the
   * terms. In the STANDALONE flow it is the whole point — so the moment the
   * decision settles, approved or not, this card FILES the order with an empty
   * bag and shows the broker their result plus the two connection links.
   */
  terminal?: boolean;
  /** Told once the standalone request really is filed, so the footer can drop
   *  a Back that would now lead to a step PMS refuses to save. */
  onFiled?: () => void;
  onPreapproval: (preapproval: BondPreapproval | null) => void;
  onDone: () => void;
}) {
  const navigate = useNavigate();
  const { authedRequest } = useAuth();
  const [preapproval, setPreapproval] = useState<BondPreapproval | null>(seed);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const settled = isPreapprovalSettled(preapproval);
  // The not-granted advance, once — the same guard the Checks bar needs, for
  // the same reason: `onDone` re-renders this card before the swap lands.
  const advanced = useRef(false);
  // ── Filing the standalone request (BRK-S160) ───────────────────────────────
  //
  // `idle` covers "not terminal" and "not settled yet"; everything else is the
  // one POST. NEVER A FALSE SUCCESS (the BRK-S113/S122 class): the completion
  // block renders Done only from `filed`, so a broker whose submit failed reads
  // the failure and a Retry rather than a finished screen over an unfiled
  // order.
  const [filing, setFiling] = useState<"idle" | "saving" | "filed" | "failed">(
    "idle",
  );
  const [fileError, setFileError] = useState<string | null>(null);
  // The two links off the SUBMIT's own response, exactly as `SubmittedScreen`
  // takes them — PMS publishes both on every bond-order body, so the ending
  // needs no second read (BRK-S115 / BRK-S118).
  // The Signal Advance answer on this order (BRK-S161), as the server states
  // it. Seeded by the filing and replaced by the enrolment's own response —
  // never by a local flag, so a failed enrolment cannot show the links.
  const [advanceOptIn, setAdvanceOptIn] = useState<BondAdvanceOptIn | null>(
    null,
  );
  const [links, setLinks] = useState<{
    accountingLink?: BondAccountingLink;
    bankLink?: BondBankLink;
  }>({});
  // One POST per settled decision. A ref rather than state because the effect
  // that sets it must not re-run on the change — and it is RELEASED by Retry,
  // which is the only thing that may try again.
  const filedRef = useRef(false);

  useEffect(() => {
    // A decision already in hand needs no read. The seed came from the order
    // response that rendered this chip, and re-reading it would ask for
    // something we have.
    if (isPreapprovalSettled(seed) && attempt === 0) return;
    let cancelled = false;
    setError(null);
    (async () => {
      try {
        const fresh = await pollBondPreapproval(authedRequest, orderId, {
          onUpdate: (p) => {
            if (!cancelled && p) setPreapproval(p);
          },
        });
        if (cancelled) return;
        setPreapproval(fresh);
        onPreapproval(fresh);
      } catch (err) {
        if (!cancelled) setError(messageFor(err));
      }
    })();
    return () => {
      cancelled = true;
    };
    // `attempt` is the retry trigger; `seed` is read once at mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authedRequest, orderId, attempt, onPreapproval]);

  useEffect(() => {
    // A TERMINAL chip never advances — there is nowhere to go, and the
    // not-granted notice renders right here instead of atop a step that does
    // not exist (BRK-S160).
    if (terminal) return;
    if (preapproval?.status !== "not_granted" || advanced.current) return;
    advanced.current = true;
    onDone();
  }, [terminal, preapproval?.status, onDone]);

  /**
   * File the standalone request, exactly once per settled decision.
   *
   * The bag is EMPTY: a `preapproval` configuration declares no final-step
   * fields, so there are no terms to send and the submit is purely "this
   * request is finished". A 409 is success that already happened — the
   * `isAlreadySubmitted` precedent — which is what makes a replayed effect, a
   * double-mounted StrictMode render and a broker's refresh all land on the
   * same screen.
   */
  const fileStandalone = useCallback(async () => {
    setFiling("saving");
    setFileError(null);
    try {
      const filed = await submitBond(authedRequest, orderId, {});
      setLinks({
        accountingLink: filed.accountingLink,
        bankLink: filed.bankLink,
      });
      // The Signal Advance answer as FILED (BRK-S161) — `null` on the filing
      // this card does, because it asks nothing before submitting. The offer
      // below records one through its own route.
      setAdvanceOptIn(filed.advanceOptIn ?? null);
      setFiling("filed");
      onFiled?.();
    } catch (err) {
      if (isAlreadySubmitted(err)) {
        // Already filed, so there is nothing to retry and nothing to report.
        // The links are unknown on this path and the panels render NOTHING for
        // an absent link — silence, never a fabricated one (BRK-S115).
        setFiling("filed");
        onFiled?.();
        return;
      }
      // Released, so Retry is a real second attempt rather than a no-op.
      filedRef.current = false;
      setFileError(messageFor(err));
      setFiling("failed");
    }
  }, [authedRequest, orderId, onFiled]);

  useEffect(() => {
    if (!terminal || !isPreapprovalSettled(preapproval)) return;
    if (filedRef.current) return;
    filedRef.current = true;
    void fileStandalone();
    // Covers a RESUMED draft too: `seed` already carries the settled decision,
    // so the order is completed on mount rather than waiting for a poll that
    // has nothing left to report.
  }, [terminal, preapproval, fileStandalone]);

  if (terminal && preapproval && isPreapprovalSettled(preapproval)) {
    const completion = (
      <PreapprovalCompletion
        state={filing}
        error={fileError}
        links={links}
        orderId={orderId}
        advanceOptIn={advanceOptIn}
        onAdvanceAnswered={(fresh) => {
          setAdvanceOptIn(fresh.advanceOptIn ?? null);
          setLinks({
            accountingLink: fresh.accountingLink,
            bankLink: fresh.bankLink,
          });
        }}
        onRetry={() => {
          filedRef.current = true;
          void fileStandalone();
        }}
      />
    );
    const done = () => navigate(`/surety/accounts/${accountId}`);
    return (
      <div className="flex min-w-0 flex-col gap-6">
        {preapproval.status === "approved" ? (
          <IndicationView
            orderId={orderId}
            preapproval={preapproval}
            insuredName={insuredName}
            programName={programName}
            onContinue={done}
            continueLabel="Done"
            continueDisabled={filing !== "filed"}
            beforeContinue={completion}
          />
        ) : (
          <>
            {/* INLINE, and with no auto-advance: there is no bond-details step
                to land on, so the notice says its piece where the broker is. */}
            <NotGrantedNotice
              insuredName={insuredName}
              programName={programName}
              standalone
            />
            {completion}
            <button
              type="button"
              onClick={done}
              disabled={filing !== "filed"}
              className="self-start rounded-full bg-brk-accent px-6 py-3 font-medium text-white transition-colors hover:bg-brk-accent-strong disabled:cursor-not-allowed disabled:opacity-50"
            >
              Done
            </button>
          </>
        )}
        {/* The request itself is still a record, and a broker who wants to see
            what they asked for should not have to find it in the list. */}
        <Link
          to={`/surety/submissions/${orderId}`}
          className="self-start text-sm text-brk-accent hover:underline"
        >
          View request
        </Link>
      </div>
    );
  }

  if (preapproval?.status === "approved") {
    return (
      <IndicationView
        orderId={orderId}
        preapproval={preapproval}
        insuredName={insuredName}
        programName={programName}
        onContinue={onDone}
      />
    );
  }

  // Not granted: this chip shows nothing, and the effect above has already sent
  // the wizard to bond details. Rendering a placeholder in the frame the broker
  // is leaving would flash a screen that says less than the notice they land on.
  if (preapproval?.status === "not_granted") return null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold text-brk-text">Indication</h2>
        <p className="text-sm text-brk-dim">
          {error === null
            ? "Finishing your program indication…"
            : "We couldn’t finish your indication just now."}
        </p>
      </div>

      {error === null ? (
        <div
          role="status"
          className="rounded-xl border border-brk-border bg-brk-panel p-5 text-sm text-brk-text"
        >
          Your review is complete and we’re putting your indication together.
          This usually only takes a few seconds.
        </div>
      ) : (
        <div className="flex flex-col gap-3 rounded-xl border border-brk-border bg-brk-panel p-5">
          <p role="alert" className="text-sm text-brk-text">
            {error}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setAttempt((a) => a + 1)}
              className="rounded-md border border-brk-border px-4 py-2 text-sm font-medium text-brk-accent transition-colors hover:bg-brk-bg"
            >
              Try again
            </button>
            {/* Never a trap: the order is complete-able either way, and an
                underwriter picks the program up from here. Absent on a TERMINAL
                chip, where there is no bond detail to continue to — a button
                that walks a broker off the end of the spine is the blank-card
                bug by another route (BRK-S160). */}
            {!terminal && (
              <button
                type="button"
                onClick={onDone}
                className="text-sm text-brk-accent hover:underline"
              >
                Continue to your bond details
              </button>
            )}
          </div>
        </div>
      )}

      {!settled && error === null && !terminal && (
        <button
          type="button"
          onClick={onDone}
          className="self-start text-sm text-brk-accent hover:underline"
        >
          Skip ahead to your bond details
        </button>
      )}
    </div>
  );
}

// ─── Filing the standalone request, and what the broker still has to do ──────
// BRK-S160.

/**
 * The three states the ending can be in, and the two links that come with it.
 *
 * SHARED by the approved and the not-granted endings, because the filing is the
 * same act either way: the broker asked for a pre-qualification, the decision
 * landed, and the request is closed. What differs above it is the decision.
 *
 * A FAILURE IS NEVER DRESSED AS SUCCESS. The order is still a draft, so the
 * only honest offer is the one that fixes it — Retry — and the caller's **Done**
 * stays disabled until `filed`. Silence here would be worse than the error: the
 * broker would leave believing they had finished.
 */
function PreapprovalCompletion({
  state,
  error,
  links,
  orderId,
  onRetry,
  advanceOptIn,
  onAdvanceAnswered,
}: {
  /** The order's Signal Advance answer, and a way to record one (BRK-S161). */
  advanceOptIn: BondAdvanceOptIn | null;
  onAdvanceAnswered: (order: BondSubmission) => void;
  state: "idle" | "saving" | "filed" | "failed";
  error: string | null;
  links: { accountingLink?: BondAccountingLink; bankLink?: BondBankLink };
  orderId: string;
  onRetry: () => void;
}) {
  if (state === "failed") {
    return (
      <div className="flex flex-col gap-3 rounded-xl border border-brk-border bg-brk-panel p-4">
        <p role="alert" className="text-sm text-brk-text">
          {error ?? "We couldn’t save this request just now."}
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="self-start rounded-md border border-brk-border px-4 py-2 text-sm font-medium text-brk-accent transition-colors hover:bg-brk-bg"
        >
          Retry
        </button>
      </div>
    );
  }
  if (state !== "filed") {
    return (
      <p role="status" className="text-sm text-brk-dim">
        Saving your result…
      </p>
    );
  }
  // THE SAME OFFER the filed-order confirmation renders, around the SAME two
  // link panels (BRK-S161 / BRK-S115 / BRK-S118) — so the two endings of this
  // flow cannot grow two ideas of what Signal Advance is or of when a bank link
  // is handed over. The links appear only once the broker has opted in.
  return (
    <>
      <SignalAdvanceOffer
        orderId={orderId}
        // This ending is the standalone pre-approval's alone (BRK-S160), and a
        // pre-approval has no project or bond to talk about (BRK-S181).
        flow="preapproval"
        answer={advanceOptIn}
        onAnswered={onAdvanceAnswered}
        links={
          <>
            <AccountingLinkPanel
              link={links.accountingLink}
              orderId={orderId}
            />
            <BankLinkPanel link={links.bankLink} orderId={orderId} />
          </>
        }
      />
      {advanceOptIn === "declined" && <AdvanceDeclinedNotice />}
    </>
  );
}

// ─── The indication view (BRK-S133) ─────────────────────────────────────────

/**
 * The one sentence every surface carrying these figures must say, declared once
 * (BRK-S134's standing banner says it too). An indication is not a quote and
 * not a commitment, and a figure on a screen without this line reads like one.
 */
export const INDICATION_DISCLAIMER =
  "Program terms are preliminary and non-binding. Each bond is subject to review of contract terms, financing and other conditions.";

/**
 * The right-column reassurances (Greg's wireframe). Data, not markup, because
 * the same three appear on this view and the layout below has to place them in
 * a column at `lg:` and inline at every width under it.
 */
const INDICATION_ASSURANCES: readonly { title: string; body: string }[] = [
  {
    title: "Preliminary and non-binding",
    body: "These are indication terms, not a commitment to issue. Nothing here obligates you or Signal Specialty.",
  },
  {
    title: "Each bond evaluated individually",
    body: "Your program sets the outside limits. Every bond request inside it is still reviewed on its own contract terms.",
  },
  {
    title: "Move forward with confidence",
    body: "You can quote inside these limits today. Submit your bond request and our surety team takes it from here.",
  },
];

/**
 * What an APPROVED preapproval looks like.
 *
 * THREE TILES AND NOTHING ELSE (Aaron 2026-09-02): the single bond limit, the
 * aggregate program limit, and the rate. Greg's wireframe also showed the
 * surety's name, its A.M. Best rating and its Treasury status — all three are
 * deliberately absent, and this is not a rendering shortcut: Signal cannot
 * state them to a broker at indication time, so PMS does not publish them and
 * `test/lib/preapprovalDiscretion.test.ts` fails if a type in this repo grows
 * a field for one.
 *
 * `terms` is nullable on the contract, and an approved decision with none is a
 * contract gap rather than a state to design for — the tiles are skipped and
 * the broker still moves on, because nothing here may block the order.
 *
 * MOBILE FIRST: one column, the tiles stacked, the assurances after the
 * disclaimer. The wireframe's two-column layout arrives at `lg:`, where the
 * assurances become the right rail.
 */
function IndicationView({
  orderId,
  preapproval,
  insuredName,
  programName,
  onContinue,
  continueLabel = "Continue to your bond details",
  continueDisabled = false,
  beforeContinue,
}: {
  orderId: string;
  preapproval: BondPreapproval;
  insuredName: string;
  programName: string;
  onContinue: () => void;
  /** The button's words. "Done" when this view is the END of a standalone
   *  pre-approval rather than a screen on the way to the terms (BRK-S160). */
  continueLabel?: string;
  /** Held while the standalone request is still being filed, so the broker
   *  cannot leave a finished-looking screen over an unfiled order. */
  continueDisabled?: boolean;
  /** What sits between the indication and the button — the standalone flow's
   *  saving/error state and its two connection links. */
  beforeContinue?: ReactNode;
}) {
  const terms = preapproval.terms;
  return (
    <div className="flex min-w-0 flex-col gap-6">
      <div className="flex flex-col gap-2 rounded-xl border border-green-200 bg-green-50 p-5">
        <p className="text-lg font-semibold text-green-900">
          Prequalification Complete
        </p>
        <p className="text-sm text-green-800">
          {insuredName || "Your insured"} qualifies for Signal Specialty’s{" "}
          {programName || "surety"} surety program
        </p>
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-6 lg:grid-cols-[1fr_18rem]">
        <div className="flex min-w-0 flex-col gap-6">
          {terms && (
            <div className="flex flex-col gap-3">
              <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-brk-dim">
                Your program indication
              </h2>
              <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <IndicationTile
                  label="Single Bond Limit"
                  value={formatPreapprovalMoney(terms.singleBondLimit)}
                />
                <IndicationTile
                  label="Aggregate Program Limit"
                  value={formatPreapprovalMoney(terms.aggregateProgramLimit)}
                />
                <IndicationTile
                  label="Bond Rate"
                  value={`${bondRatePercent(terms.ratePer1000)}%`}
                  caption="Flat rate"
                />
              </dl>
              <p className="text-sm text-brk-dim">{INDICATION_DISCLAIMER}</p>
            </div>
          )}

          <IndicationDocument
            orderId={orderId}
            available={preapproval.indicationAvailable}
          />
        </div>

        <aside className="flex min-w-0 flex-col gap-4">
          {INDICATION_ASSURANCES.map((a) => (
            <div key={a.title} className="flex min-w-0 flex-col gap-1">
              <p className="text-sm font-semibold text-brk-text">{a.title}</p>
              <p className="text-sm text-brk-dim">{a.body}</p>
            </div>
          ))}
        </aside>
      </div>

      {beforeContinue}

      <button
        type="button"
        onClick={onContinue}
        disabled={continueDisabled}
        className="self-start rounded-full bg-brk-accent px-6 py-3 font-medium text-white transition-colors hover:bg-brk-accent-strong disabled:cursor-not-allowed disabled:opacity-50"
      >
        {continueLabel}
      </button>
    </div>
  );
}

/** One figure on the indication. A `dl` pair, so the label/value relationship
 *  is in the markup rather than only in the layout. */
function IndicationTile({
  label,
  value,
  caption,
}: {
  label: string;
  value: string;
  caption?: string;
}) {
  return (
    <div className="min-w-0 rounded-xl border border-brk-border bg-brk-panel p-4">
      <dt className="text-xs font-medium uppercase tracking-wide text-brk-dim">
        {label}
      </dt>
      <dd className="mt-1 break-words text-xl font-semibold text-brk-text">
        {value}
      </dd>
      {caption && <p className="mt-1 text-xs text-brk-dim">{caption}</p>}
    </div>
  );
}

/**
 * The Program Indication LETTER — previewed inline, openable in a new tab, and
 * downloadable.
 *
 * The bytes are fetched (`useIndicationPdf`) rather than handed to an
 * `<iframe src>`, because the route is own-scoped and bearer-authenticated and
 * an iframe's own request carries no `Authorization` header — it would 401 and
 * paint an empty frame with nothing on screen saying why. What the frame gets
 * is a blob URL for bytes this session already holds.
 *
 * THERE IS NO EMPTY FRAME IN ANY STATE, which is the whole point of the four
 * branches: `loading` and `preparing` render a titled panel that says what is
 * happening, `unavailable` says so in a sentence and still offers a retry, and
 * only `ready` renders an `<object>`. `indicationAvailable` is a PREDICTION made
 * when the order was read — rendering the letter is a later step upstream than
 * deciding the program — so a false one is a waiting state, never a missing
 * panel.
 *
 * `<object>` rather than `<iframe>`: it takes a `type`, so a browser with no
 * inline PDF viewer falls back to the children instead of a blank box — and the
 * children are the two buttons, which work regardless.
 */
function IndicationDocument({
  orderId,
  available,
}: {
  orderId: string;
  available: boolean;
}) {
  const { authedBlob, authedRequest } = useAuth();
  const [attempt, setAttempt] = useState(0);
  const pdf = useIndicationPdf(authedBlob, authedRequest, orderId, available, {
    retryToken: attempt,
  });

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-brk-dim">
        Your indication letter
      </h2>

      {pdf.status === "ready" ? (
        <>
          <div className="min-w-0 overflow-hidden rounded-xl border border-brk-border bg-brk-panel">
            <object
              data={pdf.url}
              type="application/pdf"
              aria-label="Program Indication letter"
              className="h-[28rem] w-full"
            >
              <p className="p-5 text-sm text-brk-dim">
                Your browser can’t show the letter inline — use View Indication
                or Download PDF below.
              </p>
            </object>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <a
              href={pdf.url}
              target="_blank"
              rel="noreferrer"
              className="rounded-md border border-brk-border px-4 py-2 text-sm font-medium text-brk-accent transition-colors hover:bg-brk-bg"
            >
              View Indication
            </a>
            <a
              href={pdf.url}
              // The SERVER's name — `Program-Indication-SB-01578.pdf` — which
              // carries the order number and matches what an underwriter sees.
              download={pdf.filename}
              className="rounded-md border border-brk-border px-4 py-2 text-sm font-medium text-brk-accent transition-colors hover:bg-brk-bg"
            >
              Download PDF
            </a>
          </div>
        </>
      ) : pdf.status === "unavailable" ? (
        <div className="flex flex-col gap-3 rounded-xl border border-brk-border bg-brk-panel p-5">
          <p role="alert" className="text-sm text-brk-text">
            {pdf.message}
          </p>
          <button
            type="button"
            onClick={() => setAttempt((a) => a + 1)}
            className="self-start rounded-md border border-brk-border px-4 py-2 text-sm font-medium text-brk-accent transition-colors hover:bg-brk-bg"
          >
            Check again
          </button>
        </div>
      ) : (
        <div
          role="status"
          className="rounded-xl border border-brk-border bg-brk-panel p-5 text-sm text-brk-text"
        >
          We’re preparing your indication letter — it’ll appear here in a
          moment.
        </div>
      )}
    </div>
  );
}

// ─── Not granted — a notice, not a decline (BRK-S133) ────────────────────────

/**
 * What a broker reads when the program was not granted automatically.
 *
 * It sits atop the BOND DETAILS step rather than on a screen of its own, and
 * that placement is the decision: a dedicated "not granted" page would make a
 * decline out of something that is not one. The order goes to an underwriter
 * and carries on, so the broker lands on the step they were going to next and
 * the notice explains itself there.
 *
 * NEUTRAL, AND WITH NO REASON. No "declined", no "rejected", no score, no
 * explanation — Signal does not tell a broker why (Aaron 2026-09-02), PMS
 * publishes nothing that could, and inventing a reason here would be a claim
 * nobody made. What it DOES say is the only thing the broker can act on: carry
 * on, and a person will look at it.
 *
 * Neutral in COLOUR too. Not amber: the referral notice beside it is amber
 * because a pull found something, and two amber panels would read as two
 * problems. This is a fact about a program, not a finding about the insured.
 */
function NotGrantedNotice({
  insuredName,
  programName,
  standalone = false,
}: {
  insuredName: string;
  programName: string;
  /**
   * Is this the END of a standalone pre-approval, rather than a notice atop the
   * bond details (BRK-S160)?
   *
   * The second sentence is the only difference, and it has to change: there is
   * no bond request here to "still complete", and telling a broker to finish
   * one would send them looking for a step that does not exist. What stays
   * identical is everything that matters — the same heading, the same neutral
   * panel, no reason, no score, and the word "declined" nowhere near it.
   */
  standalone?: boolean;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-brk-border bg-brk-panel p-4">
      <p className="text-sm font-semibold text-brk-text">
        An underwriter will review this request
      </p>
      <p className="text-sm text-brk-dim">
        We weren’t able to pre-qualify {insuredName || "this insured"} for the{" "}
        {programName || "surety"} program automatically.{" "}
        {standalone
          ? "We’ve recorded your request — an underwriter will review it and follow up."
          : "You can still complete your bond request now — an underwriter will review it and follow up."}
      </p>
    </div>
  );
}

// ─── A preapproval the insured already held (BRK-S134) ───────────────────────

/**
 * The compact banner atop BOND DETAILS for an order that inherited a granted
 * program preapproval from the insured's account.
 *
 * WHY THE CHIPS ARE GONE RATHER THAN TICKED. `bondChips` omits the Checks and
 * Indication chips entirely for this case (`skipPreapproval`), because a ✓ on a
 * chip is a claim that THIS order did the work — and it did not: nothing ran,
 * and re-running the pulls on an insured who already holds a program would be
 * paying a bureau twice for an answer Signal already has. So the wizard goes
 * from the last fields step straight to the terms, and this banner is where the
 * standing figures live.
 *
 * A BANNER RATHER THAN THE FULL INDICATION VIEW, for the same reason: this is
 * context for the step the broker is filling in, not a result they just
 * received. It carries the same three figures, the evaluated DATE (which the
 * fresh view has no need for — that decision was seconds old), the same
 * preliminary/non-binding line, and a link to the letter.
 *
 * The letter is a LINK, not an inline preview. The `<object>` is 28rem of PDF
 * viewer; pushing the terms form below it, on a screen whose purpose is the
 * form, would bury the work. `View indication` opens the same letter — fetched
 * the same way, because the route is bearer-authenticated and an `<a href>` to
 * it would 401 (see `IndicationDocument`).
 *
 * A standing NOT-GRANTED is deliberately not this: it behaves like a fresh
 * order and the checks run again (`hasStandingPreapproval`). A prior no is not
 * a permanent no — the insured's figures move — and a wizard that skipped the
 * run on the strength of one would make a retry impossible from the portal.
 */
function StandingPreapprovalBanner({
  orderId,
  preapproval,
  programName,
}: {
  orderId: string;
  preapproval: BondPreapproval;
  programName: string;
}) {
  const terms = preapproval.terms;
  return (
    <div className="flex min-w-0 flex-col gap-3 rounded-xl border border-green-200 bg-green-50 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <p className="text-sm font-semibold text-green-900">
          Preapproved program
          {programName ? ` — ${programName}` : ""}
        </p>
        {preapproval.evaluatedAt && (
          <p className="text-xs text-green-800">
            Evaluated {formatBondDate(preapproval.evaluatedAt)}
          </p>
        )}
      </div>

      {terms && (
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-3">
          <StandingFigure
            label="Single bond limit"
            value={formatPreapprovalMoney(terms.singleBondLimit)}
          />
          <StandingFigure
            label="Aggregate program limit"
            value={formatPreapprovalMoney(terms.aggregateProgramLimit)}
          />
          <StandingFigure
            label="Bond rate"
            value={`${bondRatePercent(terms.ratePer1000)}% flat`}
          />
        </dl>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <p className="min-w-0 flex-1 text-xs text-green-800">
          {INDICATION_DISCLAIMER}
        </p>
        <IndicationLink orderId={orderId} />
      </div>
    </div>
  );
}

/** One figure on the standing banner. Denser than `IndicationTile` on purpose:
 *  this is a strip above a form, not the result screen. */
function StandingFigure({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-green-800">{label}</dt>
      <dd className="break-words text-sm font-semibold text-green-900">
        {value}
      </dd>
    </div>
  );
}

/**
 * "View indication" — the letter, in a new tab, without an inline preview.
 *
 * Still a FETCH behind the scenes (`useIndicationPdf`): the route is
 * bearer-authenticated, so a plain `<a href="/api/…/indication.pdf">` would
 * navigate the tab to a 401 rather than open a letter. The link only appears
 * once the bytes are in hand, because a link that opens nothing is worse than
 * no link — and its absence is quiet, because a broker reading their standing
 * limits is not waiting on the letter.
 */
function IndicationLink({ orderId }: { orderId: string }) {
  const { authedBlob, authedRequest } = useAuth();
  // `available: true` because this banner only renders for a GRANTED standing
  // program — and the hook re-reads the order anyway if the letter is not
  // minted yet, so an optimistic seed costs one extra read at worst.
  const pdf = useIndicationPdf(authedBlob, authedRequest, orderId, true);
  if (pdf.status !== "ready") return null;
  return (
    <a
      href={pdf.url}
      target="_blank"
      rel="noreferrer"
      className="shrink-0 text-xs font-medium text-green-900 underline hover:no-underline"
    >
      View indication
    </a>
  );
}

// ─── Referral notice ─────────────────────────────────────────────────────────

/**
 * The persistent, NON-BLOCKING referral notice. Shown from the moment a pull
 * settles non-pass, through every later step and on the confirmation screen —
 * because the broker is going to complete this submission either way, and the one
 * thing they need to know is that a human will look at it.
 */
function ReferralNotice({
  reasons,
  filed,
}: {
  reasons: string[];
  /** The order is already submitted — there is nothing left to carry on with. */
  filed?: boolean;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-amber-300 bg-amber-50 p-4">
      <p className="text-sm font-semibold text-amber-900">
        This submission will be referred to underwriting for review.
      </p>
      <p className="text-sm text-amber-800">
        {filed
          ? "Nothing to do — our surety team will follow up."
          : "Nothing to do — carry on and finish it. Our team will follow up."}
      </p>
      {reasons.length > 0 && (
        <ul className="list-inside list-disc text-sm text-amber-800">
          {reasons.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── A filed order, opened again ─────────────────────────────────────────────

// `RecordField` and `CapturedValues` moved to `components/BondRecord.tsx` in
// BRK-S140, when the submission profile became the second surface rendering a
// captured bag as a record.

/**
 * WHOSE answers a per-owner step's values are (BRK-S109, consuming PMS-S244).
 *
 * A repeated path (`personal[].lastName`) belongs to ONE owner — the one the
 * roster marks primary, resolved upstream. Saying so is the difference between
 * a record and a bag of fields: an underwriter reading a date of birth needs to
 * know whose it is, and before the roster nobody could say.
 *
 * THREE STATES, and they mean different things:
 *   • a marked primary → name them;
 *   • NO ROSTER at all → "nobody declared one". Emphatically NOT "this order has
 *     no owners", and never a licence to name whoever came first — the absent
 *     roster is what the ordering inference used to fill in;
 *   • a roster marking nobody → also "not stated", but for a different reason,
 *     said differently so the two are not confused in a bug report.
 *
 * Names ONE owner, deliberately. Listing every owner is repeatable-row work and
 * belongs to BRK-S094; this is the label the single resolved value already
 * needed.
 */
function OwnerAttribution({ owners }: { owners: BondOwner[] | undefined }) {
  const primary = primaryBondOwner(owners);
  if (primary) {
    return (
      <p className="text-xs text-brk-dim">
        Answers from{" "}
        <span className="font-medium text-brk-text">{primary.displayName}</span>
        , the primary owner.
      </p>
    );
  }
  return (
    <p className="text-xs text-brk-dim">
      {owners === undefined
        ? "Primary owner not stated — no owner roster was recorded for this submission."
        : "Primary owner not stated — the owner roster marks none as primary."}
    </p>
  );
}

/**
 * HOW MANY owners a per-owner step was about (BRK-S111, consuming PMS-S238).
 *
 * `values` resolves a repeated path to the primary owner's answer alone, which
 * is what a flow capturing one person needs — and it means a two-owner order
 * renders identically to a one-owner order. This is the line that stops the
 * record from understating itself: an underwriter reading one date of birth
 * needs to know a second owner exists, even though this flow does not show
 * their answers.
 *
 * Deliberately NOT repeatable rows — that is BRK-S094's work. This says only
 * what the count already tells us, and says it beside the owner it IS showing.
 *
 * Rendered only for a count ABOVE ONE. A count of 1 is a real affirmation on
 * the wire, but on screen it says nothing `OwnerAttribution` has not already
 * said by naming the owner, and a line under every single-owner step is noise
 * that trains a reader to skip the one that matters.
 */
function OwnerCountNotice({ count }: { count: number }) {
  if (count <= 1) return null;
  return (
    <p className="text-xs text-brk-dim">
      <span className="font-medium text-brk-text">
        {count} owners answered fields on this step.
      </span>{" "}
      Only the primary owner’s answers are shown here.
    </p>
  );
}

/** One step of the filed order: its captured answers, or the verdicts its data
 *  pull came back with. */
/**
 * A filed roster step's fields, one set per row the bag carries, LABELLED by
 * whose they are (BRK-S152).
 *
 * The record is read, not typed, so the block headings the form uses do not
 * exist here — the label has to carry the person, or a filed order reads as
 * "First name · First name · First name". A spouse row names its owner from
 * `spouseOf`, which is the same fact the checklist reads.
 */
function filedRosterFields(
  template: BondRenderField[],
  values: Record<string, unknown>,
  owners: BondOwner[] | undefined,
): { fields: BondRenderField[]; values: Record<string, unknown> } {
  const bag = values as BondValueBag;
  const roster = rosterRowsFromValues(bag, owners);
  const fields: BondRenderField[] = [];
  const resolved: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(values)) {
    if (!isRepeatedPath(key)) resolved[key] = value;
  }
  let position = 0;
  for (const person of orderedRoster(roster)) {
    if (person.spouseOf === null) position += 1;
    const ownerName =
      person.spouseOf === null
        ? person.displayName
        : (roster.find((p) => p.ownerId === person.spouseOf)?.displayName ??
          `Owner ${position}`);
    const who = person.spouseOf === null ? ownerName : `${ownerName}’s spouse`;
    for (const field of template) {
      if (!isRepeatedPath(field.name)) continue;
      const held = readFor(bag, field.name, person.ownerId, owners);
      if (held === undefined) continue;
      const name = scopedRosterName(person.ownerId, field.name);
      resolved[name] = held;
      fields.push({ ...field, name, label: `${who} — ${field.label}` });
    }
  }
  return { fields, values: resolved };
}

function FiledStepPanel({
  step,
  values,
  run,
  owners,
  ownerCount,
}: {
  step: BondStep;
  values: Record<string, unknown>;
  run: BondStepRun | undefined;
  /** The order's roster. `undefined` means none was declared — see
   *  `OwnerAttribution`; it is never read as "there are no owners". */
  owners: BondOwner[] | undefined;
  /** How many owners answered this step, or null when the server published no
   *  counts — never defaulted to 1, which would be an inference. */
  ownerCount: number | null;
}) {
  // A ROSTER step's record is one set of fields PER PERSON (BRK-S152).
  // Reading the configuration's template paths alone would find an entry LIST
  // where a value is expected and report "nothing captured on this step" for a
  // roster the broker filled in completely.
  const roster =
    step.type === "fields" && rosterKind(step) === "indemnity"
      ? filedRosterFields(step.fields.map(toRenderField), values, owners)
      : null;
  const fields =
    roster?.fields ??
    (step.type === "fields" ? step.fields.map(toRenderField) : []);
  const captured = roster?.values ?? values;
  // Only a step that captures a repeated block has an owner to attribute to.
  // A business step's values belong to the order, not to a person.
  const perOwner = capturesOwnerValues(fields);
  const primary = perOwner ? primaryBondOwner(owners) : null;
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-brk-border bg-brk-panel p-5">
      <h3 className="text-sm font-semibold text-brk-text">{step.title}</h3>
      {perOwner && <OwnerAttribution owners={owners} />}
      {perOwner && ownerCount !== null && (
        <OwnerCountNotice count={ownerCount} />
      )}
      {step.type === "fields" ? (
        <CapturedValues
          fields={fields}
          values={captured}
          // A repeated path whose PRIMARY owner supplied nothing is legitimately
          // absent from `values` (PMS-S244) — the value was never resolved to a
          // neighbour's. "Nothing captured on this step" would read as nobody
          // having answered anything, so when we can name the owner, we do.
          emptyNote={
            primary
              ? `${primary.displayName} answered nothing on this step.`
              : undefined
          }
        />
      ) : run ? (
        <div className="flex flex-col gap-3">
          {run.checks.map((c) => (
            <CheckRow key={c.key} check={c} />
          ))}
          {run.checks.length === 0 && (
            <p className="text-sm text-brk-dim">
              This check ran; it reported no individual results.
            </p>
          )}
          {/* The run's `reasons` are deliberately NOT repeated here: the
              referral notice above already lists them, and the same sentence
              twice on one screen reads as two findings. The per-row REVIEW
              verdict is what says which check produced them. */}
        </div>
      ) : (
        // A filed order can legitimately carry an un-run pull: a check the
        // broker couldn't complete never blocked them from submitting (locked
        // decision #4), so saying so is more honest than an empty panel.
        <p className="text-sm text-brk-dim">
          This check didn’t run before the submission was filed.
        </p>
      )}
    </div>
  );
}

// ─── The customer's connection links, shared by both filed-order surfaces ────

/**
 * ONE card handing the broker ONE url to pass to their customer — the shared
 * rendering behind BOTH the accounting card and the bank card (BRK-S118).
 *
 * ONE COMPONENT, FOUR RENDERINGS. Two links × two surfaces (the confirmation
 * screen the broker lands on at submit, and the durable record they reach on
 * resume) all come out of here. That is the precedent `CheckRow` set and the
 * accounting panel itself followed: the live runner and the filed record share
 * the row rather than growing a second rendering that drifts. Two renderings of
 * one idea disagree eventually, and the disagreement is invisible until a
 * broker reads one of them.
 *
 * WHAT THIS OWNS is the part that would drift: the card chrome, the plain-text
 * url, the copy control whose label never changes, and the announced "Copied."
 * WHAT IT DOES NOT OWN is any link's lifecycle. BOTH links now have a `state`
 * and their wording changes with it — but they are DIFFERENT axes, seven members
 * against four, and neither can be rendered from the other's vocabulary. So the
 * CALLER decides the words and hands them down, and this component asks no link
 * what state it is in.
 *
 * THAT SPLIT IS WHY BRK-S121 COST NOTHING HERE. This component was written when
 * only one of the two links had a state, and the note it carried then said the
 * bank card would grow its own `state`-driven choice in its own wrapper without
 * this file changing. It did, and this file did not: the only thing that moved is
 * that `status` was already optional, which is what a card with no state to
 * report needs — and still needs, for every order minted before contract 5.5.0.
 *
 * THE LINK AND NOTHING ELSE. Metrics are underwriter-only (SUR-S052 answer 1).
 * No metric value, no metric NAME, no balance-sheet item on either card — and
 * the order's own config declaration carries all three, sitting right there in
 * this page's data looking harmless to render. PMS holds this line with two
 * tests one layer up; `NewBondSubmissionPage.test.tsx` holds it here.
 */
function CustomerLinkPanel({
  headingId,
  heading,
  status,
  lead,
  detail,
  url,
  copyLabel,
}: {
  headingId: string;
  /** Names WHICH system this link connects. Two cards that did not each say so
   *  would leave the broker guessing which url goes where. */
  heading: string;
  /**
   * A short line naming WHERE the connection has got to, for a link that has a
   * lifecycle — the caller's words and the caller's verdict, never derived here
   * (BRK-S119). `connected` picks the indicator's colour and nothing else.
   *
   * OMITTED by a link with no state to report: it then renders exactly as it
   * did before this prop existed. That was the whole bank card until BRK-S121,
   * and it is now the bank link of every order minted before contract 5.5.0 —
   * a real link whose progress nobody has told us. Passing a `status` for one
   * of those would mean inventing a state upstream has said nothing about.
   */
  status?: { label: string; connected: boolean };
  lead: string;
  detail: string;
  /** The url to hand over, or null when there is nothing to hand over yet —
   *  which only the accounting link can be (its `unminted` state). The card
   *  still renders its words; it just offers no control over an empty string. */
  url: string | null;
  /** DISTINCT per card, always. Two controls both reading "Copy link" is the
   *  ambiguity that has a broker send the same url twice — one system
   *  connected, the other silently not, and the underwriter sees half the
   *  picture with nothing indicating why. */
  copyLabel: string;
}) {
  const [copied, setCopied] = useState(false);

  async function copyLink() {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // No clipboard permission (or no clipboard at all). The url is on screen
      // and selectable, so the broker is not stranded — but claiming a copy
      // that did not happen is worse than saying nothing.
      setCopied(false);
    }
  }

  return (
    <section
      aria-labelledby={headingId}
      className="flex w-full flex-col gap-3 rounded-xl border border-brk-border bg-brk-panel p-5 text-left"
    >
      <h2 id={headingId} className="text-lg font-semibold text-brk-text">
        {heading}
      </h2>
      {status && (
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-brk-dim">
          {/* A quiet indicator, and quiet in BOTH directions: green once the
              customer has connected, neutral until then. Never red, never a
              warning triangle — none of these states is an error, and none of
              them is something the broker can act on (SUR-S052 answers 5, 7). */}
          <span
            aria-hidden="true"
            className={`h-2 w-2 shrink-0 rounded-full ${
              status.connected ? "bg-green-600" : "bg-brk-dim"
            }`}
          />
          {status.label}
        </p>
      )}
      <p className="text-sm font-medium text-brk-text">{lead}</p>
      <p className="text-sm text-brk-dim">{detail}</p>

      {url !== null && (
        <>
          {/* Plain text, deliberately NOT an anchor. The link belongs to the
              CUSTOMER; a broker who follows it lands in someone else's
              connection flow. It is selectable, and the copy control is the
              way it is meant to travel. */}
          <p className="break-all rounded-md border border-brk-border bg-brk-bg px-3 py-2 font-mono text-sm text-brk-text">
            {url}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={copyLink}
              className="rounded-md border border-brk-border px-4 py-2 text-sm font-medium text-brk-text transition-colors hover:bg-brk-bg"
            >
              {copyLabel}
            </button>
            {/* Announced, never a relabelled button: the control's name has to
                stay stable for anyone who reads it a second time. */}
            {copied && (
              <span role="status" className="text-sm text-brk-dim">
                Copied.
              </span>
            )}
          </div>
        </>
      )}
    </section>
  );
}

/**
 * The link the broker hands to their customer, who uses it to connect their
 * accounting system (BRK-S115 / PMS-S256 / SUR-S088).
 *
 * This is the link WITH a lifecycle: `state` decides what the card says, and
 * that decision lives here rather than in `CustomerLinkPanel` — which is what
 * lets the bank link share the rendering without being given a state it does
 * not have.
 *
 * AN OUTSTANDING ITEM, NOT A FAILURE. The order is COMPLETE at submit (SUR-S052
 * answer 5) and there is nothing the broker can do if the customer never
 * connects (answer 7) — so no error styling, no "incomplete" badge, no nag, no
 * timeout, no dismiss. The pre-Link state is not an error on this surface even
 * though discovery answers a metric read against an unlinked connection with a
 * 409: the broker never asks for a metric.
 *
 * ABSENT is a FACT, not a gap: `accountingLink` is missing — never null — when
 * the order's configuration declared no accounting step, which is a normal
 * outcome for a bond product that asks for no financials. This renders NOTHING
 * for it, not an empty card and not a "not applicable" row.
 */
export function AccountingLinkPanel({
  link,
  orderId,
  poll,
}: {
  link?: BondAccountingLink;
  /**
   * The order to re-read while the state can still move (BRK-S119). BOTH
   * surfaces have it in hand — the filed record reads the order, and the
   * confirmation screen holds the very order it just submitted — so both
   * refresh, which is the point: the same component, the same freshness.
   *
   * Optional because the panel's job does not depend on it: with no id it
   * renders the state it was handed and never refreshes, which is strictly what
   * it did before this story.
   */
  orderId?: string;
  /** Injected in tests; defaults to a real timer. */
  poll?: BondLinkPollOptions;
}) {
  const { authedRequest } = useAuth();
  // The freshest state, not the one this surface was rendered with. Hooks run
  // before the absence check below — an absent link watches nothing.
  const current = useAccountingLinkState(authedRequest, orderId, link, poll);
  if (!current) return null;

  // What the broker can actually hand over, which is a different question from
  // what `state` says. `unminted` always carries nulls, and `pending` MAY: the
  // contract types both ids nullable on every state. So the rendering keys off
  // the url's presence within the state's framing rather than off the state
  // alone — which is what stops a copy control from ever sitting over an empty
  // string, and stops "send this link" appearing beside no link.
  const url =
    current.linkUrl && current.linkUrl.trim() !== "" ? current.linkUrl : null;
  const words = accountingLinkWords(current.state, url !== null);

  return (
    <CustomerLinkPanel
      headingId="accounting-link-heading"
      heading="Your customer’s accounting connection"
      status={{ label: words.status, connected: words.connected }}
      lead={words.lead}
      detail={words.detail}
      url={words.offerUrl ? url : null}
      copyLabel="Copy accounting link"
    />
  );
}

/** One state's words: the status line, the two lines under it, and whether the
 *  url is handed over at all. */
interface AccountingLinkWords {
  /** The short status line. DISTINCT per state — a broker who cannot tell two
   *  states apart has been told nothing. */
  status: string;
  /** Whether the status reads as connected (the quiet green indicator). */
  connected: boolean;
  lead: string;
  detail: string;
  /** Whether to offer the url. A url the state makes useless is worse than no
   *  url: the broker sends it, the customer follows it, and nothing works. */
  offerUrl: boolean;
}

/**
 * The whole state → copy mapping, in ONE exhaustive place (BRK-S119's rendering
 * half). Every member of the union gets a branch and the `never` check below
 * proves it, so "no state renders blank" is a compile-time property rather than
 * a test we remembered to write. An eighth member cannot be added upstream and
 * silently render as `pending` — the mirror would reject it first, and this
 * would not compile second.
 *
 * TWO INPUTS, NOT ONE. `state` says what is TRUE; `hasUrl` says what can be
 * HANDED OVER, and the contract types both ids nullable on every state
 * (BRK-S116). The url's presence therefore chooses within a state's framing,
 * never instead of it — which is what stops a copy control sitting over an
 * empty string, and stops a connected customer's broker losing the link they
 * saw minutes earlier.
 *
 * `linked` renders exactly as `syncing`, through `canonicalAccountingLinkState`.
 * They are the same fact in two vocabularies, and the fold lives in the lib so
 * the day discovery stops emitting `linked` nothing here changes at all.
 *
 * NONE OF THESE IS AN ERROR. The order is complete at submit (SUR-S052 answer 5)
 * and there is nothing the broker can do if a customer never connects (answer
 * 7), so no state gets error styling, an "incomplete" badge, a nag or a dismiss
 * — `needsReauth` and `disabled` included. What those two get instead is a
 * NEXT STEP, because they are the two states where doing nothing leaves the
 * underwriter without the data and only the broker is in a position to notice.
 */
function accountingLinkWords(
  state: BondAccountingLinkState,
  hasUrl: boolean,
): AccountingLinkWords {
  const canonical = canonicalAccountingLinkState(state);
  const connected = isAccountingLinkConnected(state);
  switch (canonical) {
    case "unminted":
    case "pending":
      // The two states that mean "not connected yet", told apart by whether
      // there is anything to send. `unminted` always carries nulls in practice,
      // and minting is BEST EFFORT upstream — it never fails a submit — so the
      // no-url case is a filed, numbered order with nothing to hand over YET.
      // The contract asks for exactly this: "render the state rather than an
      // empty panel". Neither is an error the broker can act on.
      return hasUrl
        ? {
            status: "Not connected",
            connected,
            lead: "Waiting for your customer to connect.",
            detail:
              "Send this link to your customer. They use it to connect their accounting system — nothing else is needed from you.",
            offerUrl: true,
          }
        : {
            status: "Not connected",
            connected,
            lead: "We’re still preparing this link.",
            detail:
              "Your bond request is submitted and complete. The link to send your customer isn’t ready yet — check back on this submission shortly.",
            offerUrl: false,
          };
    case "linked":
    case "syncing":
      // Connected, and still moving — which is why this is the state the panel
      // keeps refreshing out of. It says CONNECTED first: that is the news, and
      // the sync is a detail the broker does not act on.
      return {
        status: "Connected — syncing data",
        connected,
        lead: "Your customer has connected their accounting system.",
        detail:
          "Their data is still coming across. Nothing else is needed from you.",
        offerUrl: hasUrl,
      };
    case "connected":
      return {
        status: "Connected",
        connected,
        lead: "Your customer has connected their accounting system.",
        detail:
          "Nothing else is needed from you. The link stays here if they ever need to reconnect.",
        offerUrl: hasUrl,
      };
    case "needsReauth":
      // The one state with something for the broker to DO, so it says so — and
      // it RE-PRESENTS the link, because the customer redoing the connection is
      // the only way out of it and the broker is who hands it over. Telling
      // them to act while withholding the means would be the worse half of both
      // options.
      return {
        status: "Reconnection needed",
        connected,
        lead: "Your customer needs to reconnect their accounting system.",
        detail:
          "Their connection has lapsed. Send them this link again so they can reconnect it — nothing else is needed from you.",
        offerUrl: hasUrl,
      };
    case "disabled":
      // NO URL, deliberately, whatever the wire carries. This connection was
      // switched off; a link that cannot reconnect it is a broker sending their
      // customer somewhere that does not work, and then both of them waiting.
      // The way back is a person, so the card names one.
      return {
        status: "Connection disabled",
        connected,
        lead: "This accounting connection has been switched off.",
        detail:
          "Nothing is needed from you. Contact our surety team if your customer needs it switched back on.",
        offerUrl: false,
      };
    default: {
      // Unreachable: the union is closed and every member is answered above.
      // This is the compile-time proof of it — an added member fails HERE,
      // which is the whole reason no state can render blank.
      const exhaustive: never = canonical;
      return exhaustive;
    }
  }
}

/**
 * The OTHER link the broker hands to their customer, who uses it to connect
 * their bank through Plaid (BRK-S118 / PMS-S259 / SUR-S100), and how far they
 * have got with it (BRK-S121 / PMS-S270 / SUR-S118).
 *
 * A SEPARATE CARD, not a second row on the accounting one: nothing gates this
 * link on the accounting step, so an order can carry either, both or neither,
 * and two independent facts do not belong under one heading. It is the SAME
 * component though — `CustomerLinkPanel` above — because two cards that look
 * alike and are written twice stop looking alike.
 *
 * THE PREDICTION HELD. BRK-S118 said connection state would arrive as an added
 * key and that this wrapper would then grow a `state`-driven choice exactly like
 * the accounting card's, without the shared card changing at all. That is what
 * happened: `CustomerLinkPanel` is untouched, and it still asks no link what
 * state it is in.
 *
 * TWO ABSENCES, TWO DIFFERENT FACTS, and conflating them is the one mistake
 * this component exists to avoid:
 *
 *   • NO `bankLink` — there is nothing to hand over. A draft, an order with no
 *     owner on file, a workflow with no consent step, a vendor outage and an
 *     order filed before the link existed are one fact to a broker, so this
 *     renders NOTHING: no empty card, no "not applicable" row, no "not
 *     connected yet" claiming knowledge nobody has.
 *   • a `bankLink` with NO `state` — an order minted before contract 5.5.0. The
 *     link is real and the customer's progress is UNKNOWN, so the card renders
 *     exactly as it did before this story: the url, and no status line.
 *     Defaulting the state to `pending` here would publish a claim about a
 *     customer nobody made, on every order filed before the key existed.
 */
export function BankLinkPanel({
  link,
  orderId,
  poll,
}: {
  link?: BondBankLink;
  /** The order to re-read while the state can still move. BOTH surfaces have it
   *  in hand, exactly as they do for the accounting card, so both refresh. */
  orderId?: string;
  /** Injected in tests; defaults to a real timer. */
  poll?: BondLinkPollOptions;
}) {
  const { authedRequest } = useAuth();
  // The freshest state, not the one this surface was rendered with. Hooks run
  // before the absence check below — an absent link watches nothing.
  const current = useBankLinkState(authedRequest, orderId, link, poll);
  if (!current) return null;
  // A blank url is the same fact as no key: nothing to hand over. There is no
  // state that makes an empty string worth rendering, so the card goes away
  // rather than offering a copy control over one.
  const url = current.linkUrl.trim() !== "" ? current.linkUrl : null;
  if (url === null) return null;
  const words = bankLinkWords(current.state);

  return (
    <CustomerLinkPanel
      headingId="bank-link-heading"
      heading="Your customer’s bank connection"
      // OMITTED, not defaulted, when nothing is known — which is what keeps the
      // pre-5.5.0 rendering identical to what it was.
      {...(words.status === undefined
        ? {}
        : { status: { label: words.status, connected: words.connected } })}
      lead={words.lead}
      detail={words.detail}
      url={words.offerUrl ? url : null}
      copyLabel="Copy bank link"
    />
  );
}

/** One bank state's words. Mirrors `AccountingLinkWords`, with `status`
 *  OPTIONAL: this axis has a state that means "nobody has told us". */
interface BankLinkWords {
  /** The short status line, or `undefined` for a link whose progress is
   *  unknown — the pre-5.5.0 order, which gets no status row at all. */
  status?: string;
  /** Whether the status reads as connected (the quiet green indicator). */
  connected: boolean;
  lead: string;
  detail: string;
  offerUrl: boolean;
}

/**
 * The whole bank state → copy mapping, in ONE exhaustive place (BRK-S121). Every
 * member of the union gets a branch and the `never` check below proves it, so
 * "no state renders blank" is a compile-time property rather than a test we
 * remembered to write. A fifth member cannot be added upstream and silently
 * render as `pending` — the mirror would reject it first, and this would not
 * compile second.
 *
 * ONE INPUT, NOT TWO, unlike the accounting card's: `linkUrl` is not nullable
 * here, so there is no "minted but nothing to send" case to fold in. The one
 * state that withholds the url does so because of what the state MEANS, not
 * because of what the wire carries.
 *
 * FOUR STATES AND A SILENCE. `undefined` is the pre-5.5.0 order and is answered
 * FIRST, deliberately: it is the case a default would swallow, and the words it
 * gets are the ones this card used before it had a state at all.
 *
 * NONE OF THESE IS AN ERROR — the same line the accounting card holds (SUR-S052
 * answers 5, 7). The order is complete at submit and there is nothing the broker
 * can do if the customer never connects, so no state gets error styling, an
 * "incomplete" badge, a nag or a dismiss. And there is no state here that asks
 * the broker to DO anything beyond sending the link: Plaid-via-Lendflow
 * publishes no connection health, so this axis has no `needsReauth` and no
 * `disabled` to name a next step for.
 */
function bankLinkWords(state: BondBankLinkState | undefined): BankLinkWords {
  if (state === undefined) {
    // Nothing is known, so nothing is claimed. WORD FOR WORD what this card
    // said before BRK-S121, because that rendering was correct for exactly this
    // case and an order filed before 5.5.0 stays in it forever.
    return {
      connected: false,
      lead: "Send this link to your customer.",
      // Names its own system and nothing else. A cross-reference to the
      // neighbouring card ("separate from the accounting link…") reads as
      // helpful and puts the word `accounting` inside the BANK card, which is
      // the one place a broker skimming for the right url must not find it.
      detail:
        "They use it to connect their bank — nothing else is needed from you.",
      offerUrl: true,
    };
  }
  const connected = isBankLinkConnected(state);
  switch (state) {
    case "unminted":
      // PUBLISHED ON THE AXIS AND NEVER SENT WITH A KEY: the absence of the
      // whole `bankLink` is what says no link was minted, and discovery cannot
      // serialize this state beside a url. It is answered anyway — the mirror
      // accepts every member the contract publishes, so every member needs
      // words — and it WITHHOLDS the url, because a link that was never minted
      // is a leftover, and a broker who sends one sends their customer
      // somewhere that does not work. The words are the accounting card's for
      // the same state name, which is the point: one visual system.
      return {
        status: "Not connected",
        connected,
        lead: "We’re still preparing this link.",
        detail:
          "Your bond request is submitted and complete. The link to send your customer isn’t ready yet — check back on this submission shortly.",
        offerUrl: false,
      };
    case "pending":
      // The state most orders are in, and the one thing the broker can act on:
      // send it. Upstream cannot tell an unfinished connection from an
      // unobserved one, and publishes the earlier of the two — so this says
      // "waiting", never "they have not connected".
      return {
        status: "Not connected",
        connected,
        lead: "Waiting for your customer to connect.",
        detail:
          "Send this link to your customer. They use it to connect their bank — nothing else is needed from you.",
        offerUrl: true,
      };
    case "connected":
      // CONNECTED FIRST, because that is the news. The figures are a separate
      // orchestration upstream, so a customer who finished minutes ago may
      // still have none — this says the retrieval is happening and that nothing
      // is needed, rather than implying a delay somebody should chase.
      return {
        status: "Connected — retrieving bank figures",
        connected,
        lead: "Your customer has connected their bank.",
        detail:
          "Their bank figures are still coming across. Nothing else is needed from you.",
        offerUrl: true,
      };
    case "figuresReady":
      // Done. The link stays on the card — a customer who has to redo the
      // connection needs it, and there is no `needsReauth` on this axis to tell
      // us when that happens.
      return {
        status: "Connected",
        connected,
        lead: "Your customer has connected their bank.",
        detail:
          "Nothing else is needed from you. The link stays here if they ever need to reconnect.",
        offerUrl: true,
      };
    default: {
      // Unreachable: the union is closed and every member is answered above.
      const exhaustive: never = state;
      return exhaustive;
    }
  }
}

/**
 * An order whose status is `submitted`, reached from the list or a bookmark
 * (BRK-S088, brought to parity with the PMS admin detail in BRK-S106). It is a
 * RECORD, not a form: re-opening a filing must not offer to edit or re-file it.
 *
 * PARITY, not a second design. What an underwriter sees on PMS's bond detail —
 * every step's captured values formatted by their own type, each data pull's
 * per-row verdicts, the referral state, and the project & bond terms — is what
 * the broker who filed it sees here. A broker who has to ring the underwriter to
 * find out what they submitted is the problem this closes.
 *
 * The values come off the ORDER (`values`, `stepRuns`), the labels and the
 * ordering off the resolved CONFIG. The config is fetched separately, so its
 * loading and failure states are handled here rather than blocking the header:
 * the number, the insured and the referral are PMS's own row and are worth
 * rendering even when the discovery half is slow or unreachable (NN #7).
 */
function FiledOrderView({
  order,
  subproductName,
  config,
  configLoading,
  configError,
  referred,
  referralReasons,
}: {
  order: BondSubmission;
  subproductName: string;
  config: BondConfig | null;
  configLoading: boolean;
  configError: string | null;
  referred: boolean;
  referralReasons: string[];
}) {
  // Every step's bag arrives COMPLETE — pushed and bond-order-private alike,
  // resolved server-side (PMS-S238 / BRK-S110) — so the record renders each
  // step from its own key. `final` still carries the WHOLE shared bag, which is
  // where the Project & bond panel below reads the final step's own fields from.
  const values = order.values ?? {};
  const finalFields = (config?.finalStep.fields ?? []).map(toRenderFinalField);
  return (
    <section className="mx-auto flex w-full max-w-3xl flex-col gap-6 py-8">
      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-brk-accent">
          Bond submission
        </p>
        <h1 className="text-2xl font-bold tracking-tight">
          {subproductName || "Bond request"}
        </h1>
        <p className="text-sm text-brk-dim">
          Submitted — our surety team will review and follow up.
        </p>
      </div>

      <dl className="grid grid-cols-1 gap-4 rounded-xl border border-brk-border bg-brk-panel p-5 sm:grid-cols-2">
        <div className="flex flex-col gap-0.5">
          <dt className="text-xs uppercase tracking-wide text-brk-dim">
            Submission number
          </dt>
          <dd className="font-mono text-lg font-semibold text-brk-text">
            {order.number}
          </dd>
        </div>
        <RecordField label="Insured" value={order.accountLegalName ?? "—"} />
        {/* No "Bond type" row: the heading above IS the bond type, and naming
            it twice on one card reads as two different facts. */}
        <RecordField label="Created" value={formatBondDate(order.createdAt)} />
      </dl>

      {referred && <ReferralNotice reasons={referralReasons} filed />}

      {/* The SAME panel the confirmation screen renders — not a second one
          (BRK-S115). It sits above "What you submitted" because it is about the
          order, not about the configuration that shaped it, and because it is
          the one thing on this record that is still outstanding. */}
      <AccountingLinkPanel link={order.accountingLink} orderId={order.id} />
      <BankLinkPanel link={order.bankLink} orderId={order.id} />

      {configLoading && (
        <p role="status" className="text-sm text-brk-dim">
          Loading what you submitted…
        </p>
      )}
      {configError && (
        <p role="alert" className="text-sm text-brk-danger">
          {configError}
        </p>
      )}

      {config && (
        <>
          <div className="flex flex-col gap-4">
            <h2 className="text-lg font-semibold text-brk-text">
              What you submitted
            </h2>
            {config.steps.length === 0 && (
              <p className="text-sm text-brk-dim">
                This bond type has no steps beyond the terms below.
              </p>
            )}
            {config.steps.map((step) => (
              <FiledStepPanel
                key={step.key}
                step={step}
                values={values[step.key] ?? {}}
                run={order.stepRuns?.[step.key]}
                owners={order.owners}
                ownerCount={ownersAnsweringStep(order.elementCounts, step.key)}
              />
            ))}
          </div>

          <div className="flex flex-col gap-3 rounded-xl border border-brk-border bg-brk-panel p-5">
            <h2 className="text-lg font-semibold text-brk-text">
              Project &amp; bond
            </h2>
            <CapturedValues
              fields={finalFields}
              values={values[FINAL_STEP_KEY] ?? {}}
            />
          </div>
        </>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Link
          to="/surety/submissions"
          className="rounded-md border border-brk-border px-4 py-2 text-sm font-medium text-brk-text transition-colors hover:bg-brk-bg"
        >
          ← Back to bond submissions
        </Link>
        <Link
          to="/surety/submissions/new"
          className="rounded-md bg-brk-accent px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-brk-accent-strong"
        >
          Start another
        </Link>
      </div>
    </section>
  );
}

// ─── Confirmation ────────────────────────────────────────────────────────────

function SubmittedScreen({
  number,
  subproductName,
  referred,
  referralReasons,
  orderId,
  accountingLink,
  bankLink,
  advanceOptIn,
  filedOrder,
  onAdvanceAnswered,
}: {
  number: string;
  subproductName: string;
  referred: boolean;
  referralReasons: string[];
  /** The order just filed — BOTH cards re-read it while their own state can
   *  still move (BRK-S119, and the bank card in BRK-S121). This screen
   *  deliberately does no refetch of its own (BRK-S115); each CARD owns its own
   *  freshness, exactly as it does on the durable record, because they are
   *  literally the same two components. */
  orderId?: string;
  /** The link off the SUBMIT's own response — PMS publishes `accountingLink`
   *  on every bond-order body, so this screen needs no refetch (BRK-S115). */
  accountingLink?: BondAccountingLink;
  /** The bank link off the same response, published the same way (BRK-S118).
   *  Independent of the one above: an order can carry either, both or neither. */
  bankLink?: BondBankLink;
  /** The order's Signal Advance answer, and a way to record one (BRK-S161).
   *  Absent for a screen with no order behind it. */
  advanceOptIn?: BondAdvanceOptIn | null;
  /** The order the submit answered with — its `gia` summary decides whether
   *  the GIA section renders (BRK-S182). */
  filedOrder?: BondSubmission | null;
  onAdvanceAnswered?: (order: BondSubmission) => void;
}) {
  return (
    <section className="mx-auto flex w-full max-w-xl flex-col items-center gap-6 py-16 text-center">
      <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-green-700">
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="m5 12 5 5 9-11" />
        </svg>
      </span>
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold tracking-tight">
          Bond request submitted
        </h1>
        <p className="text-brk-dim">
          Your {subproductName ? `${subproductName.toLowerCase()} ` : ""}request
          is in. Our surety team will review and follow up.
        </p>
      </div>
      <div className="rounded-lg border border-brk-border bg-brk-panel px-6 py-4">
        <p className="text-xs uppercase tracking-wide text-brk-dim">
          Submission number
        </p>
        <p className="font-mono text-lg font-semibold text-brk-text">
          {number}
        </p>
      </div>

      {/* THE GIA (BRK-S182), FIRST on the approved outcome — the scope's own
          order — and literally the component the submission profile renders.
          Never on a referred request: `showsGiaSection` reads the filed body
          (status, referral and the `gia` summary), so a referral decided by
          the submit itself hides it too. */}
      {filedOrder?.gia && !referred && showsGiaSection(filedOrder) && (
        <GiaSection orderId={filedOrder.id} summary={filedOrder.gia} />
      )}

      {/* THE SIGNAL ADVANCE OFFER, and the links BEHIND it (BRK-S161).
          Connecting a bank feed is the insured's consent and the enrolment is
          what makes discovery mint the links at all — so the offer is asked
          first and the two panels appear only once the broker has opted in.
          Handing somebody a bank link they never asked for is the shape of a
          dark pattern, and on a decline there is nothing to connect.

          The panels themselves are unchanged and still literally the same two
          components the filed record renders (BRK-S115 / BRK-S118). */}
      {orderId && onAdvanceAnswered ? (
        <div className="w-full text-left">
          <SignalAdvanceOffer
            orderId={orderId}
            answer={advanceOptIn}
            onAnswered={onAdvanceAnswered}
            links={
              <>
                <AccountingLinkPanel link={accountingLink} orderId={orderId} />
                <BankLinkPanel link={bankLink} orderId={orderId} />
              </>
            }
          />
          {advanceOptIn === "declined" && <AdvanceDeclinedNotice />}
        </div>
      ) : (
        <>
          <AccountingLinkPanel link={accountingLink} orderId={orderId} />
          <BankLinkPanel link={bankLink} orderId={orderId} />
        </>
      )}

      {/* The referral follows the order onto the confirmation, so the broker
          leaves knowing an underwriter will look at it. */}
      {referred && (
        <div className="w-full text-left">
          <ReferralNotice reasons={referralReasons} />
        </div>
      )}
      <div className="flex flex-wrap items-center justify-center gap-3">
        <Link
          to="/surety/submissions/new"
          reloadDocument
          className="rounded-md border border-brk-border px-4 py-2 text-sm font-medium text-brk-text transition-colors hover:bg-brk-bg"
        >
          Start another
        </Link>
        <Link
          to="/surety/construction"
          className="rounded-md bg-brk-accent px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-brk-accent-strong"
        >
          Back to Construction Bond
        </Link>
      </div>
    </section>
  );
}

// ─── The document path (BRK-S120) ────────────────────────────────────────────

/**
 * The bond-request dropzone — the REAL one. It replaces BRK-S077's deliberately
 * fake stub with the shipped intake primitives: the same `DocumentDropzone` the
 * New Business path uses (pointed at the `bond` target with its own copy), the
 * same presign → PUT → confirm → process seam, and the same
 * "confirm every extracted value" review the account forms use.
 *
 * It does NOT map the proposal itself — the page does, because the mapping needs
 * the resolved configuration. This component only renders the read and, honestly,
 * what came of it: how many fields landed, and which values this bond type had
 * nowhere to put. A read that produced nothing says so in the same words a failed
 * read does, because to the broker they mean the same thing.
 */
function BondDocumentPanel({
  authedRequest,
  prefill,
  onProposal,
}: {
  authedRequest: AuthedRequester;
  /** What the page made of the last read — null until one lands (or until the
   *  bond type that gives it meaning is chosen). */
  prefill: BondPrefill | null;
  onProposal: (proposal: IntakeProposal) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <DocumentDropzone
        authedRequest={authedRequest}
        onProposal={onProposal}
        uploader={selectUploader()}
        target="bond"
        multiple={false}
        prompt="Drag & drop your Surety Bond Application, or"
        hint="PDF only, up to 32 MB. We’ll read it and fill in what we can."
        inputLabel="Upload Surety Bond Application"
        actionLabel="Read the application"
        busyLabel="Reading the application…"
        failureMessage={UNREADABLE_COPY}
        // A live demo cannot wait on a hung extraction, and the form underneath
        // was never blocked — so the watchdog hands the broker back to it.
        processTimeoutMs={PROCESS_TIMEOUT_MS}
        summaryTitle="Application read"
        summaryLine={
          prefill === null
            ? undefined
            : prefill.count === 0
              ? UNREADABLE_COPY
              : `We filled ${prefill.count} ${prefill.count === 1 ? "field" : "fields"} across the steps below — review each one before you continue. Nothing is saved until you do.`
        }
      />
      {prefill !== null && prefill.dropped.length > 0 && (
        <UnusedValuesNotice dropped={prefill.dropped} />
      )}
    </div>
  );
}

/**
 * How long to wait for `/process` before handing the broker back to the form.
 *
 * Generous, because a real extraction is a model call and a broker who dropped a
 * document would rather wait than retype — but finite, because the honest
 * failure state is strictly better than an indefinite spinner (and PMS-S269 puts
 * its own hard timeout on the server side).
 */
const PROCESS_TIMEOUT_MS = 60_000;

/**
 * What the read produced that this bond type could not take — named, not
 * swallowed.
 *
 * The same instinct as the intake report's unreadable-pages list: a value that
 * silently evaporates is one the broker believes was never printed. Nothing here
 * is an error — the field is simply theirs to fill.
 */
function UnusedValuesNotice({ dropped }: { dropped: BondPrefillDrop[] }) {
  return (
    <div className="flex flex-col gap-1 rounded-md border border-brk-border bg-brk-bg px-3 py-2 text-sm">
      <span className="font-medium text-brk-text">
        We couldn’t use everything we read:
      </span>
      <ul className="flex list-inside list-disc flex-col gap-1 text-brk-dim">
        {dropped.map((d) => (
          <li key={d.key}>
            <span className="font-medium">
              {d.label ?? humanizeLabel(d.key.split(".").pop() ?? d.key)}
            </span>{" "}
            — {d.reason} Enter it yourself.
          </li>
        ))}
      </ul>
    </div>
  );
}
